import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APPWRITE_CONFIG, BatchSetEntityGroups, BillOfMaterialsLineItem, BillOfMaterialsLineItemType, Collection, ComplaintLog, ComplaintLogProduct, ComplaintLogStatus, PlasticsHardwareItemShipment, PlasticsHardwareItemShipmentDamageCamEntry, PlasticsHardwareItemShipmentDamageItem, PlasticsHardwareItemShipmentItem, Contact, Customer, DEFAULT_SUNCAST_DATA, FHCornerLabelConfig, GlobalVariableDocumentIds, HardwareItemUnit, InventoryTransaction, InventoryTransactionType, LabelInstance, MappingComplaintLogToContact, MappingComplaintLogToQualityAlert, MappingPlasticsMaterialToQualityAlert, MappingProductVersionToDockInspectionQuailtyAlert, MappingProductVersionToPlasticsStaticLabel, MappingProductVersionToQualityAlert, MappingProductVersionToQuoteStudioCostModel, MappingProductVersionToQuoteStudioQuote, MoldRepairRequest, MoldingTaskDependantData, PlasticMaterialMixingTaskStatus, PlasticsCalendarEvent, PlasticsCalendarEventType, PlasticsColorCode, PlasticsDockInspectionInstance, PlasticsDockInspectionInstanceStatus, PlasticsFinishingStation, PlasticsFinishingTask, PlasticsFinishingTaskStatus, PlasticsFixture, PlasticsHardwareItem, PlasticsHardwareItemType, PlasticsInventoryArea, PlasticsInventoryLocation, PlasticsInventoryLocationBin, PlasticsInventoryLocationWithBins, PlasticsKit, PlasticsKittingArea, PlasticsKittingLocation, PlasticsKittingLocationBin, PlasticsManufacturingRatesParent, PlasticsManufacturingRatesVersion, PlasticsManufacturingRatesVersionStatus, PlasticsMaterial, PlasticsMaterialIngredient, PlasticsMaterialMixingTask, PlasticsMoldingTask, PlasticsMoldingTaskStatus, PlasticsOrder, PlasticsOrderedProduct, PlasticsOrderedProductStatus, PlasticsPurchaseRequisition, PlasticsQualityAlert, PlasticsQualityAlertInspection, PlasticsQualityInspectionStatus, PlasticsRework, PlasticsScrapInstance, PlasticsScrapReason, PlasticsSkid, PlasticsStaticLabel, Product, ProductInstance, ProductType, ProductVersion, ProductVersionMoldConfig, ProductVersionStatus, QualityAlertViewTally, Resource, RotoMachine, RotoMachineArmTally, RotoMachinePartOutcome, RotoSchedule, SuncastData, SuncastSnapshot, SuncastSnapshotStatus, Tag, User, VendorHardwareData, VendorHardwareDataPriceHistory, PlasticsShipment, PlasticsShipmentStatus, PrismModule, PlasticsSubModule, UserNotification, UserComment, LabelInstance_PackingSlip, LabelType, KolorcansData, DEFAULT_KOLORCANS_DATA, PlasticsShipmentLineItem, KolorcansPackingConfig, KolorcansPackingConfigProduct, MoldArmCycle } from '@prism/prism-shared';
import { ClackData, DEFAULT_CLACK_DATA } from '@prism/prism-shared';
import { PlasticsOrderReviewResearch } from '@prism/prism-shared';
import { HotListItem, HotListOrderItem } from '@prism/prism-shared';
import { prepareSameProductMoldingTaskHandoff } from '@prism/prism-shared';
import { PlasticsDailyProduction } from '@prism/prism-shared';
import { PlasticsFinishingLaborSession } from '@prism/prism-shared';
import { Query, TablesDB } from 'appwrite';
import { PlasticsMold, PlasticsMoldFamily, PlasticsMoldStatus } from 'libs/prism-shared/src/lib/models/plastics-mold-family.model';
import { PlasticsMoldHistoryEvent } from 'libs/prism-shared/src/lib/models/plastics-mold-history-event.model';
import { Observable, catchError, combineLatest, concatMap, defaultIfEmpty, defer, filter, first, forkJoin, from, last, map, mergeMap, of, scan, shareReplay, switchMap, tap, throwError, toArray } from 'rxjs';
import { AppwriteDatabaseService, AppwriteStorageService, fromAppwriteDoc, fromAppwriteDocs, QueryRef } from '@prism/angular-shared';
import { PrismCommonApiService } from './prism-common-api.service';
import { SharedApiService } from './shared-api.service';
import { UserApiService } from './user-api.service';
import { MoldCheckupApiService } from './mold-checkup-api.service';
import { saveMoldingTaskAdminEdit } from './molding-task-admin-edit';

type ReleasedProductVersionDependantData = {
    productVersion: ProductVersion;
    lineItems: BillOfMaterialsLineItem[];
    lineItemHardwareItems: PlasticsHardwareItem[];
    lineItemProducts: Product[];
    lineItemProductVersions: ProductVersion[];
    lineItemMaterials: PlasticsMaterial[];
    vendorHardwareData: VendorHardwareData[];
    productDrawingResource: Resource | null;
};

type QuantityIncrementOperation = {
    collection: Collection;
    id: string;
    field: string;
    value: number;
};

@Injectable({ providedIn: 'root' })
export class PlasticsApiService {
    private static readonly ID_BATCH_SIZE = 30;

    constructor(
        private moldCheckups: MoldCheckupApiService,
        private commonApiService: PrismCommonApiService,
        private http: HttpClient,
        private sharedApi: SharedApiService,
        private appwrite: AppwriteDatabaseService,
        private storage: AppwriteStorageService,
        private userApiService: UserApiService,
    ) { }

    private incrementField(collection: Collection, id: string, field: string, change: number): Observable<void> {
        return this.appwrite.get<any>(collection, id).pipe(
            switchMap(doc => from(this.appwrite.collection<any>(collection).doc(id).set({
                ...doc,
                [field]: (doc?.[field] ?? 0) + change,
            }))),
        );
    }

    private addQuantityIncrement(
        operationsByKey: Record<string, QuantityIncrementOperation>,
        collection: Collection,
        id: string,
        field: string,
        value: number,
    ): void {
        if (!id || value === 0) return;
        const key = `${collection}:${id}:${field}`;
        if (operationsByKey[key]) {
            operationsByKey[key].value += value;
            return;
        }
        operationsByKey[key] = { collection, id, field, value };
    }

    public loadHardwareItems(): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadHardwareItemById(hardwareItemId: string): Observable<PlasticsHardwareItem> {
        return this.appwrite.get<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemId);
    }

    public loadHardwareItemsByIds(hardwareItemIds: string[]): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.listByIds<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemIds);
    }

    public loadHardwareItemsByType(type: PlasticsHardwareItemType): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.equal('type', type)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadHardwareItemsByTypes(types: PlasticsHardwareItemType[]): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.equal('type', types)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadInventoryControlledHardwareItems(): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.equal('inventoryControlled', true)]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([]),
        );
    }

    public getHardwareItemCountByType(type: PlasticsHardwareItemType): Observable<number> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.equal('type', type), Query.limit(1)]).pipe(
            map(resp => resp.total),
        );
    }

    public getHighestHardwareItemPartNumber(): Observable<number> {
        return this.loadHardwareItems().pipe(
            map((hardwareItems) => {
                const partNumbers = hardwareItems
                    .map(item => item.partNumber)
                    .map(partNumber => parseInt(partNumber.split('-')[1]))
                const sortedPartNumbers = partNumbers.sort((a, b) => b - a)
                // if the collection is empty, sortedPartNumbers[0] is undefined,
                // which propagates to (undefined + 1) = NaN downstream. Default to 0 so the
                // first item gets part number 0001 instead of 0NaN.
                return sortedPartNumbers[0] ?? 0;
            })
        );
    }

    public setHardwareItem(hardwareItem: PlasticsHardwareItem): Observable<void> {
        return from(this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(hardwareItem.id).set(hardwareItem));
    }

    public getHardwareItemCount(): Observable<number> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.limit(1)]).pipe(
            map(resp => resp.total),
        );
    }

    public loadHardwareItemBySafetyDataSheetResourceId(resourceId: string): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.equal('safetyDataSheetResourceId', resourceId)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadHardwareItemByPartNumber(partNumber: string): Observable<PlasticsHardwareItem> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [Query.equal('partNumber', partNumber)]).pipe(
            map(resp => resp.items[0]),
        );
    }

    public loadHardwareItemsByPartNumbers(partNumbers: string[]): Observable<PlasticsHardwareItem[]> {
        const hardwareItems$ = partNumbers.map(p => this.loadHardwareItemByPartNumber(p));
        return forkJoin(hardwareItems$).pipe(
            defaultIfEmpty([])
        );
    }

    public loadVendorHardwareData(): Observable<VendorHardwareData[]> {
        return this.appwrite.list<VendorHardwareData>(Collection.vendorHardwareData, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public setVendorHardwareData(vendorHardwareData: VendorHardwareData): Observable<void> {
        return this.appwrite.get<VendorHardwareData>(Collection.vendorHardwareData, vendorHardwareData.id).pipe(
            catchError((error) => error?.code === 404 ? of(null) : throwError(() => error)),
            switchMap((existingVendorHardwareData) => {
                return from(this.appwrite.collection<VendorHardwareData>(Collection.vendorHardwareData).doc(vendorHardwareData.id).set(vendorHardwareData)).pipe(
                    switchMap(() => this.createVendorHardwareDataPriceHistoryIfNeeded(existingVendorHardwareData, vendorHardwareData)),
                );
            }),
        );
    }

    public loadVendorHardwareDataPriceHistoryByHardwareItemId(hardwareItemId: string): Observable<VendorHardwareDataPriceHistory[]> {
        return this.appwrite.list<VendorHardwareDataPriceHistory>(Collection.vendorHardwareDataPriceHistory, [
            Query.equal('parentHardwareItemId', hardwareItemId),
            Query.orderAsc('createdOn'),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadVendorHardwareDataByHardwareItemId(hardwareItemId: string): Observable<VendorHardwareData[]> {
        return this.appwrite.list<VendorHardwareData>(Collection.vendorHardwareData, [
            Query.equal('parentHardwareItemId', hardwareItemId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadVendorHardwareDataByHardwareItemIds(hardwareItemIds: string[]): Observable<VendorHardwareData[]> {
        return this.appwrite.listByFieldValues<VendorHardwareData>(Collection.vendorHardwareData, 'parentHardwareItemId', hardwareItemIds);
    }

    public loadVendorHardwareDataByVendorId(vendorId: string): Observable<VendorHardwareData[]> {
        return this.appwrite.list<VendorHardwareData>(Collection.vendorHardwareData, [
            Query.equal('vendorId', vendorId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadUnitCountedHardwareItems(): Observable<PlasticsHardwareItem[]> {
        return this.appwrite.list<PlasticsHardwareItem>(Collection.plasticsHardwareItem, [
            Query.equal('isUnitCounted', true),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    private createVendorHardwareDataPriceHistoryIfNeeded(
        previousVendorHardwareData: VendorHardwareData | null,
        nextVendorHardwareData: VendorHardwareData,
    ): Observable<void> {
        const nextUnitCost = this.calculateVendorHardwareDataUnitCost(nextVendorHardwareData);
        if (nextUnitCost === null) return of(void 0);

        const previousUnitCost = previousVendorHardwareData
            ? this.calculateVendorHardwareDataUnitCost(previousVendorHardwareData)
            : null;

        if (previousUnitCost !== null && Math.abs(previousUnitCost - nextUnitCost) < 0.0000001) {
            return of(void 0);
        }

        const priceHistory: VendorHardwareDataPriceHistory = {
            id: this.appwrite.createId(),
            vendorHardwareDataId: nextVendorHardwareData.id,
            parentHardwareItemId: nextVendorHardwareData.parentHardwareItemId,
            vendorId: nextVendorHardwareData.vendorId,
            unitCost: nextUnitCost,
            cost: nextVendorHardwareData.cost,
            packageQuantity: nextVendorHardwareData.packageQuantity,
            amountGroupCount: nextVendorHardwareData.amountGroupCount || 1,
            units: nextVendorHardwareData.units,
            createdOn: nextVendorHardwareData.lastSavedOn || new Date(),
            createdById: nextVendorHardwareData.lastSavedById || '',
        };

        return from(this.appwrite.collection<VendorHardwareDataPriceHistory>(Collection.vendorHardwareDataPriceHistory).doc(priceHistory.id).set(priceHistory));
    }

    private calculateVendorHardwareDataUnitCost(vendorHardwareData: VendorHardwareData): number | null {
        const groupCount = vendorHardwareData.amountGroupCount || 1;
        if (
            !vendorHardwareData.units ||
            !Number.isFinite(vendorHardwareData.cost) ||
            !Number.isFinite(vendorHardwareData.packageQuantity) ||
            !Number.isFinite(groupCount) ||
            vendorHardwareData.cost <= 0 ||
            vendorHardwareData.packageQuantity <= 0 ||
            groupCount < 1
        ) {
            return null;
        }

        return vendorHardwareData.cost / vendorHardwareData.packageQuantity;
    }

    public newHardwareInventoryTransaction(
        hardwareItemId: string,
        changeQty: number,
        comment: string,
        curentUser: User,
        processId: string | null = null,
    ): Observable<InventoryTransaction> {

        const transaction: InventoryTransaction = {
            id: this.appwrite.createId(),
            entityId: hardwareItemId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: curentUser?.id || "",
            type: InventoryTransactionType.PlasticsHardware,
        };

        const work$ = this.appwrite.get<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemId).pipe(
            switchMap((item) => {
                if (!item.isUnitCounted) return of(undefined);
                const update$ = from(this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(item.id).set({ ...item, qtyOnHand: (item.qtyOnHand ?? 0) + changeQty }));
                const create$ = this.sharedApi.setInventoryTransaction(transaction);
                return forkJoin([update$, create$]).pipe(map(() => undefined));
            })
        );

        return work$.pipe(map(() => transaction));
    }

    public getInventoryTransactionsByHardwareItemId(hardwareItemId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(hardwareItemId, InventoryTransactionType.PlasticsHardware);
    }

    public getRecentInventoryTransactionsByHardwareItemId(hardwareItemId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityIdRecent(hardwareItemId, InventoryTransactionType.PlasticsHardware);
    }

    public loadRecentHardwareInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.PlasticsHardware);
    }

    public loadHardwareInventoryTransactionsByHardwareId(hardwareItemId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(hardwareItemId, InventoryTransactionType.PlasticsHardware);
    }

    public loadHardwareInventoryTransactionsByHardwareIdAfterDate(hardwareItemId: string, startDate: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityIdAfterDate(hardwareItemId, InventoryTransactionType.PlasticsHardware, startDate);
    }

    public loadHardwareInventoryTransactionsByHardwareIdBetween(hardwareItemId: string, startDate: Date, endDate: Date): Observable<InventoryTransaction[]> {
        return this.appwrite.listAll<InventoryTransaction>(Collection.inventoryTransaction, [
            Query.equal('type', InventoryTransactionType.PlasticsHardware),
            Query.equal('entityId', hardwareItemId),
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.lessThanEqual('createdOn', endDate.toISOString()),
        ]);
    }


    // ============================
    // Product
    // ============================
    // CRUD and query operations for products and product versions
    // Includes child entities: ProductVersion, ProductInstance, ProductInventoryTransaction, ProductVersionMoldConfig, BillOfMaterialsLineItem

    public loadProducts(): Observable<Product[]> {
        return this.appwrite.list<Product>(Collection.product, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public setProduct(product: Product): Observable<void> {
        return from(this.appwrite.collection<Product>(Collection.product).doc(product.id).set(product));
    }

    public ensureProductExists(productId: string): Observable<void> {
        if (!productId) {
            return of(void 0);
        }

        return this.loadProductById(productId).pipe(
            map(() => void 0),
            catchError((error) => {
                if (error?.code !== 404) {
                    return throwError(() => error);
                }

                return this.appwrite.create(Collection.product, {
                    id: productId,
                    qtyOnHand: 0,
                });
            }),
        );
    }

    public createMissingProductsForProductVersions(): Observable<{ createdProducts: Product[]; missingProductIds: string[] }> {
        return forkJoin({
            products: this.loadProducts(),
            productVersions: this.loadProductVersions(),
        }).pipe(
            map(({ products, productVersions }) => {
                const existingProductIds = new Set(products.map(product => product.id));
                const productVersionProductIds = [...new Set(productVersions
                    .map(productVersion => productVersion.productId)
                    .filter(productId => !!productId))];
                const missingProductIds = productVersionProductIds.filter(productId => !existingProductIds.has(productId));
                return missingProductIds;
            }),
            switchMap((missingProductIds) => {
                const createdProducts = missingProductIds.map((productId): Product => ({
                    id: productId,
                    qtyOnHand: 0,
                }));

                if (createdProducts.length === 0) {
                    return of({
                        createdProducts,
                        missingProductIds,
                    });
                }

                return from(createdProducts).pipe(
                    mergeMap(product => this.appwrite.create(Collection.product, product), 25),
                    toArray(),
                    map(() => ({
                        createdProducts,
                        missingProductIds,
                    })),
                );
            }),
        );
    }

    public deleteProduct(productId: string): Observable<void> {
        return this.appwrite.delete(Collection.product, productId);
    }

    public loadProductById(productId: string): Observable<Product> {
        return this.appwrite.get<Product>(Collection.product, productId);
    }

    public loadProductsByIds(productIds: string[]): Observable<Product[]> {
        return this.appwrite.listByIds<Product>(Collection.product, productIds);
    }

    public getProductsCount(): Observable<number> {
        return this.appwrite.list<Product>(Collection.product, [Query.limit(1)]).pipe(
            map(resp => resp.total),
        );
    }


    public loadReleasedProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedAsMoldedProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.equal('type', ProductType.AS_MOLDED),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedAndDraftProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', [ProductVersionStatus.Released, ProductVersionStatus.Draft]),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedAndFinishedProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.equal('type', ProductType.FINISHED),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }
    public loadReleasedAndMoldedProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.equal('type', ProductType.AS_MOLDED),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }
    public loadFinishedProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('type', ProductType.FINISHED),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedProductVersionByProductId(productId: string): Observable<ProductVersion> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.equal('productId', productId),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0] ?? (null as unknown as ProductVersion)),
        );
    }

    public loadReleasedProductVersionsByCustomerId(customerId: string): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.equal('customerId', customerId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public getReleasedProductVersionByProductIdValueChanges(productId: string): Observable<ProductVersion> {
        return this.appwrite.collection<ProductVersion>(
            Collection.productVersion, ref => ref
                .where('status', '==', ProductVersionStatus.Released)
                .where('productId', '==', productId)
                .limit(1)
        ).valueChanges().pipe(
            map(versions => versions[0] ?? (null as unknown as ProductVersion)),
        );
    }

    public setProductVersion(productVersion: ProductVersion): Observable<void> {
        return this.ensureProductExists(productVersion.productId).pipe(
            switchMap(() => from(this.appwrite.collection<ProductVersion>(Collection.productVersion).doc(productVersion.id).set(productVersion))),
        );
    }

    public setProductVersions(productVersions: ProductVersion[]): Observable<void[]> {
        return forkJoin(productVersions.map(v => this.setProductVersion(v))).pipe(
            defaultIfEmpty([] as void[]),
        );
    }

    public loadProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadProductVersionById(productVersionId: string): Observable<ProductVersion> {
        return this.appwrite.get<ProductVersion>(Collection.productVersion, productVersionId);
    }

    public loadProductVersionsByPartNumber(partNumber: string): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('partNumber', partNumber),
            Query.limit(100),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public copyProductVersionAsNewDraft(
        currentUserId: string,
        sourceProductVersionId: string,
        targetPartNumber: string,
    ): Observable<ProductVersion> {
        const normalizedTargetPartNumber = targetPartNumber.trim().toUpperCase();

        return combineLatest([
            this.loadProductVersionById(sourceProductVersionId),
            this.loadBillOfMaterialsLineItemsByProductVersionId(sourceProductVersionId),
            this.loadMappingProductVersionToQualityAlertsByProductVersionId(sourceProductVersionId),
            this.loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionId(sourceProductVersionId),
            this.loadMappingProductVersionToPlasticsStaticLabelsByProductVersionId(sourceProductVersionId),
            this.loadProductVersionMoldConfigByProductVersionId(sourceProductVersionId),
            this.loadProductVersionsByPartNumber(normalizedTargetPartNumber),
        ]).pipe(
            first(),
            switchMap(([
                sourceProductVersion,
                lineItems,
                mappingProductVersionToQualityAlerts,
                mappingProductVersionToDockInspections,
                mappingProductVersionToPlasticsStaticLabels,
                productVersionMoldConfig,
                existingTargetProductVersions,
            ]) => {
                if (sourceProductVersion.status !== ProductVersionStatus.Released) {
                    return throwError(() => new Error('Only a Released product version can be copied as a new product.'));
                }
                if (existingTargetProductVersions.length > 0) {
                    return throwError(() => new Error(`Product ${normalizedTargetPartNumber} already exists.`));
                }

                const now = new Date();
                const newProduct: Product = {
                    id: this.appwrite.createId(),
                    qtyOnHand: 0,
                };
                const newProductVersion: ProductVersion = {
                    id: this.appwrite.createId(),
                    productId: newProduct.id,
                    fixtureId: sourceProductVersion.fixtureId,
                    robotProgramId: sourceProductVersion.robotProgramId,
                    status: ProductVersionStatus.Draft,
                    versionNumber: null,
                    partNumber: normalizedTargetPartNumber,
                    type: sourceProductVersion.type,
                    imageUrl: sourceProductVersion.imageUrl,
                    thumbnailImageUrl: sourceProductVersion.thumbnailImageUrl,
                    stlUrl: sourceProductVersion.stlUrl,
                    description: sourceProductVersion.description,
                    customerPartNumber: sourceProductVersion.customerPartNumber,
                    customerId: sourceProductVersion.customerId,
                    drawingResourceId: sourceProductVersion.drawingResourceId,
                    createdById: currentUserId,
                    createdOn: now,
                    updatedById: currentUserId,
                    updatedOn: now,
                    releasedById: null,
                    releasedOn: null,
                    releaseComment: '',
                    defaultFinishingStationId: sourceProductVersion.defaultFinishingStationId,
                    finishingProcedureId: sourceProductVersion.finishingProcedureId,
                    requiresAsMoldedLabel: sourceProductVersion.requiresAsMoldedLabel,
                    moldFamilyId: sourceProductVersion.moldFamilyId,
                    groceryStoreProduct: sourceProductVersion.groceryStoreProduct || false,
                };

                const batchSetEntities: BatchSetEntityGroups = {
                    [Collection.productVersion]: [newProductVersion],
                    [Collection.billOfMaterialsLineItem]: lineItems.map(lineItem => ({
                        id: this.appwrite.createId(),
                        entityId: lineItem.entityId,
                        quantity: lineItem.quantity,
                        productVersionId: newProductVersion.id,
                        type: lineItem.type,
                    })),
                    [Collection.mappingProductVersionToQualityAlert]: mappingProductVersionToQualityAlerts.map(mapping => ({
                        id: this.appwrite.createId(),
                        productVersionId: newProductVersion.id,
                        qualityAlertId: mapping.qualityAlertId,
                    })),
                    [Collection.mappingProductVersionToDockInspectionQualityAlert]: mappingProductVersionToDockInspections.map(mapping => ({
                        id: this.appwrite.createId(),
                        productVersionId: newProductVersion.id,
                        qualityAlertId: mapping.qualityAlertId,
                    })),
                    [Collection.mappingProductVersionToPlasticsStaticLabel]: mappingProductVersionToPlasticsStaticLabels.map(mapping => ({
                        id: this.appwrite.createId(),
                        productVersionId: newProductVersion.id,
                        staticLabelId: mapping.staticLabelId,
                    })),
                };

                if (productVersionMoldConfig) {
                    batchSetEntities[Collection.productVersionMoldConfig] = [{
                        id: this.appwrite.createId(),
                        productVersionId: newProductVersion.id,
                        ovenTime: productVersionMoldConfig.ovenTime,
                        ovenTemperature: productVersionMoldConfig.ovenTemperature,
                        armSpeed: productVersionMoldConfig.armSpeed,
                        plateSpeed: productVersionMoldConfig.plateSpeed,
                        reversingTime: productVersionMoldConfig.reversingTime,
                        airOnMold: productVersionMoldConfig.airOnMold,
                        airTime: productVersionMoldConfig.airTime,
                        airPressurePsi: productVersionMoldConfig.airPressurePsi || 0,
                        preCoolingTime: productVersionMoldConfig.preCoolingTime,
                        coolingTime: productVersionMoldConfig.coolingTime,
                    }];
                }

                return this.appwrite.batchCreateSetAndIncrementAcrossCollectionsTransactional(
                    { [Collection.product]: [newProduct] },
                    batchSetEntities,
                    [],
                ).pipe(
                    map(() => newProductVersion),
                );
            }),
        );
    }

    public createDraftProductVersion(currentUserId: string, productVersionId: string): Observable<ProductVersion> {
        return this.prepareDraftProductVersion(currentUserId, productVersionId).pipe(
            switchMap(({ draftProductVersion, batchSetEntities }) =>
                this.appwrite.batchSetAcrossCollections(batchSetEntities).pipe(
                    map(() => draftProductVersion),
                )
            ),
        );
    }

    private prepareDraftProductVersion(
        currentUserId: string,
        productVersionId: string,
    ): Observable<{ draftProductVersion: ProductVersion; batchSetEntities: BatchSetEntityGroups }> {
        return combineLatest([
            this.loadProductVersionById(productVersionId),
            this.loadBillOfMaterialsLineItemsByProductVersionId(productVersionId),
            this.loadMappingProductVersionToQualityAlertsByProductVersionId(productVersionId),
            this.loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionId(productVersionId),
            this.loadMappingProductVersionToPlasticsStaticLabelsByProductVersionId(productVersionId),
            this.loadProductVersionMoldConfigByProductVersionId(productVersionId),
            this.loadMappingProductVersionToQuoteStudioCostModelsByProductVersionId(productVersionId),
            this.loadMappingProductVersionToQuoteStudioQuotesByProductVersionId(productVersionId),
        ]).pipe(
            first(),
            map(([
                productVersion,
                lineItems,
                mappingProductVersionToQualityAlerts,
                mappingProductVersionToDockInspections,
                mappingProductVersionToPlasticsStaticLabels,
                productVersionMoldConfig,
                mappingProductVersionToQuoteStudioCostModels,
                mappingProductVersionToQuoteStudioQuotes,
            ]) => {
                const updatedProductVersion: ProductVersion = {
                    id: this.appwrite.createId(),
                    versionNumber: null,
                    status: ProductVersionStatus.Draft,
                    createdById: currentUserId,
                    createdOn: new Date(),
                    updatedById: currentUserId,
                    updatedOn: new Date(),
                    releasedById: null,
                    releasedOn: null,
                    releaseComment: '',
                    productId: productVersion.productId,
                    fixtureId: productVersion.fixtureId,
                    robotProgramId: productVersion.robotProgramId,
                    partNumber: productVersion.partNumber,
                    type: productVersion.type,
                    imageUrl: productVersion.imageUrl,
                    thumbnailImageUrl: productVersion.thumbnailImageUrl,
                    stlUrl: productVersion.stlUrl,
                    description: productVersion.description,
                    customerPartNumber: productVersion.customerPartNumber,
                    customerId: productVersion.customerId,
                    drawingResourceId: productVersion.drawingResourceId,
                    defaultFinishingStationId: productVersion.defaultFinishingStationId,
                    finishingProcedureId: productVersion.finishingProcedureId,
                    requiresAsMoldedLabel: productVersion.requiresAsMoldedLabel,
                    moldFamilyId: productVersion.moldFamilyId,
                    groceryStoreProduct: productVersion.groceryStoreProduct || false,
                };

                const batchSetEntities: BatchSetEntityGroups = {
                    [Collection.productVersion]: [updatedProductVersion],
                    [Collection.billOfMaterialsLineItem]: lineItems.map(lineItem => ({
                        entityId: lineItem.entityId,
                        quantity: lineItem.quantity,
                        type: lineItem.type,
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                    })),
                    [Collection.mappingProductVersionToQualityAlert]: mappingProductVersionToQualityAlerts.map(mapping => ({
                        qualityAlertId: mapping.qualityAlertId,
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                    })),
                    [Collection.mappingProductVersionToDockInspectionQualityAlert]: mappingProductVersionToDockInspections.map(mapping => ({
                        qualityAlertId: mapping.qualityAlertId,
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                    })),
                    [Collection.mappingProductVersionToPlasticsStaticLabel]: mappingProductVersionToPlasticsStaticLabels.map(mapping => ({
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                        staticLabelId: mapping.staticLabelId,
                    })),
                    [Collection.mappingProductVersionToQuoteStudioCostModel]: mappingProductVersionToQuoteStudioCostModels.map(mapping => ({
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                        costModelId: mapping.costModelId,
                    })),
                    [Collection.mappingProductVersionToQuoteStudioQuote]: mappingProductVersionToQuoteStudioQuotes.map(mapping => ({
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                        quoteId: mapping.quoteId,
                    })),
                };

                if (productVersionMoldConfig) {
                    const updatedProductVersionMoldConfig: ProductVersionMoldConfig = {
                        id: this.appwrite.createId(),
                        productVersionId: updatedProductVersion.id,
                        ovenTime: productVersionMoldConfig.ovenTime,
                        ovenTemperature: productVersionMoldConfig.ovenTemperature,
                        armSpeed: productVersionMoldConfig.armSpeed,
                        plateSpeed: productVersionMoldConfig.plateSpeed,
                        reversingTime: productVersionMoldConfig.reversingTime,
                        airOnMold: productVersionMoldConfig.airOnMold,
                        airTime: productVersionMoldConfig.airTime,
                        airPressurePsi: productVersionMoldConfig.airPressurePsi || 0,
                        preCoolingTime: productVersionMoldConfig.preCoolingTime,
                        coolingTime: productVersionMoldConfig.coolingTime,
                    };
                    batchSetEntities[Collection.productVersionMoldConfig] = [updatedProductVersionMoldConfig];
                }

                return { draftProductVersion: updatedProductVersion, batchSetEntities };
            }),
        );
    }

    public linkProductVersionsToMoldFamily(
        currentUserId: string,
        productVersionIds: string[],
        moldFamilyId: string,
        onProgress?: (savedCount: number, totalCount: number) => void,
    ): Observable<ProductVersion[]> {
        const uniqueProductVersionIds = [...new Set(productVersionIds.filter(id => !!id))];
        if (!uniqueProductVersionIds.length) return of([]);

        return this.loadMoldFamilyById(moldFamilyId).pipe(
            switchMap(moldFamily => from(uniqueProductVersionIds).pipe(
                concatMap(productVersionId => this.prepareDraftProductVersion(currentUserId, productVersionId).pipe(
                    switchMap(({ draftProductVersion, batchSetEntities }) => this.loadProductVersionsByProductId(draftProductVersion.productId).pipe(
                        map(productVersions => {
                            const priorReleasedProductVersions = productVersions
                                .filter(productVersion => [
                                    ProductVersionStatus.Released,
                                    ProductVersionStatus.Obsolete,
                                ].includes(productVersion.status))
                                .map(productVersion => ({
                                    ...productVersion,
                                    status: ProductVersionStatus.Obsolete,
                                }));
                            const now = new Date();
                            const releasedProductVersion: ProductVersion = {
                                ...draftProductVersion,
                                moldFamilyId,
                                versionNumber: priorReleasedProductVersions.length + 1,
                                status: ProductVersionStatus.Released,
                                updatedById: currentUserId,
                                updatedOn: now,
                                releasedById: currentUserId,
                                releasedOn: now,
                                releaseComment: `Linked to mold family: ${moldFamily.name}`,
                            };

                            return { batchSetEntities, priorReleasedProductVersions, releasedProductVersion };
                        }),
                    )),
                    switchMap(({ batchSetEntities, priorReleasedProductVersions, releasedProductVersion }) =>
                        this.appwrite.batchSetAcrossCollectionsTransactional({
                            ...batchSetEntities,
                            [Collection.productVersion]: [...priorReleasedProductVersions, releasedProductVersion],
                        }).pipe(map(() => releasedProductVersion))
                    ),
                )),
                scan((savedVersions, releasedVersion) => [...savedVersions, releasedVersion], [] as ProductVersion[]),
                tap(savedVersions => onProgress?.(savedVersions.length, uniqueProductVersionIds.length)),
                last(),
            )),
        );
    }

    public getReleasedProductVersionCount(productId: string): Observable<number> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('productId', productId),
            Query.equal('status', [ProductVersionStatus.Released, ProductVersionStatus.Obsolete]),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.total),
        );
    }

    public deleteProductVersion(productVersionId: string): Observable<void> {
        return this.appwrite.delete(Collection.productVersion, productVersionId);
    }

    public loadPlasticsColorCodes(): Observable<PlasticsColorCode[]> {
        return this.appwrite.list<PlasticsColorCode>(Collection.plasticsColorCode, [
            Query.orderAsc('code'),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public setPlasticsColorCode(colorCode: PlasticsColorCode): Observable<void> {
        return from(this.appwrite.collection<PlasticsColorCode>(Collection.plasticsColorCode).doc(colorCode.id).set(colorCode));
    }

    public deletePlasticsColorCode(colorCodeId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsColorCode, colorCodeId);
    }

    public setCurrentReleasedProductVersionToObsolete(productId: string) {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('productId', productId),
            Query.equal('status', ProductVersionStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            switchMap((productVersions) => {
                const updates$ = productVersions.map(v => from(this.appwrite.collection<ProductVersion>(Collection.productVersion).doc(v.id).set({ ...v, status: ProductVersionStatus.Obsolete } as ProductVersion)));
                return forkJoin(updates$).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public loadProductVersionsByProductId(productId: string): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedProductVersionsByFinishingProcedureId(finishingProcedureId: string): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('finishingProcedureId', finishingProcedureId),
            Query.equal('status', ProductVersionStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadProductsAndReleasedProductVersionsByIds(productIds: string[]): Observable<{
        products: Product[];
        productVersions: ProductVersion[];
    }> {
        if (productIds.length === 0) {
            return of({ products: [], productVersions: [] });
        }

        return this.loadProductsByIds(productIds).pipe(
            switchMap((products) => {
                if (products.length === 0) {
                    return of({ products, productVersions: [] });
                }

                const uniqueProductIds = [...new Set(products.map(p => p.id))];
                return this.loadReleasedProductVersionsByProductIds(uniqueProductIds).pipe(
                    map(productVersions => ({
                        products,
                        productVersions,
                    })),
                );
            })
        );
    }
    public loadReleasedProductVersionsByProductIds(productIds: string[]): Observable<ProductVersion[]> {
        return this.appwrite.listByFieldValues<ProductVersion>(
            Collection.productVersion,
            'productId',
            productIds,
            [
                Query.equal('status', ProductVersionStatus.Released),
            ],
        );
    }

    public loadReleasedProductVersionsByProductIdsBatched(productIds: string[]): Observable<ProductVersion[]> {
        const uniqueProductIds = Array.from(new Set(productIds.filter(id => !!id)));
        if (uniqueProductIds.length === 0) {
            return of([]);
        }
        const batches = this.toIdBatches(uniqueProductIds);
        return forkJoin(batches.map(batch => this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('status', ProductVersionStatus.Released),
            Query.equal('productId', batch),
            Query.limit(batch.length),
        ]).pipe(
            map(resp => resp.items),
        ))).pipe(
            defaultIfEmpty([]),
            map(groups => (groups as ProductVersion[][]).flat()),
        );
    }

    private toIdBatches(ids: string[], batchSize = PlasticsApiService.ID_BATCH_SIZE): string[][] {
        const uniqueIds = Array.from(new Set(ids.filter(id => !!id)));
        const batches: string[][] = [];
        for (let i = 0; i < uniqueIds.length; i += batchSize) {
            batches.push(uniqueIds.slice(i, i + batchSize));
        }
        return batches;
    }

    public loadProductVersionsByIds(productVersionIds: string[]): Observable<ProductVersion[]> {
        return this.appwrite.listByIds<ProductVersion>(Collection.productVersion, productVersionIds);
    }

    public loadProductVersionsByFixtureId(fixtureId: string): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('fixtureId', fixtureId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedProductVersionsByMoldFamilyId(moldFamilyId: string): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('moldFamilyId', moldFamilyId),
            Query.equal('status', ProductVersionStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedGroceryStoreProductVersions(): Observable<ProductVersion[]> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('groceryStoreProduct', true),
            Query.equal('status', ProductVersionStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedProductVersionByObsoleteProductVersionId(
        productVersionId: string
    ): Observable<ProductVersion> {
        return this.loadProductVersionById(productVersionId).pipe(
            switchMap((version) => {
                if (!version?.productId) {
                    return of(null as unknown as ProductVersion);
                }
                return this.loadReleasedProductVersionByProductId(version.productId)
            })
        )
    }

    public loadProductVersionsByObsoleteProductVersionId(
        productVersionId: string
    ): Observable<ProductVersion[]> {
        return this.loadProductVersionById(productVersionId).pipe(
            switchMap((version) => {
                if (!version?.productId) {
                    return of([]);
                }
                return this.loadProductVersionsByProductId(version.productId)
            })
        )
    }

    /**
     * CAUTION: a part number does NOT identify a product. The same number is
     * legitimately released on two products at once — the "As Molded" one (off the
     * machine, warehouse-facing) and the "Finished" one (what ships, office-facing)
     * — and a few are duplicated within a single type, so this returns an arbitrary
     * one of the matches. Only use it to answer "does any released version already
     * use this part number?" (the create-product duplicate check). To get the
     * version behind a known product, use loadReleasedProductVersionsByProductIds
     * /...Batched instead.
     */
    public loadReleasedProductVersionByPartNumber(partNumber: string): Observable<ProductVersion> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('partNumber', partNumber),
            Query.equal('status', ProductVersionStatus.Released),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0]),
        );
    }

    public setProductInstance(instance: ProductInstance): Observable<void> {
        return from(this.appwrite.collection<ProductInstance>(Collection.productInstance).doc(instance.id).set(instance))
    }

    public loadProductInstances(): Observable<ProductInstance[]> {
        return from(this.appwrite.collection<ProductInstance>(Collection.productInstance).get()).pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadProductInstancesByProductId(productId: string): Observable<ProductInstance[]> {
        return this.appwrite.list<ProductInstance>(Collection.productInstance, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadProductInstancesBetween(startDate: Date, endDate: Date): Observable<ProductInstance[]> {
        return this.appwrite.collection<ProductInstance>(Collection.productInstance, ref =>
            ref.where('createdOn', '>=', startDate).where('createdOn', '<=', endDate)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadGoodProductInstancesAfterDateByMoldingTaskId(startDate: Date, moldingTaskId: string): Observable<ProductInstance[]> {
        return this.appwrite.list<ProductInstance>(Collection.productInstance, [
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.equal('outcome', RotoMachinePartOutcome.GOOD),
            Query.equal('scheduledProductId', moldingTaskId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadGoodProductInstancesAfterDateByMoldingTaskIds(startDate: Date, moldingTaskIds: string[]): Observable<ProductInstance[]> {
        return this.appwrite.listByFieldValues<ProductInstance>(
            Collection.productInstance,
            'scheduledProductId',
            moldingTaskIds,
            [
                Query.greaterThanEqual('createdOn', startDate.toISOString()),
                Query.equal('outcome', RotoMachinePartOutcome.GOOD),
            ],
        );
    }

    public setProductInventoryTransaction(transaction: InventoryTransaction): Observable<void> {
        return this.sharedApi.setInventoryTransaction(transaction);
    }

    public getAllProductInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionAll(InventoryTransactionType.PlasticsProduct);
    }

    public getLoadProductInventoryTransactionByDateRange(startDate: Date, endDate: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByDateRange(InventoryTransactionType.PlasticsProduct, startDate, endDate);
    }

    public getProductInventoryTransactionsFromDateValueChanges(startDate: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.getInventoryTransactionFromDateValueChanges(InventoryTransactionType.PlasticsProduct, startDate);
    }

    public loadProductInventoryTransactionsFromDate(startDate: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByDateRange(InventoryTransactionType.PlasticsProduct, startDate, new Date());
    }

    public loadRecentProductInventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.PlasticsProduct);
    }

    public loadProductInventoryTransactionsBetween(startDate: Date, endDate: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByDateRange(InventoryTransactionType.PlasticsProduct, startDate, endDate);
    }

    public loadProductInventoryTransactionsByProductId(productId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityIdWithChangeFilter(productId, InventoryTransactionType.PlasticsProduct, 1);
    }

    public getInventoryTransactionsByProductId(productId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityId(productId, InventoryTransactionType.PlasticsProduct);
    }

    public loadProductVersionMoldConfigByProductVersionId(productVersionId: string): Observable<ProductVersionMoldConfig> {
        return this.appwrite.list<ProductVersionMoldConfig>(Collection.productVersionMoldConfig, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0] ?? (null as unknown as ProductVersionMoldConfig)),
        );
    }

    public loadProductVersionMoldConfigsByProductVersionIds(productVersionIds: string[]): Observable<ProductVersionMoldConfig[]> {
        const ids = [...new Set(productVersionIds.filter(id => !!id))];
        if (ids.length === 0) return of([]);

        return this.appwrite.listByFieldValues<ProductVersionMoldConfig>(
            Collection.productVersionMoldConfig,
            'productVersionId',
            ids,
        );
    }

    public setProductVersionMoldConfig(productVersionMoldConfig: ProductVersionMoldConfig): Observable<void> {
        return from(this.appwrite.collection<ProductVersionMoldConfig>(Collection.productVersionMoldConfig).doc(productVersionMoldConfig.id).set(productVersionMoldConfig));
    }

    public loadBillOfMaterialsLineItems(): Observable<BillOfMaterialsLineItem[]> {
        return this.appwrite.list<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadBillOfMaterialsLineItemsByProductVersionId(productVersionId: string): Observable<BillOfMaterialsLineItem[]> {
        return this.appwrite.list<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadBillOfMaterialsLineItemsByEntityId(entityId: string): Observable<BillOfMaterialsLineItem[]> {
        return this.appwrite.list<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, [
            Query.equal('entityId', entityId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadBillOfMaterialsLineItemsByEntityIds(entityIds: string[]): Observable<BillOfMaterialsLineItem[]> {
        const uniqueEntityIds = Array.from(new Set(entityIds.filter(id => !!id)));
        if (!uniqueEntityIds.length) return of([]);
        return this.appwrite.listByFieldValues<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, 'entityId', uniqueEntityIds);
    }

    public loadBillOfMaterialsLineItemsByProductVersionIds(productVersionIds: string[]): Observable<BillOfMaterialsLineItem[]> {
        return this.appwrite.listByFieldValues<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, 'productVersionId', productVersionIds);
    }


    public setBillOfMaterialsLineItem(billOfMaterialsLineItem: BillOfMaterialsLineItem): Observable<void> {
        return from(this.appwrite.collection<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem).doc(billOfMaterialsLineItem.id).set(billOfMaterialsLineItem));
    }

    public setBillOfMaterialsLineItems(billOfMaterialsLineItems: BillOfMaterialsLineItem[]): Observable<void[]> {
        return forkJoin(billOfMaterialsLineItems.map(li => this.setBillOfMaterialsLineItem(li))).pipe(
            defaultIfEmpty([] as void[]),
        );
    }

    public deleteBillOfMaterialsLineItem(lineItemId: string): Observable<void> {
        return this.appwrite.delete(Collection.billOfMaterialsLineItem, lineItemId);
    }

    public deleteBillOfMaterialsLineItemsByIds(lineItemIds: string[]): Observable<void[]> {
        return forkJoin(lineItemIds.map(id => this.appwrite.delete(Collection.billOfMaterialsLineItem, id))).pipe(
            defaultIfEmpty([] as void[]),
        );
    }

    public getBillOfMaterialsLineItemsByHardwareItemId(hardwareItemId: string): Observable<BillOfMaterialsLineItem[]> {
        return this.appwrite.list<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, [
            Query.equal('entityId', hardwareItemId),
            Query.equal('type', BillOfMaterialsLineItemType.Hardware),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public getBillOfMaterialsLineItemsByMaterialId(materialId: string): Observable<BillOfMaterialsLineItem[]> {
        return this.appwrite.list<BillOfMaterialsLineItem>(Collection.billOfMaterialsLineItem, [
            Query.equal('entityId', materialId),
            Query.equal('type', BillOfMaterialsLineItemType.Material),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }


    // ============================
    // Plastics Material
    // ============================
    // CRUD and query operations for plastics materials (resins, colorants, etc.)
    // Includes child entity: PlasticsMaterialInventoryTransaction

    public setMaterial(material: PlasticsMaterial): Observable<void> {
        return from(this.appwrite.collection<PlasticsMaterial>(Collection.plasticsMaterial).doc(material.id).set(material));
    }

    public loadMaterials(): Observable<PlasticsMaterial[]> {
        return this.appwrite.list<PlasticsMaterial>(Collection.plasticsMaterial, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMaterialById(materialId: string): Observable<PlasticsMaterial> {
        return this.appwrite.get<PlasticsMaterial>(Collection.plasticsMaterial, materialId);
    }

    public loadMaterialsByIds(materialIds: string[]): Observable<PlasticsMaterial[]> {
        return this.appwrite.listByIds<PlasticsMaterial>(Collection.plasticsMaterial, materialIds);
    }

    public loadMaterialByIdentifier(identifier: string): Observable<PlasticsMaterial> {
        return this.appwrite.list<PlasticsMaterial>(Collection.plasticsMaterial, [
            Query.equal('identifier', identifier),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0]),
        );
    }

    public loadMaterialsByIdentifiers(identifiers: string[]): Observable<PlasticsMaterial[]> {
        return this.appwrite.listByFieldValues<PlasticsMaterial>(Collection.plasticsMaterial, 'identifier', identifiers);
    }

    public loadMaterialInventoryTransactionsByMaterialId(materialId: string): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByEntityIdRecent(materialId, InventoryTransactionType.PlasticsMaterial, 50).pipe(
            catchError(error => {
                console.error('Error loading scheduled products:', error);
                return throwError(() => new Error('Failed to load scheduled products.'));
            }),
        )
    }

    public newMaterialInventoryTransaction(
        materialId: string,
        changeQty: number,
        comment: string,
        hardwareItemIdToQuantityChangeDict: Record<string, number>,
        curentUser: User,
        processId: string | null = null,
    ): Observable<InventoryTransaction> {

        const transaction: InventoryTransaction = {
            id: this.appwrite.createId(),
            entityId: materialId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: curentUser?.id || "",
            type: InventoryTransactionType.PlasticsMaterial,
        };

        const materialUpdate$: Observable<void> = this.appwrite.get<PlasticsMaterial>(Collection.plasticsMaterial, materialId).pipe(
            switchMap(m => from(this.appwrite.collection<PlasticsMaterial>(Collection.plasticsMaterial).doc(m.id).set({ ...m, qtyOnHand: (m.qtyOnHand ?? 0) + changeQty }))),
        );

        const transactionCreate$: Observable<void> = this.sharedApi.setInventoryTransaction(transaction);

        const hardwareItemUpdates$: Observable<void>[] = Object.entries(hardwareItemIdToQuantityChangeDict).map(([hardwareItemId, change]) => {
            return this.appwrite.get<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemId).pipe(
                switchMap(item => {
                    if (item.isUnitCounted) {
                        return from(this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(item.id).set({ ...item, qtyOnHand: (item.qtyOnHand ?? 0) + change }));
                    }
                    return of(undefined);
                })
            );
        });

        const hardwareItemTransactions$: Observable<void>[] = Object.entries(hardwareItemIdToQuantityChangeDict).map(([hardwareItemId, change]) => {
            return this.appwrite.get<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemId).pipe(
                switchMap(item => {
                    if (item.isUnitCounted) {
                        const hardwareTransaction: InventoryTransaction = {
                            id: this.appwrite.createId(),
                            entityId: hardwareItemId,
                            change,
                            comment,
                            createdOn: new Date(),
                            createdById: curentUser?.id || "",
                            type: InventoryTransactionType.PlasticsHardware,
                        };
                        return this.sharedApi.setInventoryTransaction(hardwareTransaction);
                    }
                    return of(undefined);
                })
            );
        });

        return forkJoin([
            materialUpdate$,
            transactionCreate$,
            ...hardwareItemUpdates$,
            ...hardwareItemTransactions$,
        ]).pipe(
            map(() => transaction)
        )

    }

    public loadRecentMaterialnventoryTransactions(): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionRecentByType(InventoryTransactionType.PlasticsMaterial);
    }

    public loadMaterialnventoryTransactionsFromDate(date: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.getInventoryTransactionFromDateValueChanges(InventoryTransactionType.PlasticsMaterial, date);
    }

    public loadMaterialInventoryTransactionsFromDate(startDate: Date): Observable<InventoryTransaction[]> {
        return this.sharedApi.loadInventoryTransactionByDateRange(InventoryTransactionType.PlasticsMaterial, startDate, new Date());
    }

    public loadMaterialInventoryTransactionsBetween(startDate: Date, endDate: Date): Observable<InventoryTransaction[]> {
        return this.appwrite.listAll<InventoryTransaction>(Collection.inventoryTransaction, [
            Query.equal('type', InventoryTransactionType.PlasticsMaterial),
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.lessThanEqual('createdOn', endDate.toISOString()),
        ]);
    }


    // ============================
    // Plastics Material Mixing Task
    // ============================
    // Operations for material mixing tasks and inventory updates

    public loadMaterialMixingTasks(): Observable<PlasticsMaterialMixingTask[]> {
        return this.appwrite.list<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadCompletedMaterialMixingTasksBetween(
        startDate: Date,
        endDateExclusive: Date,
    ): Observable<PlasticsMaterialMixingTask[]> {
        return this.appwrite.listAll<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask, [
            Query.equal('status', PlasticMaterialMixingTaskStatus.Completed),
            Query.greaterThanEqual('completedOn', startDate.toISOString()),
            Query.lessThan('completedOn', endDateExclusive.toISOString()),
            Query.orderDesc('completedOn'),
        ]);
    }

    public loadMaterialMixingTasksByOrderedProductId(orderedProductId: string): Observable<PlasticsMaterialMixingTask[]> {
        return this.appwrite.list<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask, [
            Query.equal('orderedProductId', orderedProductId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMaterialMixingTasksByAssociatedProductId(productId: string): Observable<PlasticsMaterialMixingTask[]> {
        return this.appwrite.list<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask, [
            Query.equal('associatedProductId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public countMaterialMixingTasksByOrderedProductId(orderedProductId: string): Observable<number> {
        return this.appwrite.list<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask, [
            Query.equal('orderedProductId', orderedProductId),
            Query.equal('status', [
                PlasticMaterialMixingTaskStatus.Released,
                PlasticMaterialMixingTaskStatus.Waiting,
            ]),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.total),
        );
    }


    public getMaterialMixingTasksByOrderedProductIdValueChanges(orderedProductId: string): Observable<PlasticsMaterialMixingTask[]> {
        return this.appwrite.collection<PlasticsMaterialMixingTask>(
            Collection.plasticsMaterialMixingTask,
            ref => ref.where('orderedProductId', '==', orderedProductId)
        ).valueChanges();
    }

    public saveMaterialMixingOrder(order: PlasticsMaterialMixingTask): Observable<void> {
        return this.setMaterialMixingTask(order);
    }

    public setMaterialMixingTask(task: PlasticsMaterialMixingTask): Observable<void> {
        return from(this.appwrite.collection<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask).doc(task.id).set(task));
    }

    public deleteMaterialMixingTask(taskId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsMaterialMixingTask, taskId);
    }

    public setMaterialMixingTasks(tasks: PlasticsMaterialMixingTask[]): Observable<void[]> {
        return forkJoin(tasks.map(t => this.setMaterialMixingTask(t))).pipe(
            defaultIfEmpty([])
        )
    }

    // ----- Plastics Purchase Requisitions (inventory manager queue) -----

    public setPurchaseRequisition(requisition: PlasticsPurchaseRequisition): Observable<void> {
        return from(this.appwrite.collection<PlasticsPurchaseRequisition>(Collection.plasticsPurchaseRequisition).doc(requisition.id).set(requisition));
    }

    public setPurchaseRequisitions(requisitions: PlasticsPurchaseRequisition[]): Observable<void[]> {
        return forkJoin(requisitions.map(r => this.setPurchaseRequisition(r))).pipe(
            defaultIfEmpty([])
        );
    }

    public deletePurchaseRequisition(requisitionId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsPurchaseRequisition, requisitionId);
    }

    public deletePurchaseRequisitions(requisitionIds: string[]): Observable<void[]> {
        return forkJoin(requisitionIds.map(id => this.deletePurchaseRequisition(id))).pipe(
            defaultIfEmpty([]),
        );
    }

    public loadPurchaseRequisitionsByOrderedProductId(orderedProductId: string): Observable<PlasticsPurchaseRequisition[]> {
        return this.appwrite.list<PlasticsPurchaseRequisition>(Collection.plasticsPurchaseRequisition, [
            Query.equal('plasticsOrderedProductId', orderedProductId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadPurchaseRequisitionsByPurchaseRequestId(purchaseRequestId: string): Observable<PlasticsPurchaseRequisition[]> {
        return this.appwrite.list<PlasticsPurchaseRequisition>(Collection.plasticsPurchaseRequisition, [
            Query.equal('purchaseRequestId', purchaseRequestId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    /**
     * The purchasing page needs the requisitions for a whole screen of requests at once, not one
     * request at a time. The per-batch limit is deliberate: listByFieldValues adds none of its own,
     * and without it Appwrite falls back to 25 rows per batch and silently drops coverage.
     */
    public loadPurchaseRequisitionsByPurchaseRequestIds(purchaseRequestIds: string[]): Observable<PlasticsPurchaseRequisition[]> {
        const ids = Array.from(new Set(purchaseRequestIds.filter(id => !!id)));
        if (!ids.length) {
            return of([]);
        }
        return this.appwrite.listByFieldValues<PlasticsPurchaseRequisition>(
            Collection.plasticsPurchaseRequisition,
            'purchaseRequestId',
            ids,
            [],
            30,
            5000,
        );
    }

    /**
     * All live requisitions. A requisition is ungrouped when purchaseRequestId is '', and stamped
     * with its request id once released into a purchase request (it is kept, not deleted, to trace
     * the request back to its ordered products).
     *
     * A plain load rather than a subscription. The collection is only written by the page that
     * reads it, and setPurchaseRequisitions writes one document per line, so watching it turned a
     * single request into one event per line — each re-running the caller's dependent fetches.
     * Callers reload after their own writes instead.
     */
    public loadPurchaseRequisitions(): Observable<PlasticsPurchaseRequisition[]> {
        return this.appwrite.list<PlasticsPurchaseRequisition>(Collection.plasticsPurchaseRequisition, [
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public updateMaterialMixingInventory(
        materialId: string,
        releasedGroupId: string,
        quantityChange: number,
        currentUser: User
    ) {
        const normalizeDictToProportions = (dict: Record<string, number>): Record<string, number> => {
            const entries = Object.entries(dict);
            const total = entries.reduce((sum, [, value]) => sum + value, 0);

            return total === 0
                ? Object.fromEntries(entries.map(([key]) => [key, 0]))
                : Object.fromEntries(entries.map(([key, value]) => [key, value / total]));
        }

        return this.loadMaterialById(materialId).pipe(
            mergeMap((material) => {

                const materialUpdate$: Observable<void> = this.incrementField(Collection.plasticsMaterial, material.id, 'qtyOnHand', quantityChange);

                const comment = `created with mixing task group (${releasedGroupId})`

                const materialInventoryTransaction: InventoryTransaction = {
                    id: this.appwrite.createId(),
                    entityId: material.id,
                    change: quantityChange,
                    comment,
                    createdOn: new Date(),
                    createdById: currentUser.id,
                    type: InventoryTransactionType.PlasticsMaterial,
                }

                const materialInventoryTransaction$ = from(this.appwrite
                    .collection<InventoryTransaction>(Collection.inventoryTransaction)
                    .doc(materialInventoryTransaction.id)
                    .set(materialInventoryTransaction)
                );

                const ingredientHardwareItems$: Observable<PlasticsHardwareItem>[] = material.ingredients.map((ingredient) => {
                    return this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(ingredient.hardwareItemId).get().pipe(
                        map(resp => fromAppwriteDoc(resp))
                    )
                })

                const hardwareInventoryTransactions$ = forkJoin(ingredientHardwareItems$).pipe(
                    switchMap(ingredientHardwareItems => {
                        const poundsHardwareItemIdToIngredientQuantityDict: Record<string, number> = Object.fromEntries(
                            ingredientHardwareItems
                                .filter(hardwareItem => hardwareItem.units === HardwareItemUnit.Pounds)
                                .map((hardwareItem) => {
                                    const ingredient = material.ingredients.find((ingredient) => ingredient.hardwareItemId === hardwareItem.id) as PlasticsMaterialIngredient;
                                    return [ingredient.hardwareItemId, ingredient.quantity]
                                }))
                        const gramsHardwareItemIdToIngredientQuantityDict: Record<string, number> = Object.fromEntries(
                            ingredientHardwareItems
                                .filter(hardwareItem => hardwareItem.units === HardwareItemUnit.Grams)
                                .map((hardwareItem) => {
                                    const ingredient = material.ingredients.find((ingredient) => ingredient.hardwareItemId === hardwareItem.id) as PlasticsMaterialIngredient;
                                    return [ingredient.hardwareItemId, ingredient.quantity]
                                }))

                        const poundsHardwareItemIdToProportionalIngredientQuantityDict: Record<string, number> = normalizeDictToProportions(poundsHardwareItemIdToIngredientQuantityDict)

                        const totalPounds = Object.values(poundsHardwareItemIdToIngredientQuantityDict).reduce((sum, value) => sum + value, 0)


                        const transactions$ = ingredientHardwareItems
                            .filter(hardwareItem => (hardwareItem.units === HardwareItemUnit.Pounds || hardwareItem.units === HardwareItemUnit.Grams) && hardwareItem.isUnitCounted)
                            .map((hardwareItem) => {
                                if (hardwareItem.units === HardwareItemUnit.Pounds) {
                                    const poundsProportion = poundsHardwareItemIdToProportionalIngredientQuantityDict[hardwareItem.id];
                                    const value = -Math.round(quantityChange * poundsProportion);
                                    return this.newHardwareInventoryTransaction(
                                        hardwareItem.id,
                                        value,
                                        comment,
                                        currentUser,
                                    )
                                } else {
                                    const grams = gramsHardwareItemIdToIngredientQuantityDict[hardwareItem.id]
                                    const value = -Math.round(quantityChange * (grams / totalPounds))
                                    return this.newHardwareInventoryTransaction(
                                        hardwareItem.id,
                                        value,
                                        comment,
                                        currentUser,
                                    )
                                }
                            })

                        return forkJoin(transactions$)

                    })
                )

                return forkJoin([
                    materialUpdate$,
                    materialInventoryTransaction$,
                    hardwareInventoryTransactions$
                ])

            })
        )
    }

    public getMaterialMixingOrderValueChanges(): Observable<PlasticsMaterialMixingTask[]> {
        return this.appwrite.collection<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask).valueChanges();
    }

    public getReleasedMaterialMixingTasksValueChanges() {
        return this.appwrite.collection<PlasticsMaterialMixingTask>(
            Collection.plasticsMaterialMixingTask,
            ref => ref.where('status', '==', PlasticMaterialMixingTaskStatus.Released)
        ).valueChanges();
    }

    public getMaterialMixingTasksValueChanges(
        statuses: PlasticMaterialMixingTaskStatus[] = [
            PlasticMaterialMixingTaskStatus.Released,
            PlasticMaterialMixingTaskStatus.Unassigned,
            PlasticMaterialMixingTaskStatus.Waiting,
        ]
    ) {
        return this.appwrite.collection<PlasticsMaterialMixingTask>(
            Collection.plasticsMaterialMixingTask,
            ref => ref.where('status', 'in', statuses)
        ).valueChanges();
    }

    public getCustomMaterialMixingTasksValueChanges(
        statuses: PlasticMaterialMixingTaskStatus[] = [
            PlasticMaterialMixingTaskStatus.Released,
            PlasticMaterialMixingTaskStatus.Unassigned,
            PlasticMaterialMixingTaskStatus.Waiting,
        ]
    ) {
        return this.appwrite.collection<PlasticsMaterialMixingTask>(
            Collection.plasticsMaterialMixingTask,
            ref => ref
                .where('status', 'in', statuses)
                // not associated with an ordered product
                .where('orderedProductId', '==', '')
        ).valueChanges();
    }


    // ============================
    // Plastics Molding Task
    // ============================
    // CRUD and query operations for rotational molding tasks

    public loadScheduledProductById(scheduledProductId: string): Observable<PlasticsMoldingTask> {
        return this.loadMoldingTaskById(scheduledProductId);
    }

    public loadMoldingTasks(): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.list<PlasticsMoldingTask>(Collection.plasticsMoldingTask, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadCompletedMoldingTasksBetween(
        startDate: Date,
        endDateExclusive: Date,
    ): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.listAll<PlasticsMoldingTask>(Collection.plasticsMoldingTask, [
            Query.equal('status', PlasticsMoldingTaskStatus.Complete),
            Query.greaterThanEqual('completedOn', startDate.toISOString()),
            Query.lessThan('completedOn', endDateExclusive.toISOString()),
            Query.orderDesc('completedOn'),
        ]);
    }

    public loadMoldingTasksByProductId(productId: string): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.list<PlasticsMoldingTask>(Collection.plasticsMoldingTask, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMoldingTasksByProductIds(
        productIds: string[],
        statuses: PlasticsMoldingTaskStatus[] = [PlasticsMoldingTaskStatus.IN_PROGRESS],
    ): Observable<PlasticsMoldingTask[]> {
        const uniqueProductIds = Array.from(new Set(productIds.filter(id => !!id)));
        if (uniqueProductIds.length === 0) {
            return of([]);
        }

        return this.appwrite.listByFieldValues<PlasticsMoldingTask>(
            Collection.plasticsMoldingTask,
            'productId',
            uniqueProductIds,
            [Query.equal('status', statuses)],
        );
    }

    public loadMoldingTaskById(moldingTaskId: string): Observable<PlasticsMoldingTask> {
        return this.appwrite.get<PlasticsMoldingTask>(Collection.plasticsMoldingTask, moldingTaskId);
    }

    public loadMoldingTasksByIds(moldingTaskIds: string[]): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.listByIds<PlasticsMoldingTask>(Collection.plasticsMoldingTask, moldingTaskIds);
    }

    public saveMoldingTaskEdits(original: PlasticsMoldingTask, edited: PlasticsMoldingTask): Observable<void> {
        return defer(() => saveMoldingTaskAdminEdit(new TablesDB(this.appwrite.client), this.appwrite.databaseId, original, edited));
    }

    public setMoldingTask(moldingTask: PlasticsMoldingTask): Observable<void> {
        return this.appwrite.get<PlasticsMoldingTask>(Collection.plasticsMoldingTask, moldingTask.id).pipe(
            catchError(error => error.code === 404 ? of(null) : throwError(() => error)),
            switchMap(previous => {
                if (!previous || (previous.status === moldingTask.status && previous.moldId === moldingTask.moldId)) return this.appwrite.set(Collection.plasticsMoldingTask, moldingTask.id, moldingTask);
                return forkJoin({ molds: this.appwrite.listAll<PlasticsMold>(Collection.plasticsMold), schedule: this.loadRotoSchedule() }).pipe(
                    switchMap(({ molds, schedule }) => {
                        if (!molds.some(mold => mold.placement?.taskId === moldingTask.id)) return this.appwrite.set(Collection.plasticsMoldingTask, moldingTask.id, moldingTask);
                        return this.moldCheckups.transition({ ...schedule, expectedLastSavedOn: schedule.lastSavedOn,
                            productLists: moldingTask.status === PlasticsMoldingTaskStatus.IN_PROGRESS ? schedule.productLists
                                : schedule.productLists.map(list => ({ ...list, moldingTaskIds: list.moldingTaskIds.filter(id => id !== moldingTask.id) })) }, [moldingTask]);
                    }),
                );
            }),
        );
    }

    public setMoldingTasks(tasks: PlasticsMoldingTask[]): Observable<void> {
        if (tasks.length === 0) {
            return of(void 0);
        }
        return forkJoin(tasks.map(task => this.setMoldingTask(task))).pipe(
            map(() => void 0),
        );
    }

    public getMoldingTasksByIdsValueChanges(moldingTaskIds: string[]): Observable<PlasticsMoldingTask[]> {
        const uniqueMoldingTaskIds = Array.from(new Set(moldingTaskIds.filter(id => !!id)));

        if (uniqueMoldingTaskIds.length === 0) {
            return of([]);
        }

        const batchSize = 30;
        const batches: string[][] = [];
        for (let i = 0; i < uniqueMoldingTaskIds.length; i += batchSize) {
            batches.push(uniqueMoldingTaskIds.slice(i, i + batchSize));
        }

        const listeners$ = batches.map((batch) =>
            this.appwrite.collection<PlasticsMoldingTask>(
                Collection.plasticsMoldingTask,
                ref => ref.where('$id', 'in', batch)
            ).valueChanges().pipe(
                map(tasks => tasks as PlasticsMoldingTask[])
            )
        );
        return combineLatest(listeners$).pipe(
            map(results => results.flat())
        )
    }

    public getMoldingTaskByIdValueChanges(moldingTaskId: string): Observable<PlasticsMoldingTask> {
        return this.appwrite.collection<PlasticsMoldingTask>(Collection.plasticsMoldingTask).doc(moldingTaskId).valueChanges().pipe(
            map((p => p as PlasticsMoldingTask))
        )
    }

    public getMoldingTaskValueChangesByStatus(
        statuses: PlasticsMoldingTaskStatus[] = [PlasticsMoldingTaskStatus.IN_PROGRESS]
    ): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.collection<PlasticsMoldingTask>(
            Collection.plasticsMoldingTask,
            ref => ref
                .where('status', 'in', statuses)
                .limit(5000)
        ).valueChanges();
    }

    public getMoldingTasksByProductIdsValueChanges(
        productIds: string[],
        statuses: PlasticsMoldingTaskStatus[] = [PlasticsMoldingTaskStatus.IN_PROGRESS],
    ): Observable<PlasticsMoldingTask[]> {
        const batches = this.toIdBatches(productIds);
        if (batches.length === 0) {
            return of([]);
        }

        return combineLatest(batches.map(batch => this.appwrite.collection<PlasticsMoldingTask>(
            Collection.plasticsMoldingTask,
            ref => ref
                .where('productId', 'in', batch)
                .where('status', 'in', statuses)
                .limit(5000),
        ).valueChanges())).pipe(
            map(groups => groups.flat()),
        );
    }

    public getMoldingTasksByOrderedProductIdValueChanges(orderedProductId: string): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.collection<PlasticsMoldingTask>(
            Collection.plasticsMoldingTask,
            ref => ref.where('orderedProductId', '==', orderedProductId)
        ).valueChanges();
    }

    public loadMoldingTasksByOrderedProductId(orderedProductId: string): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.list<PlasticsMoldingTask>(Collection.plasticsMoldingTask, [
            Query.equal('orderedProductId', orderedProductId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMoldingTasksByOrderedProductIds(orderedProductIds: string[]): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.listByFieldValues<PlasticsMoldingTask>(Collection.plasticsMoldingTask, 'orderedProductId', orderedProductIds);
    }

    public countMoldingTasksByOrderedProductId(orderedProductId: string): Observable<number> {
        return this.appwrite.list<PlasticsMoldingTask>(Collection.plasticsMoldingTask, [
            Query.equal('orderedProductId', orderedProductId),
            Query.equal('status', [
                PlasticsMoldingTaskStatus.IN_PROGRESS,
            ]),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.total),
        );
    }


    // ============================
    // Plastics Finishing Task & Finishing Station
    // ============================
    // CRUD and query operations for finishing tasks and stations

    public loadFinishingTaskById(finishingTaskId: string): Observable<PlasticsFinishingTask> {
        return this.appwrite.get<PlasticsFinishingTask>(Collection.plasticsFinishingTask, finishingTaskId);
    }

    /** every crew session started in the period, open or closed - the finishing labor report's raw data */
    public loadFinishingLaborSessionsStartedBetween(startDate: Date, endDate: Date): Observable<PlasticsFinishingLaborSession[]> {
        return this.appwrite.listAll<PlasticsFinishingLaborSession>(Collection.plasticsFinishingLaborSession, [
            Query.greaterThanEqual('startedOn', startDate.toISOString()),
            Query.lessThan('startedOn', endDate.toISOString()),
        ]).pipe(
            map(sessions => sessions.map(session => ({
                ...session,
                startedOn: new Date(session.startedOn),
                endedOn: session.endedOn ? new Date(session.endedOn) : null,
            }))),
        );
    }

    public loadFinishingTasksByIds(finishingTaskIds: string[]): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.listByIds<PlasticsFinishingTask>(Collection.plasticsFinishingTask, finishingTaskIds);
    }

    /** the tasks, live - in batches, as the realtime query takes a limited id list */
    public getFinishingTasksByIdsValueChanges(finishingTaskIds: string[]): Observable<PlasticsFinishingTask[]> {
        const uniqueFinishingTaskIds = [...new Set(finishingTaskIds.filter(id => !!id))];
        if (!uniqueFinishingTaskIds.length) {
            return of([]);
        }

        const batchSize = 30;
        const batches: string[][] = [];
        for (let i = 0; i < uniqueFinishingTaskIds.length; i += batchSize) {
            batches.push(uniqueFinishingTaskIds.slice(i, i + batchSize));
        }

        return combineLatest(batches.map((batch) =>
            this.appwrite.collection<PlasticsFinishingTask>(
                Collection.plasticsFinishingTask,
                ref => ref.where('$id', 'in', batch)
            ).valueChanges()
        )).pipe(
            map(results => results.flat())
        );
    }

    public setFinishingTask(finishingTask: PlasticsFinishingTask): Observable<void> {
        return from(this.appwrite.collection<PlasticsFinishingTask>(Collection.plasticsFinishingTask).doc(finishingTask.id).set(finishingTask));
    }

    public setFinishingTasks(tasks: PlasticsFinishingTask[]): Observable<void> {
        if (tasks.length === 0) {
            return of(void 0);
        }
        return forkJoin(tasks.map(task => this.setFinishingTask(task))).pipe(
            map(() => void 0),
        );
    }

    public normalizePlasticsFinishingStation(station: PlasticsFinishingStation): PlasticsFinishingStation {
        return {
            ...station,
            hideTasksFromIndex: this.normalizeHideTasksFromIndex(station.hideTasksFromIndex, station.taskIds.length),
        };
    }

    public appendFinishingTaskToStation(
        station: PlasticsFinishingStation,
        taskId: string,
    ): PlasticsFinishingStation {
        const taskIds = station.taskIds.includes(taskId)
            ? station.taskIds
            : [...station.taskIds, taskId];

        return this.normalizePlasticsFinishingStation({
            ...station,
            taskIds,
        });
    }

    public insertFinishingTaskAfter(
        station: PlasticsFinishingStation,
        anchorTaskId: string,
        insertedTaskId: string,
    ): PlasticsFinishingStation {
        const anchorIndex = station.taskIds.findIndex(taskId => taskId === anchorTaskId);
        if (anchorIndex < 0) {
            return this.appendFinishingTaskToStation(station, insertedTaskId);
        }

        const taskIds = station.taskIds.filter(taskId => taskId !== insertedTaskId);
        const insertIndex = anchorIndex + 1;
        taskIds.splice(insertIndex, 0, insertedTaskId);

        return this.normalizePlasticsFinishingStation({
            ...station,
            taskIds,
            hideTasksFromIndex: station.hideTasksFromIndex >= 0 && insertIndex <= station.hideTasksFromIndex
                ? station.hideTasksFromIndex + 1
                : station.hideTasksFromIndex,
        });
    }

    public removeFinishingTaskFromStation(
        station: PlasticsFinishingStation,
        taskId: string,
    ): PlasticsFinishingStation {
        const index = station.taskIds.findIndex(id => id === taskId);
        if (index < 0) {
            return this.normalizePlasticsFinishingStation(station);
        }

        return this.normalizePlasticsFinishingStation({
            ...station,
            taskIds: station.taskIds.filter(id => id !== taskId),
            hideTasksFromIndex: station.hideTasksFromIndex >= 0 && index < station.hideTasksFromIndex
                ? station.hideTasksFromIndex - 1
                : station.hideTasksFromIndex,
        });
    }

    public setFinishingTasksAndStationsTransactional(
        tasks: PlasticsFinishingTask[],
        stations: PlasticsFinishingStation[],
    ): Observable<void> {
        const groups: BatchSetEntityGroups = {};
        if (tasks.length) {
            groups[Collection.plasticsFinishingTask] = tasks;
        }
        if (stations.length) {
            groups[Collection.plasticsFinishingStation] = stations.map(station =>
                this.normalizePlasticsFinishingStation(station)
            );
        }

        return this.appwrite.batchSetAcrossCollectionsTransactional(groups);
    }

    public setFinishingTaskAndStationTransactional(
        task: PlasticsFinishingTask,
        station: PlasticsFinishingStation,
    ): Observable<void> {
        return this.setFinishingTasksAndStationsTransactional([task], [station]);
    }

    public setFinishingStationsTransactional(
        stations: PlasticsFinishingStation[],
    ): Observable<void> {
        return this.setFinishingTasksAndStationsTransactional([], stations);
    }

    private normalizeHideTasksFromIndex(index: number, taskCount: number): number {
        if (index < 0) {
            return -1;
        }

        return Math.min(index, taskCount);
    }

    public getFinishingTasksByOrderedProductIdValueChanges(
        orderedProductId: string
    ): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.collection<PlasticsFinishingTask>(
            Collection.plasticsFinishingTask,
            ref => ref
                .where('orderedProductId', '==', orderedProductId)
                .where('status', 'in', [
                    PlasticsFinishingTaskStatus.InProgress,
                    PlasticsFinishingTaskStatus.Completed,
                ])
        ).valueChanges();
    }

    public loadFinishingTasksByOrderedProductId(orderedProductId: string): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.list<PlasticsFinishingTask>(Collection.plasticsFinishingTask, [
            Query.equal('orderedProductId', orderedProductId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadFinishingTasksByOrderedProductIds(orderedProductIds: string[]): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.listByFieldValues<PlasticsFinishingTask>(Collection.plasticsFinishingTask, 'orderedProductId', orderedProductIds);
    }

    public countFinishingTasksByOrderedProductId(orderedProductId: string): Observable<number> {
        return this.appwrite.list<PlasticsFinishingTask>(Collection.plasticsFinishingTask, [
            Query.equal('orderedProductId', orderedProductId),
            Query.equal('status', PlasticsFinishingTaskStatus.InProgress),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.total),
        );
    }

    public loadFinishingTasks(): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.list<PlasticsFinishingTask>(Collection.plasticsFinishingTask, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadCompletedFinishingTasksBetween(
        startDate: Date,
        endDateExclusive: Date,
    ): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.listAll<PlasticsFinishingTask>(Collection.plasticsFinishingTask, [
            Query.equal('status', PlasticsFinishingTaskStatus.Completed),
            Query.greaterThanEqual('completedOn', startDate.toISOString()),
            Query.lessThan('completedOn', endDateExclusive.toISOString()),
            Query.orderDesc('completedOn'),
        ]);
    }

    // Nightly rollup rows (one per production day) whose productionDate falls in the range
    public loadPlasticsDailyProductionBetween(
        startDate: Date,
        endDateExclusive: Date,
    ): Observable<PlasticsDailyProduction[]> {
        return this.appwrite.listAll<PlasticsDailyProduction>(Collection.plasticsDailyProduction, [
            Query.greaterThanEqual('productionDate', startDate.toISOString()),
            Query.lessThan('productionDate', endDateExclusive.toISOString()),
            Query.orderAsc('productionDate'),
        ]);
    }

    public loadFinishingTasksbyProductId(productId: string): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.list<PlasticsFinishingTask>(Collection.plasticsFinishingTask, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public getFinishingTaskValueChangesByStatus(
        statuses: PlasticsFinishingTaskStatus[] = [
            PlasticsFinishingTaskStatus.InProgress,
        ]
    ): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.collection<PlasticsFinishingTask>(
            Collection.plasticsFinishingTask,
            ref => ref
                .where('status', 'in', statuses)
                .limit(5000)
        ).valueChanges();
    }

    public getPlasticFinishingStationsValueChanges(): Observable<PlasticsFinishingStation[]> {
        return this.appwrite.collection<PlasticsFinishingStation>(
            Collection.plasticsFinishingStation,
            ref => ref.limit(1000)
        ).valueChanges().pipe(
            map(stations => stations.sort((a, b) => a.name.localeCompare(b.name))),
        );
    }

    public getPlasticFinishingStationByIdValueChanges(stationId: string): Observable<PlasticsFinishingStation | null> {
        return this.appwrite.collection<PlasticsFinishingStation>(Collection.plasticsFinishingStation).doc(stationId).valueChanges().pipe(
            map((station) => station ?? null)
        );
    }

    public getPlasticFinishingTaskByIdValueChanges(taskId: string): Observable<PlasticsFinishingTask | null> {
        return this.appwrite.collection<PlasticsFinishingTask>(Collection.plasticsFinishingTask).doc(taskId).valueChanges().pipe(
            map((resp) => {
                if (!resp) {
                    return null
                }
                return resp as PlasticsFinishingTask
            }),
        )
    }


    public setPlasticsFinishingStation(station: PlasticsFinishingStation): Observable<void> {
        return from(this.appwrite.collection<PlasticsFinishingStation>(Collection.plasticsFinishingStation).doc(station.id).set(this.normalizePlasticsFinishingStation(station)));
    }

    public setPlasticsFinishingTask(task: PlasticsFinishingTask): Observable<void> {
        return this.setFinishingTask(task);
    }

    public loadActivePlasticsFinishingTasks(): Observable<PlasticsFinishingTask[]> {

        return this.appwrite.list<PlasticsFinishingTask>(Collection.plasticsFinishingTask, [
            Query.equal('status', PlasticsFinishingTaskStatus.InProgress),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadPlasticsFinishingStations(): Observable<PlasticsFinishingStation[]> {
        return this.appwrite.list<PlasticsFinishingStation>(Collection.plasticsFinishingStation, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
            map(stations => stations.sort((a, b) => a.name.localeCompare(b.name))),
        );
    }

    public loadPlasticsFinishingStationById(finishingStationId: string): Observable<PlasticsFinishingStation> {
        return this.appwrite.get<PlasticsFinishingStation>(Collection.plasticsFinishingStation, finishingStationId);
    }

    public loadPlasticsFinishingStationsByIds(finishingStationIds: string[]): Observable<PlasticsFinishingStation[]> {
        return this.appwrite.listByIds<PlasticsFinishingStation>(Collection.plasticsFinishingStation, finishingStationIds);
    }


    // ============================
    // Plastics Order
    // ============================
    // CRUD and query operations for customer orders
    // Includes child entities: PlasticsOrderedProduct, PlasticsSkid

    public getOrderCount(): Observable<number> {
        return this.appwrite.list<PlasticsOrder>(Collection.plasticsOrder, [Query.limit(1)]).pipe(
            map(resp => resp.total),
        );
    }

    public setOrder(order: PlasticsOrder): Observable<void> {
        return from(this.appwrite.collection<PlasticsOrder>(Collection.plasticsOrder).doc(order.id).set(order));
    }

    public updateOrderReviewSummary(orderId: string, changes: Pick<PlasticsOrder, 'pricingChecked' | 'estimatingConfirmed' | 'updatedOn' | 'updatedById'>): Observable<void> {
        return this.appwrite.update<PlasticsOrder>(Collection.plasticsOrder, orderId, changes);
    }

    public updateOrderReviewTruckingNote(orderId: string, changes: Pick<PlasticsOrder, 'truckingNote' | 'updatedOn' | 'updatedById'>): Observable<void> {
        return this.appwrite.update<PlasticsOrder>(Collection.plasticsOrder, orderId, changes);
    }

    /** Atomically release the order lines and record the saved acknowledgement PDF. */
    public finalizeOrder(
        order: Pick<PlasticsOrder, 'id' | 'acknowledged' | 'files' | 'updatedOn' | 'updatedById'>,
        lineUpdates: Array<{ id: string } & Partial<PlasticsOrderedProduct>>,
    ): Observable<void> {
        return this.appwrite.batchUpdateAcrossCollectionsTransactional({
            [Collection.plasticsOrder]: [order],
            ...(lineUpdates.length ? { [Collection.plasticsOrderedProduct]: lineUpdates } : {}),
        });
    }

    public deleteOrder(orderId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsOrder, orderId);
    }

    public loadOrderById(orderId: string): Observable<PlasticsOrder> {
        return this.appwrite.get<PlasticsOrder>(Collection.plasticsOrder, orderId);
    }

    public loadOrdersByIds(orderIds: string[]): Observable<PlasticsOrder[]> {
        return this.appwrite.listByIds<PlasticsOrder>(Collection.plasticsOrder, orderIds);
    }

    // --- Shipments ---

    public setShipment(shipment: PlasticsShipment): Observable<void> {
        return from(this.appwrite.collection<PlasticsShipment>(Collection.plasticsShipment).doc(shipment.id).set(shipment));
    }

    /**
     * Persists a shipment's status change and keeps its attached skids' shipped
     * stamps in sync:
     *  - moving TO Shipped stamps shippedOn/shippedById on every attached skid
     *    that isn't already shipped, so cross-shipment "previously shipped" math
     *    and the ready-to-ship pool stay correct;
     *  - moving OUT of Shipped clears those stamps on this shipment's skids
     *    (an un-ship correction returns them to the not-yet-shipped state).
     * Any other transition just persists the shipment (no skid load).
     *
     * After the persist succeeds, fans out opt-in notifications for transitions
     * into Released/Staged (see notifyShipmentStatusChange). The tablet's own
     * toggle passes suppressReleasedNotification, since the warehouse putting a
     * shipment back in its own queue isn't news to the warehouse.
     */
    public markShipmentStatus(
        shipment: PlasticsShipment,
        status: PlasticsShipmentStatus,
        userId: string,
        options: { suppressReleasedNotification?: boolean } = {},
    ): Observable<void> {
        const updated: PlasticsShipment = {
            ...shipment,
            status,
            updatedOn: new Date(),
            updatedById: userId,
        };

        const movingToShipped = status === PlasticsShipmentStatus.Shipped
            && shipment.status !== PlasticsShipmentStatus.Shipped;
        const movingFromShipped = shipment.status === PlasticsShipmentStatus.Shipped
            && status !== PlasticsShipmentStatus.Shipped;
        const movingToCanceled = status === PlasticsShipmentStatus.Canceled
            && shipment.status !== PlasticsShipmentStatus.Canceled;

        let persist$: Observable<void>;
        if (!movingToShipped && !movingFromShipped && !movingToCanceled) {
            persist$ = this.setShipment(updated);
        } else if (movingToCanceled) {
            // Canceling returns the shipment's unshipped skids to the available pool
            // (grocery skids also shed their injected order info, on the skid and its
            // packing label) so they aren't reserved forever by a dead shipment. Any
            // already-shipped skids keep their stamps. Persist the status alongside.
            persist$ = forkJoin([
                this.setShipment(updated),
                this.releaseShipmentSkids(shipment.id),
            ]).pipe(map(() => void 0));
        } else {
            // Stamp (or clear) only the skids that actually need it, then persist the
            // shipment and the changed skids together. setSkids([]) is a no-op, so an
            // empty changeset still resolves cleanly.
            persist$ = this.loadSkidsByShipmentId(shipment.id).pipe(
                switchMap(skids => {
                    const changedSkids: PlasticsSkid[] = movingToShipped
                        ? skids
                            .filter(s => s.shippedOn == null)
                            .map(s => ({ ...s, shippedOn: new Date(), shippedById: userId }))
                        : skids
                            .filter(s => s.shippedOn != null)
                            .map(s => ({ ...s, shippedOn: null, shippedById: '' }));
                    // On Shipped, reconcile the electronic record with what physically
                    // shipped: a shipment is built from planned orders, but the truck can
                    // fill up on higher-priority ones, leaving some lines with nothing on
                    // it. The qty on this shipment is skid-derived (the deprecated
                    // lineItem.quantity is always 0), so drop any line whose ordered
                    // product has no skid quantity here — only this shipment's "now" qty
                    // matters (prev/total are irrelevant). This mirrors the packing slip's
                    // QTY-0 filter, so the saved shipment matches the printed slip.
                    const persistedShipment = movingToShipped
                        ? { ...updated, lineItems: this.dropUnshippedLineItems(updated.lineItems ?? [], skids) }
                        : updated;
                    return forkJoin([
                        this.setShipment(persistedShipment),
                        this.setSkids(changedSkids),
                    ]).pipe(map(() => void 0));
                }),
            );
        }

        return persist$.pipe(
            switchMap(() => this.notifyShipmentStatusChange(
                shipment.status,
                updated,
                userId,
                options.suppressReleasedNotification ?? false,
            )),
        );
    }

    /**
     * Keeps only the shipment lines that actually have product on this shipment.
     * A line's shipped quantity is the sum of its ordered product's skid quantities
     * attached to this shipment; a line whose skids total 0 (or that has no skids)
     * never physically left, so it is dropped. Used when a shipment moves to Shipped
     * to reconcile the electronic record with the printed packing slip.
     */
    private dropUnshippedLineItems(
        lineItems: PlasticsShipmentLineItem[],
        shipmentSkids: PlasticsSkid[],
    ): PlasticsShipmentLineItem[] {
        const qtyByOrderedProductId = new Map<string, number>();
        for (const skid of shipmentSkids) {
            qtyByOrderedProductId.set(
                skid.orderedProductId,
                (qtyByOrderedProductId.get(skid.orderedProductId) ?? 0) + (skid.quantity || 0),
            );
        }
        return lineItems.filter(li => (qtyByOrderedProductId.get(li.orderedProductId) ?? 0) > 0);
    }

    /**
     * Fans out opt-in UserNotifications after a shipment status change persists:
     *  - into Released → warehouse users with ReceivesShipmentReleasedNotifications
     *    (PlasticsShipping), linking to the tablet scanner detail page, so new
     *    staging work doesn't have to be spotted by eyeballing the board;
     *  - into Staged → office users with ReceivesShipmentStagedNotifications
     *    (ShippingManager), linking to the office view page.
     * The acting user is never notified about their own change. Best-effort: any
     * failure here resolves quietly so it can't fail the status change itself.
     */
    private notifyShipmentStatusChange(
        previousStatus: PlasticsShipmentStatus,
        shipment: PlasticsShipment,
        actorUserId: string,
        suppressReleasedNotification: boolean,
    ): Observable<void> {
        const becameReleased = shipment.status === PlasticsShipmentStatus.Released
            && previousStatus !== PlasticsShipmentStatus.Released
            && !suppressReleasedNotification;
        const becameStaged = shipment.status === PlasticsShipmentStatus.Staged
            && previousStatus !== PlasticsShipmentStatus.Staged;
        if (!becameReleased && !becameStaged) return of(void 0);

        // A Draft can change status before a customer is chosen; fall back to a
        // customer-less description rather than dropping the notification.
        const customer$: Observable<Customer | null> = shipment.customerId
            ? this.commonApiService.loadCustomerById(shipment.customerId).pipe(catchError(() => of(null)))
            : of(null);

        return forkJoin([this.userApiService.getUsers(), customer$]).pipe(
            switchMap(([users, customer]) => {
                // Optional-chained: user docs that predate these abilities may lack
                // the submodule object until an admin abilities sync runs.
                const recipients = users.filter(user => user.id !== actorUserId && (becameReleased
                    ? user.abilities[PrismModule.Plastics]?.[PlasticsSubModule.PlasticsShipping]?.ReceivesShipmentReleasedNotifications
                    : user.abilities[PrismModule.Plastics]?.[PlasticsSubModule.ShippingManager]?.ReceivesShipmentStagedNotifications));
                const prefix = customer ? `${customer.name} shipment` : 'Shipment';
                const description = `${prefix} ${shipment.id.slice(-4)}: ${previousStatus} → ${shipment.status}`;
                const associatedUrl = becameReleased
                    ? `${PrismModule.Plastics}/${PlasticsSubModule.PlasticsShipping}/shipment-scanner/${shipment.id}`
                    : `${PrismModule.Plastics}/${PlasticsSubModule.ShippingManager}/shipments/view/${shipment.id}`;
                const notifications: UserNotification[] = recipients.map(user => ({
                    id: this.appwrite.createId(),
                    userId: user.id,
                    description,
                    createdOn: new Date(),
                    acknowledgedOn: null,
                    associatedUrl,
                }));
                return this.userApiService.setUserNotifications(notifications);
            }),
            catchError(() => of(void 0)),
        );
    }

    /**
     * Re-reads the current shipment document and writes it back with the new
     * status via markShipmentStatus. The refetch means a status flip (board,
     * status dialog, or tablet toggle) no longer carries a stale whole-document
     * snapshot, so it can't silently revert a comment the other department just
     * added. The editor keeps calling markShipmentStatus directly, since it is the
     * authoritative writer of the full document.
     */
    public changeShipmentStatus(
        shipmentId: string,
        status: PlasticsShipmentStatus,
        userId: string,
        options: { suppressReleasedNotification?: boolean } = {},
    ): Observable<void> {
        return this.loadShipmentById(shipmentId).pipe(
            switchMap(fresh => this.markShipmentStatus(fresh, status, userId, options)),
        );
    }

    /**
     * Appends a comment to a shipment without clobbering concurrent edits: it
     * re-reads the document immediately before writing and merges only the
     * comments array, so a status change happening at the same time survives.
     */
    public appendShipmentComment(shipmentId: string, comment: UserComment): Observable<PlasticsShipment> {
        return this.loadShipmentById(shipmentId).pipe(
            switchMap(fresh => {
                const updated: PlasticsShipment = {
                    ...fresh,
                    comments: [...(fresh.comments ?? []), comment],
                };
                return this.setShipment(updated).pipe(map(() => updated));
            }),
        );
    }

    /**
     * Releases every unshipped skid attached to a shipment back to the available
     * pool. Pass `orderedProductIds` to release only the skids on those product
     * lines (used by the editor when a line is removed); omit it to release the
     * whole shipment (used on cancel). Grocery skids also shed their injected
     * order info — see releaseSkids.
     */
    public releaseShipmentSkids(shipmentId: string, orderedProductIds?: string[]): Observable<void> {
        return forkJoin([
            this.loadSkidsByShipmentId(shipmentId),
            this.loadReleasedGroceryStoreProductVersions(),
        ]).pipe(
            switchMap(([skids, groceryVersions]) => {
                const groceryProductIds = new Set(groceryVersions.map(v => v.productId));
                const target = orderedProductIds
                    ? skids.filter(s => orderedProductIds.includes(s.orderedProductId))
                    : skids;
                return this.releaseSkids(target, groceryProductIds);
            }),
        );
    }

    /**
     * Detaches skids from their shipment and returns them to the available pool.
     * For grocery-store skids (productId is a grocery product) the injected order
     * info is also cleared from the skid AND its packing label — mirroring the
     * tablet remove flow — so the stock returns to anonymous. Made-to-order skids
     * keep their orderedProductId/SO/PO (intrinsic to the skid). Only unshipped
     * skids are touched.
     */
    private releaseSkids(skids: PlasticsSkid[], groceryProductIds: Set<string>): Observable<void> {
        const unshipped = skids.filter(s => s.shippedOn == null && s.shipmentId);
        if (unshipped.length === 0) {
            return of(void 0);
        }

        const grocerySkids = unshipped.filter(s => groceryProductIds.has(s.productId));
        const normalSkids = unshipped.filter(s => !groceryProductIds.has(s.productId));

        const clearedNormal: PlasticsSkid[] = normalSkids.map(s => ({ ...s, shipmentId: '' }));
        const clearedGrocery: PlasticsSkid[] = grocerySkids.map(s => ({
            ...s,
            shipmentId: '',
            orderedProductId: '',
            salesOrderNumber: '',
            purchaseOrderNumber: '',
        }));

        const labelIds = grocerySkids.map(s => s.packingLabelId).filter(id => !!id);
        const labels$: Observable<LabelInstance[]> = labelIds.length === 0
            ? of([])
            : this.loadLabelInstancesByIds(labelIds).pipe(
                map(labels => labels.map(l => ({
                    ...(l as LabelInstance_PackingSlip),
                    orderedProductId: '',
                    salesOrderNumber: '',
                    purchaseOrderNumber: '',
                }) as LabelInstance)),
            );

        return labels$.pipe(
            switchMap(clearedLabels => forkJoin([
                this.setSkids([...clearedNormal, ...clearedGrocery]),
                this.setLabelInstances(clearedLabels),
            ])),
            map(() => void 0),
        );
    }

    public deleteShipment(shipmentId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsShipment, shipmentId);
    }

    public loadShipmentById(shipmentId: string): Observable<PlasticsShipment> {
        return this.appwrite.get<PlasticsShipment>(Collection.plasticsShipment, shipmentId);
    }

    public loadShipmentsByIds(shipmentIds: string[]): Observable<PlasticsShipment[]> {
        if (shipmentIds.length === 0) {
            return of([]);
        }
        return forkJoin(shipmentIds.map(id => this.loadShipmentById(id))).pipe(
            defaultIfEmpty([])
        );
    }

    public loadShipments(): Observable<PlasticsShipment[]> {
        return this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    // "Active" shipments only, so callers don't have to load the whole (ever-growing)
    // collection: everything that's Released or Staged regardless of date, plus
    // Shipped shipments whose shipDate is on/after `shippedSince`. Split into two
    // server-side queries so the large Shipped set is bounded by date instead of
    // scanned client-side. Requires indexes on `status` and `shipDate` (ideally a
    // composite status+shipDate index for the second query).
    public loadActiveShipments(shippedSince: Date): Observable<PlasticsShipment[]> {
        const releasedAndStaged$ = this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [
            Query.equal('status', [PlasticsShipmentStatus.Released, PlasticsShipmentStatus.Staged]),
            Query.limit(5000),
        ]).pipe(map(resp => resp.items));

        const recentlyShipped$ = this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [
            Query.equal('status', PlasticsShipmentStatus.Shipped),
            Query.greaterThanEqual('shipDate', shippedSince.toISOString()),
            Query.limit(5000),
        ]).pipe(map(resp => resp.items));

        return forkJoin([releasedAndStaged$, recentlyShipped$]).pipe(
            map(([releasedStaged, recentlyShipped]) => [...releasedStaged, ...recentlyShipped]),
        );
    }

    // Shipments in any of the given statuses — one indexed `status` query instead of
    // loading the whole collection. Used where "active" means Draft/Released/Staged
    // (not date-bounded like loadActiveShipments), e.g. the editor's
    // already-committed-elsewhere check. Relies on the `status` index.
    public loadShipmentsByStatuses(statuses: PlasticsShipmentStatus[]): Observable<PlasticsShipment[]> {
        if (statuses.length === 0) {
            return of([]);
        }
        return this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [
            Query.equal('status', statuses),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadShipmentsByCustomerId(customerId: string): Observable<PlasticsShipment[]> {
        return this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [Query.equal('customerId', customerId), Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    // Shipments routed via a given carrier — the carrier page's history list. The
    // caller sorts newest-first. The "Best Way" default carrier has an empty
    // carrierId. Falls back to a full scan + client filter if carrierId isn't
    // indexed on the shipment collection.
    /** Lightweight shipment summary for carrier statistics: only the columns the browse page
     *  needs (carrier, customer, date, status) for shipments shipped on/after `since`, so the
     *  carriers list can show usage without loading every shipment document. */
    public loadShipmentSummaries(since: Date): Observable<Pick<PlasticsShipment, 'id' | 'carrierId' | 'customerId' | 'shipDate' | 'status'>[]> {
        return this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [
            Query.greaterThanEqual('shipDate', since.toISOString()),
            Query.select(['$id', 'carrierId', 'customerId', 'shipDate', 'status']),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadShipmentsByCarrierId(carrierId: string): Observable<PlasticsShipment[]> {
        return this.appwrite.list<PlasticsShipment>(Collection.plasticsShipment, [
            Query.equal('carrierId', carrierId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            catchError(() => this.loadShipments().pipe(
                map(shipments => shipments.filter(s => (s.carrierId || '') === carrierId)),
            )),
        );
    }

    // Shipment labels printed for a shipment — used to warn when a carrier/ship-date
    // edit makes already-printed labels stale, and to show a "labels printed" count.
    public loadShipmentLabelsByShipmentId(shipmentId: string): Observable<LabelInstance[]> {
        return this.appwrite.list<LabelInstance>(Collection.plasticsLabelInstance, [
            Query.equal('type', LabelType.Shipment),
            Query.equal('shipmentId', shipmentId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([] as LabelInstance[]),
            // Tolerate the query failing before the label-instance schema migration
            // (shipmentId attribute) is applied — the stale-label warning is advisory.
            catchError(() => of([] as LabelInstance[])),
        );
    }

    public loadOrders(): Observable<PlasticsOrder[]> {
        return this.appwrite.list<PlasticsOrder>(Collection.plasticsOrder, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    // public loadOrdersAndOrderedProducts(): Observable<{
    //     orders: PlasticsOrder[];
    //     orderedProducts: PlasticsOrderedProduct[];
    // }> {
    //     return from(this.appwriteDb.collection<PlasticsOrder>(Collection.plasticsOrder).get()).pipe(
    //         map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
    //         switchMap((orders) => {
    //             const orderedProducts$ = orders.map(o => this.loadOrderedProductsByOrderId(o.id))
    //             return forkJoin(orderedProducts$).pipe(
    //                 defaultIfEmpty([]),
    //                 map((orderedProducts: PlasticsOrderedProduct[][]) => ({
    //                     orders,
    //                     orderedProducts: orderedProducts.flat()
    //                 }))
    //             )
    //         })
    //     )
    // }

    public loadOrdersAndOrderedProducts(): Observable<{
        orders: PlasticsOrder[];
        orderedProducts: PlasticsOrderedProduct[];
    }> {
        return forkJoin({
            orders: this.loadOrders(),
            orderedProducts: this.loadOrderedProducts(),
        });
    }

    public loadOrderedProductsBetween(startDate: Date, endDate: Date): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.lessThanEqual('createdOn', endDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public deleteOrderedProduct(orderedProductId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsOrderedProduct, orderedProductId);
    }

    public setOrderedProduct(orderedProduct: PlasticsOrderedProduct): Observable<void> {
        return from(this.appwrite.collection<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct).doc(orderedProduct.id).set(orderedProduct));
    }

    public updateOrderedProductReview(
        orderedProductId: string,
        changes: Pick<PlasticsOrderedProduct, 'pricingChecked' | 'estimatingConfirmed' | 'updatedOn' | 'updatedById'>
            & Partial<Pick<PlasticsOrderedProduct, 'dueDate' | 'changeLog' | 'note'>>,
    ): Observable<void> {
        return this.appwrite.update<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, orderedProductId, changes);
    }

    // Order review releases a Draft line: status plus the planning flags and release weeks, with its change log.
    public releaseOrderedProduct(
        orderedProductId: string,
        changes: Pick<PlasticsOrderedProduct, 'status' | 'productionPlanComplete' | 'inventoryPlanComplete' | 'updatedOn' | 'updatedById' | 'changeLog'>
            & Partial<Pick<PlasticsOrderedProduct, 'scheduledReleaseWeeks'>>,
    ): Observable<void> {
        return this.appwrite.update<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, orderedProductId, changes);
    }

    public releaseOrderedProductsTogether(
        changes: Array<{ id: string }
            & Pick<PlasticsOrderedProduct, 'status' | 'productionPlanComplete' | 'inventoryPlanComplete' | 'updatedOn' | 'updatedById' | 'changeLog'>
            & Partial<Pick<PlasticsOrderedProduct, 'scheduledReleaseWeeks'>>>,
    ): Observable<void> {
        if (!changes.length) return of(undefined);
        return this.appwrite.batchUpdateAcrossCollectionsTransactional({
            [Collection.plasticsOrderedProduct]: changes,
        });
    }

    public loadOrderReviewResearchByOrderedProductIds(orderedProductIds: string[]): Observable<PlasticsOrderReviewResearch[]> {
        return this.appwrite.listByFieldValues<PlasticsOrderReviewResearch>(
            Collection.plasticsOrderReviewResearch,
            'orderedProductId',
            orderedProductIds,
        );
    }

    public setOrderedProducts(orderedProducts: PlasticsOrderedProduct[]): Observable<void> {
        if (orderedProducts.length === 0) {
            return of(void 0);
        }
        return forkJoin(orderedProducts.map(orderedProduct => this.setOrderedProduct(orderedProduct))).pipe(
            map(() => void 0),
        );
    }

    public loadOrderedProducts(): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadOrderedProductsByCustomerId(customerId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('customerId', customerId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadOrderedProductsByOrderId(orderId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('plasticsOrderId', orderId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadOrderedProductsByProductId(productId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedOrderedProductsByProductId(productId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('productId', productId),
            Query.equal('status', PlasticsOrderedProductStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadReleasedOrderedProductsByCustomerId(customerId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('customerId', customerId),
            Query.equal('status', PlasticsOrderedProductStatus.Released),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadOrderedProductsByCustomerIdAndStatuses(customerId: string, statuses: PlasticsOrderedProductStatus[]): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('customerId', customerId),
            Query.equal('status', statuses),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadOrderedProductsByproductIds(productIds: string[]): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.listByFieldValues<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, 'productId', productIds);
    }

    public loadOrderedProductsByProductIdsAndStatuses(
        productIds: string[],
        statuses: PlasticsOrderedProductStatus[],
    ): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.listByFieldValues<PlasticsOrderedProduct>(
            Collection.plasticsOrderedProduct,
            'productId',
            productIds,
            [Query.equal('status', statuses)],
            30,
            5000,
        );
    }

    public loadReleasedOrderedProductsByProductIds(productIds: string[]): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.listByFieldValues<PlasticsOrderedProduct>(
            Collection.plasticsOrderedProduct,
            'productId',
            productIds,
            [Query.equal('status', PlasticsOrderedProductStatus.Released)],
        );
    }

    public loadOrderedProductsByStatuses(
        statuses: PlasticsOrderedProductStatus[],
        oldestDate: Date | null = null,
    ): Observable<PlasticsOrderedProduct[]> {

        const queries = [
            Query.equal('status', statuses),
            Query.limit(5000),
        ];

        if (oldestDate) {
            queries.splice(1, 0, Query.greaterThan('updatedOn', oldestDate.toISOString()));
        }

        return this.appwrite.list<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, queries).pipe(
            map(resp => resp.items),
        );
    }

    public loadAllOrderedProductsByStatuses(
        statuses: PlasticsOrderedProductStatus[],
    ): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.listAll<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, [
            Query.equal('status', statuses),
        ]);
    }

    public loadAllOrderedProductsByStatusesUpdatedBetween(
        statuses: PlasticsOrderedProductStatus[],
        updatedAfter: Date,
        updatedBefore: Date | null = null,
    ): Observable<PlasticsOrderedProduct[]> {
        const queries = [
            Query.equal('status', statuses),
            Query.greaterThan('updatedOn', updatedAfter.toISOString()),
        ];
        if (updatedBefore) {
            queries.push(Query.lessThanEqual('updatedOn', updatedBefore.toISOString()));
        }
        return this.appwrite.listAll<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, queries);
    }

    public loadOrderedProductById(orderedProductId: string): Observable<PlasticsOrderedProduct> {
        return this.appwrite.get<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, orderedProductId);
    }

    public loadOrderedProductsByIds(orderedProductIds: string[]): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.listByIds<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct, orderedProductIds);
    }

    public getPlasticsOrderedProductsValueChanges(
        statuses: PlasticsOrderedProductStatus[] = [
            PlasticsOrderedProductStatus.Released,
            PlasticsOrderedProductStatus.Hold,
            PlasticsOrderedProductStatus.ToBeCanceled,
        ]
    ): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.collection<PlasticsOrderedProduct>(
            Collection.plasticsOrderedProduct,
            ref => ref.where('status', 'in', statuses)
        ).valueChanges().pipe(
            map(orderedProducts => orderedProducts.map(op => ({
                ...op,
                changeLog: op.changeLog.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime())
            })))
        )
    }

    public getPlasticsOrderedProductsByOrderIdValueChanges(plasticsOrderId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.collection<PlasticsOrderedProduct>(
            Collection.plasticsOrderedProduct,
            ref => ref.where('plasticsOrderId', '==', plasticsOrderId)
        ).valueChanges().pipe(
            map(orderedProducts => orderedProducts.map(op => ({
                ...op,
                changeLog: op.changeLog.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime())
            })))
        )
    }

    public getOrderedProductByIdValueChanges(orderedProductId: string): Observable<PlasticsOrderedProduct> {
        return this.appwrite.collection<PlasticsOrderedProduct>(Collection.plasticsOrderedProduct).doc(orderedProductId).valueChanges().pipe(
            map((p => {
                if (!p) {
                    return null as unknown as PlasticsOrderedProduct;
                }
                return p as PlasticsOrderedProduct
            }))
        )
    }

    public getOrderedProductsByProductIdValueChanges(productId: string): Observable<PlasticsOrderedProduct[]> {
        return this.appwrite.collection<PlasticsOrderedProduct>(
            Collection.plasticsOrderedProduct,
            (ref) => ref.where('productId', '==', productId)
        ).valueChanges();
    }

    public setSkid(skid: PlasticsSkid): Observable<void> {
        return from(this.appwrite.collection<PlasticsSkid>(Collection.plasticsSkid).doc(skid.id).set(skid));
    }

    public setSkids(skids: PlasticsSkid[]): Observable<void> {
        if (skids.length === 0) {
            return of(void 0);
        }
        return forkJoin(skids.map(skid => this.setSkid(skid))).pipe(
            map(() => void 0),
        );
    }

    public loadSkidById(id: string): Observable<PlasticsSkid> {
        return this.appwrite.get<PlasticsSkid>(Collection.plasticsSkid, id);
    }

    public loadSkidByLabelInstanceId(labelInstanceId: string): Observable<PlasticsSkid> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('packingLabelId', labelInstanceId),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0]),
        );
    }

    public loadSkidsByLabelInstanceIds(labelInstanceIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(Collection.plasticsSkid, 'packingLabelId', labelInstanceIds);
    }

    public loadSkids(): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadSkidsByShipmentId(shipmentId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('shipmentId', shipmentId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([] as PlasticsSkid[]),
        );
    }

    // All skids attached to any of the given shipments — used by the browse board
    // to total skid quantity per shipment.
    public loadSkidsByShipmentIds(shipmentIds: string[]): Observable<PlasticsSkid[]> {
        if (shipmentIds.length === 0) {
            return of([]);
        }
        // Batched: Appwrite caps a query value at 100 items / 4096 chars, and callers
        // that list a whole board pass more shipment ids than that in one go.
        return this.appwrite.listByFieldValues<PlasticsSkid>(
            Collection.plasticsSkid,
            'shipmentId',
            shipmentIds,
            [],
            30,
            5000,
        ).pipe(
            defaultIfEmpty([] as PlasticsSkid[]),
        );
    }

    public loadSkidsByOrderedProductCompletedOn(orderedProductCompletedOn: Date | null): Observable<PlasticsSkid[]> {
        if (!!orderedProductCompletedOn) {
            return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
                Query.greaterThanEqual('orderedProductCompletedOn', orderedProductCompletedOn.toISOString()),
                Query.limit(5000),
            ]).pipe(
                map(resp => resp.items),
                defaultIfEmpty([])
            )
        } else {
            return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
                Query.isNull('orderedProductCompletedOn'),
                Query.limit(5000),
            ]).pipe(
                map(resp => resp.items),
                defaultIfEmpty([])
            )
        }
    }
    public loadSkidsShippedOnAfterDate(startDate: Date): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.greaterThanEqual('shippedOn', startDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([])
        );
    }


    public loadReadyToShipSkidsByProductId(productId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('productId', productId),
            Query.isNull('shippedOn'),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([] as PlasticsSkid[]),
        )
    }

    // Every skid ready to ship (no shippedOn date) across all customers/products —
    // one query, used to derive ready-to-ship totals per ordered product up front.
    public loadReadyToShipSkids(): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.isNull('shippedOn'),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([] as PlasticsSkid[]),
        )
    }

    public loadSkidsByProductId(
        productId: string,
    ): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([])
        );
    }


    public loadSkidsByProductIds(productIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(Collection.plasticsSkid, 'productId', productIds);
    }

    public loadReadyToShipSkidsByProductIds(productIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(Collection.plasticsSkid, 'productId', productIds, [Query.isNull('shippedOn')]);
    }

    public getReadyToShipSkidsByProductIdsValueChanges(productIds: string[]): Observable<PlasticsSkid[]> {
        const batches = this.toIdBatches(productIds);
        if (batches.length === 0) {
            return of([]);
        }

        return combineLatest(batches.map(batch => this.appwrite.collection<PlasticsSkid>(
            Collection.plasticsSkid,
            ref => ref
                .where('productId', 'in', batch)
                .where('shippedOn', '==', null)
                .limit(5000),
        ).valueChanges())).pipe(
            map(groups => groups.flat()),
        );
    }

    public loadReadyToShipSkidsWithValidOrderedProductIds(): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.isNull('shippedOn'),
            Query.notEqual('orderedProductId', ''),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadShippedSkidsByOrderedProductId(orderedProductId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.isNotNull('shippedOn'),
            Query.equal('orderedProductId', orderedProductId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadSkidsByProductVersionId(productVersionId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([])
        );
    }

    public loadSkidsByOrderedProductIds(orderedProductIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(Collection.plasticsSkid, 'orderedProductId', orderedProductIds);
    }

    // Shipped skids (shippedOn set) for a batch of ordered products, used to total
    // how much has already shipped against each order line.
    public loadShippedSkidsByOrderedProductIds(orderedProductIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(Collection.plasticsSkid, 'orderedProductId', orderedProductIds, [Query.isNotNull('shippedOn')]);
    }


    public loadUnassignedSkids(): Observable<PlasticsSkid[]> {
        return this.loadSkidsByOrderedProductId('');
    }
    public loadUnassignedSkidsByProductIds(productIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(
            Collection.plasticsSkid,
            'productId',
            productIds,
            [Query.equal('orderedProductId', '')],
        );
    }

    public loadSkidsByOrderedProductId(orderedProductId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('orderedProductId', orderedProductId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([])
        );
    }

    public loadSkidsByCustomerId(customerId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.equal('customerId', customerId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([])
        );
    }

    public getSkidsByOrderedProductIdValueChanges(orderedProductId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.collection<PlasticsSkid>(
            Collection.plasticsSkid,
            ref => ref
                .where('orderedProductId', '==', orderedProductId)
                .where('orderedProductCompletedOn', '==', null)
        ).valueChanges();
    }

    public loadSkidsByProductVersionIds(productVersionIds: string[]): Observable<PlasticsSkid[]> {
        return this.appwrite.listByFieldValues<PlasticsSkid>(Collection.plasticsSkid, 'productVersionId', productVersionIds);
    }

    public loadUnshippedSkidsByProductId(productId: string): Observable<PlasticsSkid[]> {
        return this.appwrite.list<PlasticsSkid>(Collection.plasticsSkid, [
            Query.isNull('shippedOn'),
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public getSkidsValueChanges(): Observable<PlasticsSkid[]> {
        return this.appwrite.collection<PlasticsSkid>(Collection.plasticsSkid).valueChanges();
    }

    public deleteSkid(skidId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsSkid, skidId);
    }


    // ============================
    // Label Instance
    // ============================
    // Operations for product label instances (barcodes, FH labels, etc.)

    public setLabelInstance(labelInstance: LabelInstance): Observable<void> {
        return from(this.appwrite.collection<LabelInstance>(Collection.plasticsLabelInstance).doc(labelInstance.id).set(labelInstance))
    }

    public setLabelInstances(labelInstances: LabelInstance[]): Observable<void> {
        return forkJoin(labelInstances.map(labelInstance => this.setLabelInstance(labelInstance))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public loadLabelInstances(): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(Collection.plasticsLabelInstance).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        ))
    }

    public loadLabelInstanceById(id: string): Observable<LabelInstance> {
        return from(this.appwrite.collection<LabelInstance>(Collection.plasticsLabelInstance).doc(id).get().pipe(
            map((resp) => {
                if (!resp.exists) {
                    return null as unknown as LabelInstance;
                }
                return fromAppwriteDoc(resp)
            })
        ))
    }

    public loadLabelInstanceByCode(code: string): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            (ref: QueryRef) => ref.where('labelCode', '==', code)
        ).get().pipe(
            map((resp) => {
                if (resp.empty) {
                    return [];
                }
                return resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)
            })
        ))
    }

    public loadLabelInstanceByPurchaseOrder(purchaseOrderNumber: string): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            (ref: QueryRef) => ref.where('purchaseOrderNumber', '==', purchaseOrderNumber)
        ).get().pipe(
            map((resp) => {
                if (resp.empty) {
                    return [];
                }
                return resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)
            })
        ))
    }

    public loadLabelInstanceBySalesOrder(salesOrderNumber: string): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            (ref: QueryRef) => ref.where('salesOrderNumber', '==', salesOrderNumber)
        ).get().pipe(
            map((resp) => {
                if (resp.empty) {
                    return [];
                }
                return resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)
            })
        ))
    }

    public loadLabelInstancesByIds(ids: string[]): Observable<LabelInstance[]> {
        return this.appwrite.listByIds<LabelInstance>(Collection.plasticsLabelInstance, ids);
    }

    public loadLabelInstancesByProductVersionId(productVersionId: string): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            ref => ref.where('productVersionId', '==', productVersionId)
        ).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        ))
    }

    public loadLabelInstancesByProductVersionIds(productVersionIds: string[]): Observable<LabelInstance[]> {
        return this.appwrite.listByFieldValues<LabelInstance>(Collection.plasticsLabelInstance, 'productVersionId', productVersionIds);
    }

    public loadLabelInstancesByProductId(productId: string): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            ref => ref.where('productId', '==', productId)
        ).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        ))
    }

    public loadLabelInstancesByProductIds(productId: string[]): Observable<LabelInstance[]> {
        return this.appwrite.listByFieldValues<LabelInstance>(Collection.plasticsLabelInstance, 'productId', productId);
    }

    public getLabelInstanceValueChangesFromDate(startDate: Date): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            ref => ref.where('printDate', '>=', startDate)
        ).valueChanges().pipe(
            map((labels) => labels)
        ))
    }

    public loadLabelInstancesFromDate(startDate: Date): Observable<LabelInstance[]> {
        return this.appwrite.list<LabelInstance>(Collection.plasticsLabelInstance, [
            Query.greaterThanEqual('printDate', startDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadLabelInstancesByOrderedProductId(orderedProductId: string): Observable<LabelInstance[]> {
        return from(this.appwrite.collection<LabelInstance>(
            Collection.plasticsLabelInstance,
            ref => ref.where('orderedProductId', '==', orderedProductId)
        ).valueChanges().pipe(
            map((labels) => labels)
        ))
    }


    // ============================
    // Plastics Scrap (Instance & Reason)
    // ============================
    // Operations for scrap tracking, reasons, and active scrap instances

    public loadScrapInstancesBetween(startDate: Date, endDate: Date): Observable<PlasticsScrapInstance[]> {
        return this.appwrite.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance, ref =>
            ref.where('createdOn', '>=', startDate).where('createdOn', '<=', endDate)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public getScrapInstancesBetweenValueChanges(startDate: Date): Observable<PlasticsScrapInstance[]> {
        return from(this.appwrite.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance, ref =>
            ref.where('createdOn', '>=', startDate)
        ).valueChanges()).pipe(
            map((instances) => instances)
        )
    }

    public loadScrapInstancesFromDate(startDate: Date): Observable<PlasticsScrapInstance[]> {
        return this.appwrite.list<PlasticsScrapInstance>(Collection.plasticsScrapInstance, [
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadScrapInstancesByProductId(productId: string): Observable<PlasticsScrapInstance[]> {
        return this.appwrite.list<PlasticsScrapInstance>(Collection.plasticsScrapInstance, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadScrapInstancesByProductIdandOnCreatedBetween(productId: string, startDate: Date, endDate: Date): Observable<PlasticsScrapInstance[]> {
        return this.appwrite.list<PlasticsScrapInstance>(Collection.plasticsScrapInstance, [
            Query.equal('productId', productId),
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.lessThanEqual('createdOn', endDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadScrapInstancesByIdFromDate(productId: string, startDate: Date): Observable<PlasticsScrapInstance[]> {
        return this.appwrite.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance, ref =>
            ref.where('createdOn', '>=', startDate).where('productId', '==', productId)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadScrapInstanceById(scrapInstanceId: string): Observable<PlasticsScrapInstance> {
        return from(this.appwrite.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance).doc(scrapInstanceId).get().pipe(
            map((resp) => fromAppwriteDoc(resp))
        ))
    }

    public loadScrapReasons(): Observable<PlasticsScrapReason[]> {
        return this.appwrite.list<PlasticsScrapReason>(Collection.plasticsScrapReason, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
            map(reasons => reasons.sort((a, b) => a.name.localeCompare(b.name))),
        );
    }


    public setScrapReason(scrapReason: PlasticsScrapReason): Observable<void> {
        return from(this.appwrite.collection<PlasticsScrapReason>(Collection.plasticsScrapReason).doc(scrapReason.id).set(scrapReason))
    }

    public deleteScrapReason(scrapReasonId: string): Observable<void> {
        return from(this.appwrite.collection<PlasticsScrapReason>(Collection.plasticsScrapReason).doc(scrapReasonId).delete())
    }

    public deleteActiveScrapInstances(): Observable<void> {
        return this.setActiveScrapInstances([]);
        // return from(this.appwriteDb.collection<PlasticsScrapInstance>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ActivePlasticsScrapInstances).delete())
    }

    public setActiveScrapInstances(instances: PlasticsScrapInstance[]) {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ActivePlasticsScrapInstances).set({ instances }));
    }

    public increaseProductScrapCount(productId: string, scrapCount: number): Observable<void> {
        return this.incrementField(Collection.product, productId, 'qtyScrap', scrapCount);
    }


    public createScrapInstance(instance: PlasticsScrapInstance): Observable<void> {
        // need to updated product inventory
        return from(this.appwrite.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance).doc(instance.id).set(instance));
        // return this.increaseProductScrapCount(instance.productId, 1).pipe(
        //     mergeMap(() => {
        //         return this.appwriteDb.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance).doc(instance.id).set(instance)
        //     })
        // )
    }

    // new api function so creating instance update product inventory but set/changing an instance doesnt
    public setScrapInstance(instance: PlasticsScrapInstance): Observable<void> {
        return from(this.appwrite.collection<PlasticsScrapInstance>(Collection.plasticsScrapInstance).doc(instance.id).set(instance))
    }


    public getActiveScrapInstancesValueChanges(): Observable<PlasticsScrapInstance[]> {
        return this.appwrite.collection<{ instances: PlasticsScrapInstance[] }>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.ActivePlasticsScrapInstances)
            .valueChanges()
            .pipe(
                map((resp) => {
                    if (!resp) {
                        return []
                    }
                    return (resp.instances ?? []) as PlasticsScrapInstance[]
                }),
            )
    }


    // ============================
    // Plastics Quality Alert & Inspection
    // ============================
    // CRUD and query operations for quality alerts, inspections, and dock inspections
    // Includes child entities: PlasticsQualityAlertInspection, PlasticsDockInspectionInstance, QualityAlertViewTally

    public loadPlasticsQualityAlertInspections(): Observable<PlasticsQualityAlertInspection[]> {
        return this.appwrite.collection<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
        )
    }

    public loadPlasticsQualityAlertInspectionsByProductVersionId(productVersionId: string): Observable<PlasticsQualityAlertInspection[]> {
        return this.appwrite.collection<PlasticsQualityAlertInspection>(
            Collection.plasticsQualityAlertInspection,
            ref => ref.where('productVersionId', '==', productVersionId)
        ).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
        )
    }

    public loadPlasticsQualityAlertInspectionsByProductVersionIds(productVersionIds: string[]): Observable<PlasticsQualityAlertInspection[]> {
        return this.appwrite.listByFieldValues<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection, 'productVersionId', productVersionIds);
    }

    public loadRecentPlasticsQualityAlertInspectionsByQualityAlertId(alertId: string): Observable<PlasticsQualityAlertInspection[]> {
        const validStatus = [PlasticsQualityInspectionStatus.Passed, PlasticsQualityInspectionStatus.Failed]
        return this.appwrite.collection<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection, ref => ref
            .where('qualityAlertId', '==', alertId)
            .where('status', 'in', validStatus)
            .orderBy('createdOn', 'desc')
            .limit(10)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
        )
    }

    public loadRecentPlasticsDockInspectionInstancesByQualityAlertId(alertId: string): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.list<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, [
            Query.equal('qualityAlertId', alertId),
            Query.orderDesc('updatedOn'),
            Query.limit(10),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public getLastWeekPlasticsQualityAlertInspectionsValueChanges(): Observable<PlasticsQualityAlertInspection[]> {
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)

        return this.appwrite.collection<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection, ref =>
            ref.where('createdOn', '>', oneWeekAgo)
        ).valueChanges().pipe(
            map((resp) => resp),
        )
    }

    public getPlasticsQualityAlertInspectionsFromDateValueChanges(startDate: Date): Observable<PlasticsQualityAlertInspection[]> {
        return from(this.appwrite.collection<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection, ref =>
            ref.where('createdOn', '>=', startDate)
        ).valueChanges()).pipe(
            map((inspections) => inspections)
        )
    }

    public loadPlasticsQualityAlertInspectionsFromDate(startDate: Date): Observable<PlasticsQualityAlertInspection[]> {
        return this.appwrite.list<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection, [
            Query.greaterThanEqual('createdOn', startDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }


    public setPlasticsQualityAlertInspection(qualityAlertInspection: PlasticsQualityAlertInspection) {
        return from(this.appwrite.collection<PlasticsQualityAlertInspection>(Collection.plasticsQualityAlertInspection).doc(qualityAlertInspection.id).set(qualityAlertInspection))
    }

    public setPlasticsQualityAlert(qualityAlert: PlasticsQualityAlert): Observable<void> {
        return from(this.appwrite.collection<PlasticsQualityAlert>(Collection.plasticsQualityAlert).doc(qualityAlert.id).set(qualityAlert));
    }

    public loadPlasticsQualityAlerts(): Observable<PlasticsQualityAlert[]> {
        return this.appwrite.list<PlasticsQualityAlert>(Collection.plasticsQualityAlert, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
            map(reasons => reasons.sort((a, b) => a.title.localeCompare(b.title))),
        );
    }

    public loadPlasticsQualityAlertsById(qualityAlertId: string): Observable<PlasticsQualityAlert> {
        return this.appwrite.get<PlasticsQualityAlert>(Collection.plasticsQualityAlert, qualityAlertId);
    }

    public loadPlasticsQualityAlertsByProductVersionId(productVersionId: string): Observable<PlasticsQualityAlert[]> {
        return this.loadMappingProductVersionToQualityAlertsByProductVersionId(productVersionId).pipe(
            map(mappings => mappings.map(m => m.qualityAlertId)),
            switchMap((qualityAlertIds) => this.loadPlasticsQualityAlertsByIds(qualityAlertIds))
        );
    }

    public loadPlasticsQualityAlertsByProductVersionIds(productVersionIds: string[]): Observable<PlasticsQualityAlert[]> {
        return this.loadMappingProductVersionToQualityAlertsByProductVersionIds(productVersionIds).pipe(
            switchMap(mappings => this.loadPlasticsQualityAlertsByIds(mappings.map(m => m.qualityAlertId))),
        );
    }

    public loadPlasticsQualityAlertsByIds(qualityAlertIds: string[]): Observable<PlasticsQualityAlert[]> {
        return this.appwrite.listByIds<PlasticsQualityAlert>(Collection.plasticsQualityAlert, qualityAlertIds);
    }

    public setQualityAlertViewTally(tally: QualityAlertViewTally): Observable<void> {
        return from(this.appwrite.collection<QualityAlertViewTally>(Collection.qualityAlertViewTally).doc(tally.id).set(tally));
    }

    public getQualityAlertViewFromDateValueChanges(startDate: Date): Observable<QualityAlertViewTally[]> {
        return from(this.appwrite.collection<QualityAlertViewTally>(Collection.qualityAlertViewTally, ref =>
            ref.where('timestamp', '>=', startDate)
        ).valueChanges()).pipe(
            map((tallys) => tallys)
        )
    }

    public loadQualityAlertViewFromDate(startDate: Date): Observable<QualityAlertViewTally[]> {
        return this.appwrite.list<QualityAlertViewTally>(Collection.qualityAlertViewTally, [
            Query.greaterThanEqual('timestamp', startDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public deleteDockInspectionInstancesAndSkidBySkidId(skidId: string): Observable<void> {
        return this.loadDockInspectionInstancesBySkidId(skidId).pipe(
            switchMap(inspections => forkJoin([
                this.deleteSkid(skidId),
                ...inspections.map(inspection => this.appwrite.delete(Collection.dockInspectionInstance, inspection.id)),
            ]).pipe(
                map(() => void 0),
            )),
        );
    }

    public deleteDockInspectionInstances(instanceIds: string[]) {
        if (instanceIds.length === 0) {
            return of([] as void[]);
        }
        return forkJoin(instanceIds.map(id => this.appwrite.delete(Collection.dockInspectionInstance, id)));
    }

    public loadActiveDockInspectionInstancesValueChanges(): Observable<PlasticsDockInspectionInstance[]> {
        const queryParams = [PlasticsDockInspectionInstanceStatus.FAILED, PlasticsDockInspectionInstanceStatus.NOT_STARTED]
        return this.appwrite.collection<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance,
            ref => ref
                .where('status', 'in', queryParams)
        ).valueChanges();
    }

    public loadInactiveDockInspectionInstancesBySkidId(skidId: string): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.list<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, [
            Query.equal('skidId', skidId),
            Query.equal('status', PlasticsDockInspectionInstanceStatus.PASSED),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadDockInspectionQualityAlertsByProductVersionId(productVersionId: string): Observable<PlasticsQualityAlert[]> {
        const qualityAlerts$ = this.loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionId(productVersionId).pipe(
            switchMap(mappings => this.loadPlasticsQualityAlertsByIds(mappings.map(m => m.qualityAlertId)))
        );
        return qualityAlerts$
    }

    public loadDockInspectionQualityAlertsByProductVersionIds(productVersionIds: string[]): Observable<PlasticsQualityAlert[]> {
        return this.loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionIds(productVersionIds).pipe(
            switchMap(mappings => this.loadPlasticsQualityAlertsByIds(mappings.map(m => m.qualityAlertId))),
        );
    }

    public setDockInspectionInstances(inspections: PlasticsDockInspectionInstance[]): Observable<void> {
        if (inspections.length === 0) {
            return of(void 0);
        }
        return forkJoin(inspections.map(inspection => this.setDockInspectionInstance(inspection))).pipe(
            map(() => void 0),
        );
    }
    public setDockInspectionInstance(inspection: PlasticsDockInspectionInstance): Observable<void> {
        return from(this.appwrite.collection<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance).doc(inspection.id).set(inspection));
    }

    public loadDockInspectionInstances(): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.list<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadDockInspectionInstancesBySkidId(skidId: string): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.list<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, [
            Query.equal('skidId', skidId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadDockInspectionInstancesBySkidIds(skidIds: string[]): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.listByFieldValues<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, 'skidId', skidIds);
    }

    public loadDockInspectionInstancesByProductVersionId(productVersionId: string): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.list<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadDockInspectionInstancesByProductVersionIds(productVersionIds: string[]): Observable<PlasticsDockInspectionInstance[]> {
        return this.appwrite.listByFieldValues<PlasticsDockInspectionInstance>(Collection.dockInspectionInstance, 'productVersionId', productVersionIds);
    }

    public loadDockInspectionProductVersionsByQualityAlertId(qualityAlertId: string): Observable<ProductVersion[]> {
        const versions$ = this.loadMappingProductVersionsToDockInspectionQualityAlertsByQualityAlertId(qualityAlertId).pipe(
            switchMap(mappings => this.loadProductVersionsByIds(mappings.map(m => m.productVersionId))),
            map(versions => {
                const productIdToVersion: Record<string, ProductVersion> = {};

                for (const curr of versions) {
                    if (!curr.productId) continue;

                    const existing = productIdToVersion[curr.productId];
                    if (!existing || (curr.versionNumber ?? 0) > (existing.versionNumber ?? 0)) {
                        productIdToVersion[curr.productId] = curr;
                    }
                }

                return Object.values(productIdToVersion);
            })
        )

        return versions$
    }


    // ============================
    // Plastics Fixture
    // ============================
    // CRUD and query operations for fixtures

    public loadFixtures(): Observable<PlasticsFixture[]> {
        return this.appwrite.list<PlasticsFixture>(Collection.plasticsFixture, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public setFixture(fixture: PlasticsFixture): Observable<void> {
        return from(this.appwrite.collection<PlasticsFixture>(Collection.plasticsFixture).doc(fixture.id).set(fixture));
    }

    public loadFixtureById(fixtureId: string): Observable<PlasticsFixture> {
        return this.appwrite.get<PlasticsFixture>(Collection.plasticsFixture, fixtureId);
    }

    public loadFixturesByIds(fixtureIds: string[]): Observable<PlasticsFixture[]> {
        return this.appwrite.listByIds<PlasticsFixture>(Collection.plasticsFixture, fixtureIds);
    }

    public getFixtureCount(): Observable<number> {
        return from(this.appwrite.collection<PlasticsFixture>(Collection.plasticsFixture).get()).pipe(
            map((resp) => {
                return resp.docs.length
            })
        )
    }


    // ============================
    // Roto Machine, Schedule & Arm Tally
    // ============================
    // Operations for rotational molding machines, scheduling, and arm tallies

    public loadRotoSchedule(): Observable<RotoSchedule> {
        return this.appwrite.get<RotoSchedule>(Collection.globalVariables, GlobalVariableDocumentIds.RotoSchedule);
    }

    public setRotoSchedule(rotoSchedule: RotoSchedule): Observable<void> {
        return this.moldCheckups.transition(rotoSchedule, [], { scheduleOnly: true });
    }

    public getRotoScheduleValueChanges(): Observable<RotoSchedule> {
        return this.appwrite.collection<RotoSchedule>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.RotoSchedule)
            .valueChanges()
            .pipe(filter((rotoSchedule): rotoSchedule is RotoSchedule => !!rotoSchedule));
    }

    public loadRotoMachines(): Observable<RotoMachine[]> {
        return this.appwrite.list<RotoMachine>(Collection.rotoMachine, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadRotoMachinesByIds(rotoMachineIds: string[]): Observable<RotoMachine[]> {
        return this.appwrite.listByIds<RotoMachine>(Collection.rotoMachine, rotoMachineIds);
    }

    public loadRotoMachineById(rotoMachineId: string): Observable<RotoMachine> {
        return this.appwrite.get<RotoMachine>(Collection.rotoMachine, rotoMachineId);
    }

    public setRotoMachine(rotoMachine: RotoMachine): Observable<void> {
        return from(this.appwrite.collection<RotoMachine>(Collection.rotoMachine).doc(rotoMachine.id).set(rotoMachine));
    }

    public setRotoMachines(rotoMachines: RotoMachine[]): Observable<void> {
        if (rotoMachines.length === 0) {
            return of(void 0);
        }
        return forkJoin(rotoMachines.map(machine => this.setRotoMachine(machine))).pipe(
            map(() => void 0),
        );
    }

    public setRotoMachineArmTally(tally: RotoMachineArmTally): Observable<void> {
        return from(this.appwrite.collection<RotoMachineArmTally>(Collection.rotoMachineArmTally).doc(tally.id).set(tally));
    }

    public getRotoMachineArmTallyGreaterThanDateValueChanges(
        startDate: Date,
    ) {
        return this.appwrite.collection<RotoMachineArmTally>(
            Collection.rotoMachineArmTally,
            (ref: QueryRef) => ref
                .where('timestamp', '>', startDate)
        ).valueChanges().pipe(
            map(docs => docs),
        )
    }

    public loadRotoMachineArmTalliesAfterDate(startDate: Date): Observable<RotoMachineArmTally[]> {
        return this.appwrite.listAll<RotoMachineArmTally>(Collection.rotoMachineArmTally, [
            Query.greaterThan('timestamp', startDate.toISOString()),
        ]);
    }

    public loadRotoMachineArmTalliesBetweenDates(startDate: Date, endDate: Date): Observable<RotoMachineArmTally[]> {
        return this.appwrite.listAll<RotoMachineArmTally>(Collection.rotoMachineArmTally, [
            Query.greaterThan('timestamp', startDate.toISOString()),
            Query.lessThanEqual('timestamp', endDate.toISOString()),
        ]);
    }

    public loadRotoMachineArmTallys() {
        return this.appwrite.collection<RotoMachineArmTally>(
            Collection.rotoMachineArmTally,
        ).valueChanges().pipe(
            map(docs => docs),
        )
    }


    // ============================
    // Plastics Mold & Mold Family
    // ============================
    // CRUD and query operations for molds and mold families

    public loadMolds(): Observable<PlasticsMold[]> {
        return this.appwrite.collection<PlasticsMold>(Collection.plasticsMold).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }
    public loadMoldsByIds(moldIds: string[]): Observable<PlasticsMold[]> {
        return this.appwrite.listByIds<PlasticsMold>(Collection.plasticsMold, moldIds);
    }

    public loadMoldArmCyclesBetween(startDate: Date, endDate: Date): Observable<MoldArmCycle[]> {
        return this.appwrite.listAll<MoldArmCycle>(Collection.moldArmCycle, [
            Query.greaterThanEqual('completedOn', startDate.toISOString()),
            Query.lessThanEqual('completedOn', endDate.toISOString()),
        ]);
    }

    public loadMoldFamilies(): Observable<PlasticsMoldFamily[]> {
        return this.appwrite.list<PlasticsMoldFamily>(Collection.plasticsMoldFamily, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }
    public loadMoldFamilyById(moldFamilyId: string): Observable<PlasticsMoldFamily> {
        return this.appwrite.get<PlasticsMoldFamily>(Collection.plasticsMoldFamily, moldFamilyId);
    }
    public loadMoldFamiliesByIds(moldFamilyIds: string[]): Observable<PlasticsMoldFamily[]> {
        return this.appwrite.listByIds<PlasticsMoldFamily>(Collection.plasticsMoldFamily, moldFamilyIds);
    }

    public loadMoldsByMoldFamilyId(moldFamilyId: string): Observable<PlasticsMold[]> {
        return this.appwrite.collection<PlasticsMold>(Collection.plasticsMold, ref =>
            ref.where('moldFamilyId', '==', moldFamilyId)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadMoldsByMoldFamilyIds(moldFamilyIds: string[]): Observable<PlasticsMold[]> {
        return this.appwrite.listByFieldValues<PlasticsMold>(
            Collection.plasticsMold,
            'moldFamilyId',
            moldFamilyIds,
        );
    }

    public completeSameProductMoldingTask(
        productListId: string,
        machineId: string,
        armId: string,
        currentTaskId: string,
        nextTaskId: string,
        operatorId: string,
    ): Observable<void> {
        return this.loadRotoSchedule().pipe(
            first(),
            switchMap(rotoSchedule => {
                const productList = rotoSchedule.productLists.find(list => list.id === productListId);
                if (!productList
                    || productList.machineId !== machineId
                    || productList.armId !== armId
                    || productList.moldingTaskIds[0] !== currentTaskId
                    || productList.moldingTaskIds[1] !== nextTaskId) {
                    return throwError(() => new Error('The roto schedule changed. Refresh before continuing to the next task.'));
                }

                return this.loadMoldingTasksByIds([currentTaskId, nextTaskId]).pipe(
                    first(),
                    switchMap(tasks => {
                        const currentTask = tasks.find(task => task.id === currentTaskId);
                        const nextTask = tasks.find(task => task.id === nextTaskId);
                        if (!currentTask || !nextTask) {
                            return throwError(() => new Error('A molding task could not be loaded. Ask a supervisor to review the schedule.'));
                        }

                        const handoff = prepareSameProductMoldingTaskHandoff(
                            rotoSchedule,
                            productListId,
                            machineId,
                            armId,
                            currentTask,
                            nextTask,
                            operatorId,
                            new Date(),
                        );
                        return this.completeMoldingTasksAndAssignMolds(
                            [handoff.completedTask],
                            [handoff.incomingTask],
                            { ...handoff.rotoSchedule, expectedLastSavedOn: rotoSchedule.lastSavedOn },
                        );
                    }),
                );
            }),
        );
    }

    public completeMoldingTasksAndAssignMolds(
        completedMoldingTasks: PlasticsMoldingTask[],
        incomingMoldingTasks: PlasticsMoldingTask[],
        rotoSchedule: RotoSchedule,
        supervisorBadgeId?: string,
    ): Observable<void> {
        return this.moldCheckups.transition(rotoSchedule, [...completedMoldingTasks, ...incomingMoldingTasks],
            { machineChangeover: true, supervisorBadgeId });
    }

    public saveMoldAwareTaskChange(tasks: PlasticsMoldingTask[], schedule: RotoSchedule, scheduleOnly = false): Observable<void> {
        return this.moldCheckups.transition(schedule, tasks, { scheduleOnly });
    }

    public isMoldSerialNumberLinked(serialNumber: number, excludedMoldId?: string): Observable<boolean> {
        return this.appwrite.list<PlasticsMold>(Collection.plasticsMold, [
            Query.equal('serialNumber', serialNumber),
            Query.limit(excludedMoldId ? 2 : 1),
        ]).pipe(
            map(resp => resp.items.some(mold => mold.id !== excludedMoldId)),
        );
    }

    public setPlasticsMoldFamily(moldFamily: PlasticsMoldFamily): Observable<void> {
        return from(this.appwrite.collection<PlasticsMoldFamily>(Collection.plasticsMoldFamily).doc(moldFamily.id).set(moldFamily));
    }

    public setPlasticsMold(mold: PlasticsMold, expectedUpdatedOn?: Date): Observable<void> {
        return this.appwrite.get<PlasticsMold>(Collection.plasticsMold, mold.id).pipe(
            catchError(error => error.code === 404 ? of(null) : throwError(() => error)),
            switchMap(existing => {
                if (!existing) {
                    if (mold.status === 'Needs checkup') return throwError(() => new Error('Needs checkup is assigned when a mold is removed.'));
                    return this.appwrite.set(Collection.plasticsMold, mold.id,
                        { ...mold, checkupOnNextRemoval: !!mold.checkupOnNextRemoval, lastCheckupCycleCount: 0, placement: null, openCheckupId: null });
                }
                return this.moldCheckups.settings().pipe(switchMap(settings => {
                    if (!settings.id) return this.appwrite.set(Collection.plasticsMold, mold.id, { ...existing, ...mold,
                        totalCycleCount: existing.totalCycleCount, checkupOnNextRemoval: !!(existing.checkupOnNextRemoval || mold.checkupOnNextRemoval) });
                    return this.moldCheckups.command({ type: 'edit-mold', moldId: mold.id, expectedUpdatedOn: expectedUpdatedOn ?? existing.updatedOn,
                        status: mold.status, interval: mold.checkupInterval ?? null, checkupOnNextRemoval: mold.checkupOnNextRemoval,
                        metadata: { name: mold.name, customerId: mold.customerId, serialNumber: mold.serialNumber, files: mold.files } }).pipe(map(() => void 0));
                }));
            }),
        );
    }

    public loadPlasticsMoldHistoryEvents(moldId: string): Observable<PlasticsMoldHistoryEvent[]> {
        return this.appwrite.list<PlasticsMoldHistoryEvent>(Collection.plasticsMoldHistoryEvent, [
            Query.equal('moldId', moldId),
            Query.orderDesc('occurredOn'),
            Query.limit(5000),
        ]).pipe(
            map(response => response.items),
        );
    }

    public setPlasticsMoldHistoryEvent(historyEvent: PlasticsMoldHistoryEvent): Observable<void> {
        return from(this.appwrite
            .collection<PlasticsMoldHistoryEvent>(Collection.plasticsMoldHistoryEvent)
            .doc(historyEvent.id)
            .set(historyEvent));
    }


    // ============================
    // Plastics Static Label
    // ============================
    // Operations for static label configurations

    public loadStaticLabels(): Observable<PlasticsStaticLabel[]> {
        return this.appwrite.list<PlasticsStaticLabel>(Collection.plasticsStaticLabel, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadStaticLabelById(id: string): Observable<PlasticsStaticLabel> {
        return this.appwrite.get<PlasticsStaticLabel>(Collection.plasticsStaticLabel, id);
    }

    public setStaticLabel(staticLabel: PlasticsStaticLabel): Observable<void> {
        return from(this.appwrite.collection<PlasticsStaticLabel>(Collection.plasticsStaticLabel).doc(staticLabel.id).set(staticLabel));
    }

    public loadPlasticsStaticLabelByIds(staticLabelIds: string[]): Observable<PlasticsStaticLabel[]> {
        return this.appwrite.listByIds<PlasticsStaticLabel>(Collection.plasticsStaticLabel, staticLabelIds);
    }


    // ============================
    // Plastics Calendar Event
    // ============================
    // CRUD and query operations for calendar events (maintenance, etc.)

    public setCalendarEvent(calendarEvent: PlasticsCalendarEvent): Observable<void> {
        return from(this.appwrite.collection<PlasticsCalendarEvent>(Collection.plasticsCalendarEvent).doc(calendarEvent.id).set(calendarEvent));
    }
    public setCalendarEvents(calendarEvents: PlasticsCalendarEvent[]): Observable<void> {
        if (calendarEvents.length === 0) {
            return of(void 0);
        }
        return forkJoin(calendarEvents.map(calendarEvent => this.setCalendarEvent(calendarEvent))).pipe(
            map(() => void 0),
        );
    }

    public loadCalendarEvents(): Observable<PlasticsCalendarEvent[]> {
        return this.appwrite.list<PlasticsCalendarEvent>(Collection.plasticsCalendarEvent, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }
    public deleteCalendarEventsByIds(calendarEventIds: string[]): Observable<void> {
        if (calendarEventIds.length === 0) {
            return of(void 0);
        }
        return forkJoin(calendarEventIds.map(id => this.appwrite.delete(Collection.plasticsCalendarEvent, id))).pipe(
            map(() => void 0),
        );
    }

    public loadCalendarEventsBetween(startDate: Date, endDate: Date): Observable<PlasticsCalendarEvent[]> {
        return this.appwrite.list<PlasticsCalendarEvent>(Collection.plasticsCalendarEvent, [
            Query.greaterThanEqual('eventDate', startDate.toISOString()),
            Query.lessThanEqual('eventDate', endDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }
    public loadCalendarEventsAfter(startDate: Date): Observable<PlasticsCalendarEvent[]> {
        return this.appwrite.list<PlasticsCalendarEvent>(Collection.plasticsCalendarEvent, [
            Query.greaterThanEqual('eventDate', startDate.toISOString()),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public deleteCalendarEvent(calendarEvent: PlasticsCalendarEvent): Observable<void> {
        return this.appwrite.delete(Collection.plasticsCalendarEvent, calendarEvent.id);
    }

    public getCalendarEventsValueChanges(): Observable<PlasticsCalendarEvent[]> {
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        const sevenDaysFromNow = new Date()
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
        return this.appwrite.collection<PlasticsCalendarEvent>(
            Collection.plasticsCalendarEvent,
            ref => ref.where('eventDate', '>=', now).where('eventDate', '<=', sevenDaysFromNow)
        ).valueChanges();
    }

    public getMaintenanceCalendarEventsValueChanges(): Observable<PlasticsCalendarEvent[]> {
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        const sevenDaysFromNow = new Date()
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7)
        return this.appwrite.collection<PlasticsCalendarEvent>(
            Collection.plasticsCalendarEvent,
            ref => ref.where('eventDate', '>=', now).where('eventDate', '<=', sevenDaysFromNow).where('type', '==', PlasticsCalendarEventType.Maintenance)
        ).valueChanges();
    }


    // ============================
    // Complaint Log
    // ============================
    // CRUD and query operations for customer complaint logs
    // Includes child entity: ComplaintLogProduct

    public loadComplaintLogs(): Observable<ComplaintLog[]> {
        return from(this.appwrite.collection<ComplaintLog>(Collection.complaintLog).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        ))
    }

    public loadUnresolvedComplaintLogs(): Observable<ComplaintLog[]> {
        return this.appwrite.listAll<ComplaintLog>(
            Collection.complaintLog,
            [Query.equal('status', ComplaintLogStatus.Unresolved)],
        );
    }

    public loadComplaintLogById(logId: string): Observable<ComplaintLog> {
        return this.appwrite.get<ComplaintLog>(Collection.complaintLog, logId);
    }

    public loadComplaintLogsByIds(logIds: string[]): Observable<ComplaintLog[]> {
        return this.appwrite.listByIds<ComplaintLog>(Collection.complaintLog, logIds);
    }

    public setComplaintLog(log: ComplaintLog): Observable<void> {
        return from(this.appwrite.collection<ComplaintLog>(Collection.complaintLog).doc(log.id).set(log))
    }

    public setComplaintLogWithProducts(
        complaintLog: ComplaintLog,
        complaintLogProducts: ComplaintLogProduct[],
    ): Observable<void> {
        const groups: BatchSetEntityGroups = {
            [Collection.complaintLog]: [complaintLog],
            [Collection.complaintLogProduct]: complaintLogProducts,
        };

        return this.appwrite.batchSetAcrossCollectionsTransactional(groups);
    }

    public loadComplaintLogProductsByComplaintLogId(logId: string): Observable<ComplaintLogProduct[]> {
        return this.appwrite.list<ComplaintLogProduct>(Collection.complaintLogProduct, [
            Query.equal('complaintLogId', logId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadComplaintLogProductsByComplaintLogIds(logIds: string[]): Observable<ComplaintLogProduct[]> {
        return this.appwrite.listByFieldValues<ComplaintLogProduct>(
            Collection.complaintLogProduct,
            'complaintLogId',
            logIds,
            [],
            PlasticsApiService.ID_BATCH_SIZE,
            batch => batch.length * 50,
        );
    }

    public setComplaintLogProduct(complaintLogProduct: ComplaintLogProduct): Observable<void> {
        return from(this.appwrite.collection<ComplaintLogProduct>(Collection.complaintLogProduct).doc(complaintLogProduct.id).set(complaintLogProduct));
    }

    public deleteComplaintLog(complaintLog: ComplaintLog): Observable<void> {
        return this.appwrite.delete(Collection.complaintLog, complaintLog.id).pipe(
            switchMap(() => this.loadComplaintLogProductsByComplaintLogId(complaintLog.id)),
            map((complaintLogProducts) => {
                forkJoin(complaintLogProducts.map(c => this.deleteComplaintLogProduct(c))).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public deleteComplaintLogProduct(complaintLogProduct: ComplaintLogProduct): Observable<void> {
        return from(this.appwrite.collection<ComplaintLogProduct>(Collection.complaintLogProduct).doc(complaintLogProduct.id).delete())
    }

    public loadHotListItems(): Observable<HotListItem[]> {
        return this.appwrite.listAll<HotListItem>(Collection.hotListItem);
    }

    public setHotListItem(item: HotListItem): Observable<void> {
        return from(this.appwrite.collection<HotListItem>(Collection.hotListItem).doc(item.id).set(item));
    }

    public deleteHotListItem(item: HotListItem): Observable<void> {
        return this.appwrite.delete(Collection.hotListItem, item.id);
    }

    public loadHotListOrderItems(): Observable<HotListOrderItem[]> {
        return this.appwrite.listAll<HotListOrderItem>(Collection.hotListOrderItem);
    }

    public getActiveHotListOrderItemValueChanges(): Observable<HotListOrderItem[]> {
        return this.appwrite.collection<HotListOrderItem>(
            Collection.hotListOrderItem,
            ref => ref
                .where('resolvedOn', '==', null)
                .limit(5000)
        ).valueChanges().pipe(
            map((items) => items.filter((item) => !!item.releasedOn)),
        );
    }

    public setHotListOrderItem(item: HotListOrderItem): Observable<void> {
        return from(this.appwrite.collection<HotListOrderItem>(Collection.hotListOrderItem).doc(item.id).set(item));
    }

    public deleteHotListOrderItem(item: HotListOrderItem): Observable<void> {
        return this.appwrite.delete(Collection.hotListOrderItem, item.id);
    }

    public loadMoldingTasksByHotListOrderItemIds(hotListOrderItemIds: string[]): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.listByFieldValues<PlasticsMoldingTask>(Collection.plasticsMoldingTask, 'hotListOrderItemId', hotListOrderItemIds);
    }

    public loadFinishingTasksByHotListOrderItemIds(hotListOrderItemIds: string[]): Observable<PlasticsFinishingTask[]> {
        return this.appwrite.listByFieldValues<PlasticsFinishingTask>(Collection.plasticsFinishingTask, 'hotListOrderItemId', hotListOrderItemIds);
    }

    public loadComplaintLogsByProductId(productId: string): Observable<ComplaintLog[]> {
        return this.appwrite.list<ComplaintLogProduct>(Collection.complaintLogProduct, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            switchMap((complaintLogProducts: ComplaintLogProduct[]) => {
                return this.loadComplaintLogsByIds(complaintLogProducts.map(complaintLogProduct => complaintLogProduct.complaintLogId));
            })
        );
    }

    public loadComplaintLogsByCustomerId(customerId: string): Observable<ComplaintLog[]> {
        return from(this.appwrite.collection<ComplaintLog>(Collection.complaintLog,
            ref => ref.where('customerId', '==', customerId)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        ))
    }

    public loadComplaintLogProducts(): Observable<ComplaintLogProduct[]> {
        return this.appwrite.collection<ComplaintLogProduct>(Collection.complaintLogProduct).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadComplaintLogProductsByProductId(productId: string): Observable<ComplaintLogProduct[]> {
        return from(this.appwrite.collection<ComplaintLogProduct>(
            Collection.complaintLogProduct,
            ref => ref.where('productId', '==', productId)
        ).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        ))
    }

    // ============================
    // Plastics Rework
    // ============================

    public loadPlasticsReworks(): Observable<PlasticsRework[]> {
        return this.appwrite.list<PlasticsRework>(Collection.plasticsRework, [
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadPlasticsReworkById(reworkId: string): Observable<PlasticsRework> {
        return this.appwrite.get<PlasticsRework>(Collection.plasticsRework, reworkId);
    }

    public loadPlasticsReworksByIds(reworkIds: string[]): Observable<PlasticsRework[]> {
        if (reworkIds.length === 0) return of([]);
        return this.appwrite.listByIds<PlasticsRework>(Collection.plasticsRework, reworkIds);
    }

    public loadPlasticsReworksByProductId(productId: string): Observable<PlasticsRework[]> {
        return this.appwrite.list<PlasticsRework>(Collection.plasticsRework, [
            Query.equal('productId', productId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadPlasticsReworksByComplaintLogId(complaintLogId: string): Observable<PlasticsRework[]> {
        return this.appwrite.list<PlasticsRework>(Collection.plasticsRework, [
            Query.equal('complaintLogId', complaintLogId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public setPlasticsRework(rework: PlasticsRework): Observable<void> {
        return from(this.appwrite.collection<PlasticsRework>(Collection.plasticsRework).doc(rework.id).set(rework));
    }

    public deletePlasticsRework(reworkId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsRework, reworkId);
    }


    // ============================
    // Contact & Tag
    // ============================
    // CRUD operations for contacts and tags

    // Originals preserved as a /* */ block.
    public loadContacts(): Observable<Contact[]> {
        return this.appwrite.list<Contact>(Collection.contact, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadContactById(contactId: string): Observable<Contact> {
        return this.appwrite.get<Contact>(Collection.contact, contactId);
    }

    public setContact(contact: Contact): Observable<void> {
        return from(this.appwrite.collection<Contact>(Collection.contact).doc(contact.id).set(contact));
    }


    public loadTags(): Observable<Tag[]> {
        return this.appwrite.list<Tag>(Collection.tag, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }


    // ============================
    // Mapping Entities
    // ============================
    // Join/mapping tables between entities (ProductVersion↔QualityAlert, ComplaintLog↔QualityAlert, etc.)

    public loadMappingProductVersionToQualityAlertsByProductVersionId(productVersionId: string): Observable<MappingProductVersionToQualityAlert[]> {
        return this.appwrite.list<MappingProductVersionToQualityAlert>(Collection.mappingProductVersionToQualityAlert, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMappingProductVersionToQualityAlertsByProductVersionIds(productVersionIds: string[]): Observable<MappingProductVersionToQualityAlert[]> {
        return this.appwrite.listByFieldValues<MappingProductVersionToQualityAlert>(Collection.mappingProductVersionToQualityAlert, 'productVersionId', productVersionIds);
    }

    public loadMappingProductVersionToQualityAlertsByQualityAlertId(qualityAlertId: string): Observable<MappingProductVersionToQualityAlert[]> {
        return this.appwrite.list<MappingProductVersionToQualityAlert>(Collection.mappingProductVersionToQualityAlert, [
            Query.equal('qualityAlertId', qualityAlertId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMappingComplaintLogToQualityAlertsByQualityAlertId(qualityAlertId: string): Observable<MappingComplaintLogToQualityAlert[]> {
        return from(this.appwrite.collection<MappingComplaintLogToQualityAlert>(
            Collection.mappingComplaintLogToQualityAlert,
            ref => ref
                .where('qualityAlertId', '==', qualityAlertId)
        ).get()).pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadMappingComplaintLogToQualityAlertsByComplaintLogId(complaintLogId: string): Observable<MappingComplaintLogToQualityAlert[]> {
        return from(this.appwrite.collection<MappingComplaintLogToQualityAlert>(
            Collection.mappingComplaintLogToQualityAlert,
            ref => ref
                .where('complaintLogId', '==', complaintLogId)
        ).get()).pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public getMappingComplaintLogToQualityAlertByParameterIds(qualityAlertId: string, complaintLogId: string): Observable<MappingComplaintLogToQualityAlert[]> {
        return from(
            this.appwrite.collection<MappingComplaintLogToQualityAlert>(
                Collection.mappingComplaintLogToQualityAlert,
                ref => ref
                    .where('complaintLogId', '==', complaintLogId)
                    .where('qualityAlertId', '==', qualityAlertId)
            ).get()
        ).pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public deleteMappingComplaintLogToQualityAlerts(qualityAlertIds: string[], complaintLogId: string): Observable<void[]> {
        const mappings$: Observable<MappingComplaintLogToQualityAlert[]>[] = qualityAlertIds.map(id =>
            this.getMappingComplaintLogToQualityAlertByParameterIds(id, complaintLogId)
        );

        return forkJoin(mappings$).pipe(
            defaultIfEmpty([] as MappingComplaintLogToQualityAlert[][]),
            switchMap((mappings) => {
                const deletes$ = mappings.flat().map(mapping => {
                    return from(this.appwrite.collection(Collection.mappingComplaintLogToQualityAlert).doc(mapping.id).delete())
                })

                return forkJoin(deletes$).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public getMappingComplaintLogToContactByParameterIds(contactId: string, complaintLogId: string): Observable<MappingComplaintLogToContact[]> {
        return from(
            this.appwrite.collection<MappingComplaintLogToContact>(
                Collection.mappingComplaintLogToContact,
                ref => ref
                    .where('complaintLogId', '==', complaintLogId)
                    .where('contactId', '==', contactId)
            ).get()
        ).pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public deleteMappingsComplaintLogToContacts(contactIds: string[], complaintLogId: string): Observable<void[]> {
        const mappings$: Observable<MappingComplaintLogToContact[]>[] = contactIds.map(id =>
            this.getMappingComplaintLogToContactByParameterIds(id, complaintLogId)
        );

        return forkJoin(mappings$).pipe(
            defaultIfEmpty([] as MappingComplaintLogToContact[][]),
            switchMap((mappings) => {
                const deletes$ = mappings.flat().map(mapping => {
                    return from(this.appwrite.collection(Collection.mappingComplaintLogToContact).doc(mapping.id).delete())
                })

                return forkJoin(deletes$).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public setMappingComplaintLogToQualityAlerts(mappings: MappingComplaintLogToQualityAlert[]): Observable<void> {
        return forkJoin(mappings.map(mapping => from(this.appwrite.collection<MappingComplaintLogToQualityAlert>(Collection.mappingComplaintLogToQualityAlert).doc(mapping.id).set(mapping)))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public loadMappingComplaintLogToContactsByComplaintLogId(complaintLogId: string): Observable<MappingComplaintLogToContact[]> {
        return from(this.appwrite.collection<MappingComplaintLogToContact>(
            Collection.mappingComplaintLogToContact,
            ref => ref
                .where('complaintLogId', '==', complaintLogId)
        ).get()).pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadMappingComplaintLogToContactsByContactId(contactId: string): Observable<MappingComplaintLogToContact[]> {
        return from(this.appwrite.collection<MappingComplaintLogToContact>(
            Collection.mappingComplaintLogToContact,
            ref => ref
                .where('contactId', '==', contactId)
        ).get()).pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public setMappingComplaintLogToContact(mapping: MappingComplaintLogToContact): Observable<void> {
        return from(this.appwrite.collection<MappingComplaintLogToContact>(Collection.mappingComplaintLogToContact).doc(mapping.id).set(mapping))
    }

    public setMappingsComplaintLogToContact(mappings: MappingComplaintLogToContact[]): Observable<void> {
        return forkJoin(mappings.map(mapping => this.setMappingComplaintLogToContact(mapping))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        );
    }

    public deleteMappingComplaintLogToContact(mapping: MappingComplaintLogToContact): Observable<void> {
        return from(this.appwrite.collection<MappingComplaintLogToContact>(Collection.mappingComplaintLogToContact).doc(mapping.id).delete())
    }

    public loadMappingProductVersionToQualityAlerts(): Observable<MappingProductVersionToQualityAlert[]> {
        return this.appwrite.list<MappingProductVersionToQualityAlert>(Collection.mappingProductVersionToQualityAlert, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public setMappingProductVersionToQualityAlerts(mappings: MappingProductVersionToQualityAlert[]): Observable<void[]> {
        return forkJoin(mappings.map(m => from(this.appwrite.collection<MappingProductVersionToQualityAlert>(Collection.mappingProductVersionToQualityAlert).doc(m.id).set(m)))).pipe(
            defaultIfEmpty([] as void[]),
        );
    }

    public deleteAllMappingProductVersionToQualityAlertsByProductVersionId(productVersionId: string): Observable<void[]> {
        return this.loadMappingProductVersionToQualityAlertsByProductVersionId(productVersionId).pipe(
            mergeMap((mappings) => {
                return forkJoin(mappings.map(m => this.appwrite.delete(Collection.mappingProductVersionToQualityAlert, m.id))).pipe(
                    defaultIfEmpty([] as void[]),
                );
            })
        )
    }

    public loadMappingProductVersionsToDockInspectionQualityAlerts(): Observable<MappingProductVersionToDockInspectionQuailtyAlert[]> {
        return this.appwrite.list<MappingProductVersionToDockInspectionQuailtyAlert>(Collection.mappingProductVersionToDockInspectionQualityAlert, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }
    public loadMappingProductVersionsToDockInspectionQualityAlertsByQualityAlertId(qualityAlertId: string): Observable<MappingProductVersionToDockInspectionQuailtyAlert[]> {
        return this.appwrite.list<MappingProductVersionToDockInspectionQuailtyAlert>(Collection.mappingProductVersionToDockInspectionQualityAlert, [
            Query.equal('qualityAlertId', qualityAlertId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public setMappingProductVersionToDockInspectionQualityAlerts(mapping: MappingProductVersionToDockInspectionQuailtyAlert): Observable<void> {
        return from(this.appwrite.collection<MappingProductVersionToDockInspectionQuailtyAlert>(Collection.mappingProductVersionToDockInspectionQualityAlert).doc(mapping.id).set(mapping));
    }

    public loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionId(productVersionId: string): Observable<MappingProductVersionToDockInspectionQuailtyAlert[]> {
        return this.appwrite.list<MappingProductVersionToDockInspectionQuailtyAlert>(Collection.mappingProductVersionToDockInspectionQualityAlert, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionIds(productVersionIds: string[]): Observable<MappingProductVersionToDockInspectionQuailtyAlert[]> {
        return this.appwrite.listByFieldValues<MappingProductVersionToDockInspectionQuailtyAlert>(Collection.mappingProductVersionToDockInspectionQualityAlert, 'productVersionId', productVersionIds);
    }

    public deleteAllMappingProductVersionToDockInspectionQualityAlertsByProductVersionId(productVersionId: string): Observable<void[]> {
        return this.loadMappingProductVersionToDockInspectionsQualityAlertsByProductVersionId(productVersionId).pipe(
            mergeMap((mappings) => {
                return forkJoin(mappings.map(m => this.appwrite.delete(Collection.mappingProductVersionToDockInspectionQualityAlert, m.id))).pipe(
                    defaultIfEmpty([] as void[]),
                );
            })
        )
    }

    public setMappingProductVersionToDockInspectionsQualityAlerts(mappings: MappingProductVersionToDockInspectionQuailtyAlert[]): Observable<void[]> {
        return forkJoin(mappings.map(m => from(this.appwrite.collection<MappingProductVersionToDockInspectionQuailtyAlert>(Collection.mappingProductVersionToDockInspectionQualityAlert).doc(m.id).set(m)))).pipe(
            defaultIfEmpty([] as void[]),
        );
    }

    public loadMappingProductVersionToPlasticsStaticLabelsByProductVersionId(productVersionId: string): Observable<MappingProductVersionToPlasticsStaticLabel[]> {
        return this.appwrite.list<MappingProductVersionToPlasticsStaticLabel>(Collection.mappingProductVersionToPlasticsStaticLabel, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    /** the Quote Studio cost models that cost a product version */
    public loadMappingProductVersionToQuoteStudioCostModelsByProductVersionId(productVersionId: string): Observable<MappingProductVersionToQuoteStudioCostModel[]> {
        return this.appwrite.list<MappingProductVersionToQuoteStudioCostModel>(Collection.mappingProductVersionToQuoteStudioCostModel, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    /** the released Quote Studio quotes that priced a product version */
    public loadMappingProductVersionToQuoteStudioQuotesByProductVersionId(productVersionId: string): Observable<MappingProductVersionToQuoteStudioQuote[]> {
        return this.appwrite.list<MappingProductVersionToQuoteStudioQuote>(Collection.mappingProductVersionToQuoteStudioQuote, [
            Query.equal('productVersionId', productVersionId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    /** every product version's cost model links - the margin report costs all open orders at once */
    public loadAllMappingProductVersionToQuoteStudioCostModels(): Observable<MappingProductVersionToQuoteStudioCostModel[]> {
        return this.appwrite.listAll<MappingProductVersionToQuoteStudioCostModel>(Collection.mappingProductVersionToQuoteStudioCostModel);
    }

    /** every product version's quote links - the margin report prices all open orders at once */
    public loadAllMappingProductVersionToQuoteStudioQuotes(): Observable<MappingProductVersionToQuoteStudioQuote[]> {
        return this.appwrite.listAll<MappingProductVersionToQuoteStudioQuote>(Collection.mappingProductVersionToQuoteStudioQuote);
    }

    public loadMappingProductVersionToPlasticsStaticLabelsByProductVersionIds(productVersionIds: string[]): Observable<MappingProductVersionToPlasticsStaticLabel[]> {
        return this.appwrite.listByFieldValues<MappingProductVersionToPlasticsStaticLabel>(Collection.mappingProductVersionToPlasticsStaticLabel, 'productVersionId', productVersionIds);
    }

    public loadMappingProductVersionToPlasticsStaticLabelsByStaticLabelId(staticLabelId: string): Observable<MappingProductVersionToPlasticsStaticLabel[]> {
        return this.appwrite.list<MappingProductVersionToPlasticsStaticLabel>(Collection.mappingProductVersionToPlasticsStaticLabel, [
            Query.equal('staticLabelId', staticLabelId),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public setMappingProductVersionToPlasticsStaticLabels(mappings: MappingProductVersionToPlasticsStaticLabel[]): Observable<void[]> {
        return forkJoin(mappings.map(m => from(this.appwrite.collection<MappingProductVersionToPlasticsStaticLabel>(Collection.mappingProductVersionToPlasticsStaticLabel).doc(m.id).set(m)))).pipe(
            defaultIfEmpty([] as void[]),
        );
    }

    public loadMappingMaterialToQualityAlerts(): Observable<MappingPlasticsMaterialToQualityAlert[]> {
        return this.appwrite.collection<MappingPlasticsMaterialToQualityAlert>(Collection.mappingPlasticsMaterialToQualityAlert).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadMappingMaterialToQualityAlertsByMaterialId(materialId: string): Observable<MappingPlasticsMaterialToQualityAlert[]> {
        return this.appwrite.collection<MappingPlasticsMaterialToQualityAlert>(Collection.mappingPlasticsMaterialToQualityAlert, ref =>
            ref.where('materialId', '==', materialId))
            .get().pipe(
                map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
            )
    }

    public loadMappingMaterialToQualityAlertsByMaterialIds(materialIds: string[]): Observable<MappingPlasticsMaterialToQualityAlert[]> {
        const ids = [...new Set(materialIds.filter(id => !!id))];
        if (ids.length === 0) return of([]);

        return this.appwrite.listByFieldValues<MappingPlasticsMaterialToQualityAlert>(
            Collection.mappingPlasticsMaterialToQualityAlert,
            'materialId',
            ids,
        );
    }

    public loadMappingMaterialToQualityAlertsByQualityAlertId(qualityAlertId: string): Observable<MappingPlasticsMaterialToQualityAlert[]> {
        return this.appwrite.collection<MappingPlasticsMaterialToQualityAlert>(Collection.mappingPlasticsMaterialToQualityAlert, ref =>
            ref.where('qualityAlertId', '==', qualityAlertId)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public replaceMappingMaterialToQualityAlerts(materialId: string, newQualityAlertIds: string[]): Observable<void> {
        return this.loadMappingMaterialToQualityAlertsByMaterialId(materialId).pipe(
            switchMap(existingMappings => {
                const deletes$ = existingMappings.map(mapping =>
                    this.appwrite.delete(Collection.mappingPlasticsMaterialToQualityAlert, mapping.id)
                );
                const sets$ = newQualityAlertIds.map(qualityAlertId => {
                    const newMapping: MappingPlasticsMaterialToQualityAlert = {
                        id: this.appwrite.createId(),
                        qualityAlertId,
                        materialId,
                    };
                    return from(this.appwrite.collection<MappingPlasticsMaterialToQualityAlert>(Collection.mappingPlasticsMaterialToQualityAlert).doc(newMapping.id).set(newMapping));
                });
                return forkJoin([...deletes$, ...sets$]).pipe(
                    map(() => void 0),
                    defaultIfEmpty(void 0),
                );
            })
        );
    }

    public loadPlasticsMaterialsByQualityAlertId(qualityAlertId: string): Observable<PlasticsMaterial[]> {
        return this.loadMappingMaterialToQualityAlertsByQualityAlertId(qualityAlertId).pipe(
            switchMap(mappings => this.loadMaterialsByIds(mappings.map(m => m.materialId))),
        )
    }


    // ============================
    // Suncast
    // ============================
    // Operations for Suncast-specific data and plastics hardware item shipments

    public loadSuncastData(): Observable<SuncastData> {
        return this.appwrite.collection<SuncastData>(Collection.globalVariables).doc(GlobalVariableDocumentIds.SuncastData).get().pipe(
            map(resp => {
                const data = resp.data()
                if (!resp.exists || !data) {
                    return DEFAULT_SUNCAST_DATA
                }

                return data
            })
        )
    }

    public setSuncastData(suncastData: SuncastData): Observable<void> {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.SuncastData).set(suncastData));
    }

    public loadClackData(): Observable<ClackData> {
        const defaultData = (): ClackData => ({
            skuConfigs: DEFAULT_CLACK_DATA.skuConfigs.map(config => ({ ...config })),
        });

        return this.appwrite.collection<ClackData>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ClackData).get().pipe(
            map(resp => {
                const data = resp.data();
                if (!resp.exists || !data) {
                    return defaultData();
                }

                const skuConfigs = Array.isArray(data.skuConfigs)
                    ? data.skuConfigs
                        .map(config => ({
                            sku: String(config?.sku ?? '').trim().toUpperCase(),
                            minimumQuantity: Number.isFinite(config?.minimumQuantity) ? Number(config.minimumQuantity) : null,
                            maximumQuantity: Number.isFinite(config?.maximumQuantity) ? Number(config.maximumQuantity) : null,
                            minimumTaskQuantity: Number.isFinite(config?.minimumTaskQuantity) ? Number(config.minimumTaskQuantity) : null,
                        }))
                        .filter(config => !!config.sku)
                    : defaultData().skuConfigs;

                return { skuConfigs };
            }),
            catchError(error => {
                if (!this.isAppwriteNotFound(error)) {
                    return throwError(() => error);
                }
                return of(defaultData());
            }),
        );
    }

    public setClackData(clackData: ClackData): Observable<void> {
        return from(this.appwrite.collection<ClackData>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ClackData).set(clackData));
    }

    public loadKolorcansData(): Observable<KolorcansData> {
        return this.appwrite.collection<KolorcansData>(Collection.globalVariables).doc(GlobalVariableDocumentIds.KolorcansData).get().pipe(
            map(resp => {
                const data: KolorcansData = resp.data() ?? {} as KolorcansData;
                const predefinedBoxes = (data?.predefinedBoxes ?? []).filter(box =>
                    !!box.id
                    && Number.isFinite(box.l)
                    && Number.isFinite(box.w)
                    && Number.isFinite(box.h)
                    && box.l > 0
                    && box.w > 0
                    && box.h > 0);
                return {
                    ...DEFAULT_KOLORCANS_DATA,
                    ...data,
                    predefinedBoxes: predefinedBoxes.length ? predefinedBoxes : DEFAULT_KOLORCANS_DATA.predefinedBoxes,
                    boxAliases: data?.boxAliases?.length ? data.boxAliases : DEFAULT_KOLORCANS_DATA.boxAliases,
                    componentClassifiers: data?.componentClassifiers?.length
                        ? data.componentClassifiers
                        : DEFAULT_KOLORCANS_DATA.componentClassifiers,
                    assemblyRules: data?.assemblyRules?.length ? data.assemblyRules : DEFAULT_KOLORCANS_DATA.assemblyRules,
                    palletPackingRules: data?.palletPackingRules?.length
                        ? data.palletPackingRules
                        : DEFAULT_KOLORCANS_DATA.palletPackingRules,
                };
            }),
            catchError((error) => {
                if (!this.isAppwriteNotFound(error)) {
                    return throwError(() => error);
                }
                return of(DEFAULT_KOLORCANS_DATA);
            }),
        );
    }

    public setKolorcansData(kolorcansData: KolorcansData): Observable<void> {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.KolorcansData).set(kolorcansData));
    }

    public getKolorcansPackingConfigsValueChanges(): Observable<KolorcansPackingConfig[]> {
        return this.appwrite.collection<KolorcansPackingConfig>(
            Collection.kolorcansPackingConfig,
            ref => ref.limit(5000),
        ).valueChanges();
    }

    public loadKolorcansPackingConfigsByOrderedProductIds(orderedProductIds: string[]): Observable<KolorcansPackingConfig[]> {
        return this.appwrite.listByIds<KolorcansPackingConfigProduct>(
            Collection.kolorcansPackingConfigProduct,
            orderedProductIds,
        ).pipe(
            switchMap(configProducts => this.appwrite.listByIds<KolorcansPackingConfig>(
                Collection.kolorcansPackingConfig,
                configProducts.map(configProduct => configProduct.packingConfigId),
            )),
        );
    }

    public createKolorcansPackingConfig(config: KolorcansPackingConfig): Observable<void> {
        const configProducts: KolorcansPackingConfigProduct[] = Array.from(new Set(config.orderedProductIds))
            .map(orderedProductId => ({
                id: orderedProductId,
                packingConfigId: config.id,
            }));
        const createdConfigProductIds: string[] = [];

        return from(configProducts).pipe(
            concatMap(configProduct => this.appwrite.create(
                Collection.kolorcansPackingConfigProduct,
                configProduct,
            ).pipe(
                tap(() => createdConfigProductIds.push(configProduct.id)),
            )),
            toArray(),
            switchMap(() => this.appwrite.create(Collection.kolorcansPackingConfig, config)),
            catchError(error => this.rollbackKolorcansPackingConfigCreate(
                config.id,
                createdConfigProductIds,
                error,
            )),
        );
    }

    public deleteKolorcansPackingConfig(config: KolorcansPackingConfig): Observable<void> {
        return from(Array.from(new Set(config.orderedProductIds))).pipe(
            concatMap(orderedProductId => this.deleteAppwriteRowIfExists(
                Collection.kolorcansPackingConfigProduct,
                orderedProductId,
            )),
            toArray(),
            switchMap(() => this.deleteAppwriteRowIfExists(Collection.kolorcansPackingConfig, config.id)),
        );
    }

    private rollbackKolorcansPackingConfigCreate(
        configId: string,
        configProductIds: string[],
        originalError: unknown,
    ): Observable<never> {
        const cleanupRequests = [
            this.deleteAppwriteRowIfExists(Collection.kolorcansPackingConfig, configId),
            ...configProductIds.map(orderedProductId => this.deleteAppwriteRowIfExists(
                Collection.kolorcansPackingConfigProduct,
                orderedProductId,
            )),
        ];
        return forkJoin(cleanupRequests).pipe(
            catchError(cleanupError => {
                console.error('Unable to fully roll back KOLORCANS packing config creation.', cleanupError);
                return of([]);
            }),
            switchMap(() => throwError(() => originalError)),
        );
    }

    private deleteAppwriteRowIfExists(collection: Collection, id: string): Observable<void> {
        return this.appwrite.delete(collection, id).pipe(
            catchError(error => this.isAppwriteNotFound(error)
                ? of(void 0)
                : throwError(() => error)),
        );
    }

    public loadMostRecentSuncastSnapshot(): Observable<SuncastSnapshot | null> {
        return this.appwrite.collection<SuncastSnapshot>(Collection.suncastSnapshot, ref => ref
            .where('status', '==', SuncastSnapshotStatus.Complete)
            .orderBy('capturedOn', 'desc')
            .limit(1)
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs)[0] || null)
        )
    }



    // ============================
    // Plastics Hardware Item Shipments
    // ============================

    public loadPlasticsHardwareItemShipments(): Observable<PlasticsHardwareItemShipment[]> {
        return this.appwrite.collection<PlasticsHardwareItemShipment>(Collection.plasticsHardwareItemShipment).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public setPlasticsHardwareItemShipment(shipment: PlasticsHardwareItemShipment): Observable<void> {
        return from(this.appwrite.collection<PlasticsHardwareItemShipment>(Collection.plasticsHardwareItemShipment).doc(shipment.id).set(shipment));
    }

    public loadPlasticsHardwareItemShipmentById(shipmentId: string): Observable<PlasticsHardwareItemShipment> {
        return this.appwrite.collection<PlasticsHardwareItemShipment>(Collection.plasticsHardwareItemShipment).doc(shipmentId).get().pipe(
            map(resp => fromAppwriteDoc(resp))
        )
    }

    public deletePlasticsHardwareItemShipmentById(shipmentId: string): Observable<void> {
        return from(this.appwrite.collection<PlasticsHardwareItemShipment>(Collection.plasticsHardwareItemShipment).doc(shipmentId).delete())
    }

    public loadPlasticsHardwareItemShipmentItemsByShipmentId(shipmentId: string): Observable<PlasticsHardwareItemShipmentItem[]> {
        return this.appwrite.collection<PlasticsHardwareItemShipmentItem>(Collection.plasticsHardwareItemShipmentItem, ref => ref
            .where('plasticsHardwareItemShipmentId', '==', shipmentId)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadPlasticsHardwareItemShipmentDamageItemsByShipmentId(shipmentId: string): Observable<PlasticsHardwareItemShipmentDamageItem[]> {
        return this.appwrite.collection<PlasticsHardwareItemShipmentDamageItem>(Collection.plasticsHardwareItemShipmentDamageItem, ref => ref
            .where('plasticsHardwareItemShipmentId', '==', shipmentId)
        ).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public deletePlasticsHardwareItemShipmentDamageItemsByShipmentId(shipmentId: string): Observable<void[]> {
        return this.loadPlasticsHardwareItemShipmentDamageItemsByShipmentId(shipmentId).pipe(
            switchMap(items => items.length
                ? forkJoin(items.map(item => this.appwrite.collection<PlasticsHardwareItemShipmentDamageItem>(Collection.plasticsHardwareItemShipmentDamageItem).doc(item.id).delete()))
                : of([])
            )
        )
    }

    public setPlasticsHardwareItemShipmentDamageCamData(values: PlasticsHardwareItemShipmentDamageCamEntry[]): Observable<void> {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.PlasticsHardwareItemShipmentDamageCAMData).set({ values }));
    }

    public deleteUserEntryForDamageItemFromPlasticsHardwareItemShipmentDamageCamData(userId: string, damageItemId: string): Observable<void> {
        return this.loadPlasticsHardwareItemShipmentDamageCamData().pipe(
            switchMap(resp => {
                const updated = resp.filter(entry => entry.userId !== userId || entry.damageItemId !== damageItemId);
                return this.setPlasticsHardwareItemShipmentDamageCamData(updated);
            })
        )
    }

    public deleteUserEntriesForPlasticsHardwareItemShipmentFromDamageCamData(userId: string, shipmentId: string): Observable<void> {
        return this.loadPlasticsHardwareItemShipmentDamageCamData().pipe(
            switchMap(resp => {
                const updated = resp.filter(entry => entry.userId !== userId || entry.plasticsHardwareItemShipmentId !== shipmentId);
                return this.setPlasticsHardwareItemShipmentDamageCamData(updated);
            })
        )
    }

    public getPlasticsHardwareItemShipmentDamageCamDataValueChanges(): Observable<PlasticsHardwareItemShipmentDamageCamEntry[]> {
        const doc = this.appwrite.collection<{ values: PlasticsHardwareItemShipmentDamageCamEntry[] }>(Collection.globalVariables).doc(GlobalVariableDocumentIds.PlasticsHardwareItemShipmentDamageCAMData);
        return doc.valueChanges().pipe(
            catchError((error) => {
                if (!this.isAppwriteNotFound(error)) {
                    return throwError(() => error);
                }
                return this.setPlasticsHardwareItemShipmentDamageCamData([]).pipe(
                    switchMap(() => doc.valueChanges()),
                );
            }),
            map((resp) => {
                if (!resp || !resp.values) return []
                return resp.values
            }),
        )
    }

    public loadPlasticsHardwareItemShipmentDamageCamData(): Observable<PlasticsHardwareItemShipmentDamageCamEntry[]> {
        return this.appwrite.get<{ values: PlasticsHardwareItemShipmentDamageCamEntry[] }>(Collection.globalVariables, GlobalVariableDocumentIds.PlasticsHardwareItemShipmentDamageCAMData).pipe(
            map(resp => {
                if (!resp || !resp.values) return []
                return resp.values
            }),
            catchError((error) => {
                if (!this.isAppwriteNotFound(error)) {
                    return throwError(() => error);
                }
                return of([]);
            }),
        )
    }

    private isAppwriteNotFound(error: unknown): boolean {
        return typeof error === 'object'
            && error !== null
            && 'code' in error
            && (error as { code?: number }).code === 404;
    }

    // ============================
    // Plastics Inventory (Location, Bin, Area)
    // ============================
    // CRUD and query operations for inventory locations, bins, and areas

    public loadInventoryLocations(): Observable<PlasticsInventoryLocation[]> {
        return from(this.appwrite.collection<PlasticsInventoryLocation>(
            Collection.plasticsInventoryLocation
        ).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
            defaultIfEmpty([])
        ))
    }



    public loadInventoryLocationsWithBins(): Observable<PlasticsInventoryLocationWithBins[]> {
        return from(
            this.appwrite
                .collection<PlasticsInventoryLocation>(Collection.plasticsInventoryLocation)
                .get()
                .pipe(
                    switchMap((locationsSnapshot) => {
                        const locations: PlasticsInventoryLocation[] = locationsSnapshot.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc);

                        if (locations.length === 0) {
                            return of([]);
                        }

                        // Load all bins for all locations
                        return this.appwrite
                            .collection<PlasticsInventoryLocationBin>(Collection.plasticsInventoryLocationBin)
                            .get()
                            .pipe(
                                map((binsSnapshot) => {
                                    const allBins: PlasticsInventoryLocationBin[] = binsSnapshot.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc);

                                    // Group bins by location ID
                                    const binsByLocation = allBins.reduce((acc, bin) => {
                                        if (!acc[bin.inventoryLocationId]) {
                                            acc[bin.inventoryLocationId] = [];
                                        }
                                        acc[bin.inventoryLocationId].push(bin);
                                        return acc;
                                    }, {} as Record<string, PlasticsInventoryLocationBin[]>);

                                    // Combine locations with their bins
                                    return locations.map((location) => ({
                                        inventoryLocation: location,
                                        bins: binsByLocation[location.id] || [],
                                        inventoryAreaId: ''
                                    }));
                                })
                            );
                    }),
                    defaultIfEmpty([])
                )
        );
    }


    // Load a specific inventory area by ID
    loadInventoryAreaById(areaId: string): Observable<PlasticsInventoryArea | null> {
        return this.appwrite
            .collection<PlasticsInventoryArea>(Collection.plasticsInventoryArea)
            .doc(areaId)
            .valueChanges()
            .pipe(
                map(area => area || null)
            );
    }

    // Load all inventory locations for a specific area
    loadInventoryLocationsByAreaId(areaId: string): Observable<PlasticsInventoryLocation[]> {
        return this.appwrite
            .collection<PlasticsInventoryLocation>(
                Collection.plasticsInventoryLocation,
                ref => ref.where('inventoryAreaId', '==', areaId)
            )
            .valueChanges()
            .pipe(
                map(locations => locations.sort((a, b) => a.name.localeCompare(b.name)))
            );
    }

    // Load all bins for all locations in a specific area
    loadInventoryBinsByAreaId(areaId: string): Observable<PlasticsInventoryLocationBin[]> {
        // First get all location IDs for this area
        return this.loadInventoryLocationsByAreaId(areaId).pipe(
            switchMap(locations => {
                const locationIds = locations.map(l => l.id);

                if (locationIds.length === 0) {
                    return of([]);
                }

                // Appwrite compat 'in' queries are limited to 10 items, so we may need to batch
                const chunks: string[][] = [];
                for (let i = 0; i < locationIds.length; i += 10) {
                    chunks.push(locationIds.slice(i, i + 10));
                }

                // Query bins for each chunk and combine
                const queries = chunks.map(chunk =>
                    this.appwrite
                        .collection<PlasticsInventoryLocationBin>(
                            Collection.plasticsInventoryLocationBin,
                            ref => ref.where('inventoryLocationId', 'in', chunk)
                        )
                        .valueChanges()
                );

                return combineLatest(queries.length > 0 ? queries : [of([])]).pipe(
                    map(results => results.flat())
                );
            })
        );
    }

    public loadInventoryLocationById(id: string): Observable<PlasticsInventoryLocation | null> {
        return from(this.appwrite.collection<PlasticsInventoryLocation>(
            Collection.plasticsInventoryLocation
        ).doc(id).get().pipe(
            map((doc) => doc.exists ? fromAppwriteDoc(doc) : null)
        ))
    }

    public loadInventoryLocationsByIds(ids: string[]): Observable<PlasticsInventoryLocation[]> {
        return this.appwrite.listByIds<PlasticsInventoryLocation>(Collection.plasticsInventoryLocation, ids);
    }

    public loadInventoryLocationBins(): Observable<PlasticsInventoryLocationBin[]> {
        return this.appwrite.list<PlasticsInventoryLocationBin>(Collection.plasticsInventoryLocationBin, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([] as PlasticsInventoryLocationBin[]),
        );
    }

    public loadInventoryLocationBinsByLocationId(locationId: string): Observable<PlasticsInventoryLocationBin[]> {
        return from(
            this.appwrite
                .collection<PlasticsInventoryLocationBin>(
                    Collection.plasticsInventoryLocationBin,
                    ref => ref.where('inventoryLocationId', '==', locationId)
                )
                .get()
                .pipe(
                    map((snapshot) => snapshot.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
                    defaultIfEmpty([])
                )
        );
    }

    public loadInventoryLocationBinsByHardwareItemId(hardwareItemId: string): Observable<PlasticsInventoryLocationBin[]> {
        return this.appwrite.list<PlasticsInventoryLocationBin>(Collection.plasticsInventoryLocationBin, [
            Query.equal('hardwareItemId', hardwareItemId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([] as PlasticsInventoryLocationBin[]),
        );
    }

    public loadInventoryLocationBinById(id: string): Observable<PlasticsInventoryLocationBin | null> {
        return from(this.appwrite.collection<PlasticsInventoryLocationBin>(
            Collection.plasticsInventoryLocationBin
        ).doc(id).get().pipe(
            map((doc) => doc.exists ? fromAppwriteDoc(doc) : null)
        ))
    }

    public loadInventoryLocationBinsByIds(ids: string[]): Observable<PlasticsInventoryLocationBin[]> {
        return this.appwrite.listByIds<PlasticsInventoryLocationBin>(Collection.plasticsInventoryLocationBin, ids);
    }

    public loadInventoryLocationBinsByInventoryLocationId(inventoryLocationId: string): Observable<PlasticsInventoryLocationBin[]> {
        return from(this.appwrite.collection<PlasticsInventoryLocationBin>(
            Collection.plasticsInventoryLocationBin,
            ref => ref.where('inventoryLocationId', '==', inventoryLocationId)
        ).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
            defaultIfEmpty([])
        ))
    }

    public setInventoryLocation(location: PlasticsInventoryLocation): Observable<void> {
        return from(this.appwrite.collection<PlasticsInventoryLocation>(
            Collection.plasticsInventoryLocation
        ).doc(location.id).set(location))
    }

    public setInventoryLocations(locations: PlasticsInventoryLocation[]): Observable<void> {
        return forkJoin(locations.map(location => this.setInventoryLocation(location))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        )
    }

    public setInventoryLocationBin(bin: PlasticsInventoryLocationBin): Observable<void> {
        return from(this.appwrite.collection<PlasticsInventoryLocationBin>(
            Collection.plasticsInventoryLocationBin
        ).doc(bin.id).set(bin))
    }

    public setInventoryLocationBins(bins: PlasticsInventoryLocationBin[]): Observable<void> {
        return forkJoin(bins.map(bin => this.setInventoryLocationBin(bin))).pipe(
            map(() => void 0),
            defaultIfEmpty(void 0),
        )
    }

    public loadInventoryAreas(): Observable<PlasticsInventoryArea[]> {
        return this.appwrite.collection<PlasticsInventoryArea>(Collection.plasticsInventoryArea).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }



    // ============================
    // FH Corner Label Config
    // ============================
    // Global configuration for FH corner labels

    public setFHCornerLabelConfig(config: FHCornerLabelConfig): Observable<void> {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.FHCornerLabelConfig).set({
            ...config,
            inventoryTransactionHardwareItemIds: config.inventoryTransactionHardwareItemIds ?? [],
            fhNobleCustomerId: config.fhNobleCustomerId ?? '',
            boxInventoryRulesByDesignId: config.boxInventoryRulesByDesignId ?? {},
        }));
    }

    public loadFHCornerLabelConfig(): Observable<FHCornerLabelConfig> {
        return this.appwrite.collection<FHCornerLabelConfig>(Collection.globalVariables).doc(GlobalVariableDocumentIds.FHCornerLabelConfig).get().pipe(
            map(resp => {
                if (!resp || !resp.exists) {
                    return null as unknown as FHCornerLabelConfig
                }

                const config = resp.data() as FHCornerLabelConfig;
                return {
                    ...config,
                    inventoryTransactionHardwareItemIds: config.inventoryTransactionHardwareItemIds ?? [],
                    fhNobleCustomerId: config.fhNobleCustomerId ?? '',
                    boxInventoryRulesByDesignId: config.boxInventoryRulesByDesignId ?? {},
                }
            })
        )
    }

    public loadPlasticsManufacturingRatesParentById(manufacturingRatesId: string): Observable<PlasticsManufacturingRatesParent> {
        return this.appwrite.get<PlasticsManufacturingRatesParent>(Collection.plasticsManufacturingRates, manufacturingRatesId);
    }

    public setPlasticsManufacturingRatesParent(manufacturingRates: PlasticsManufacturingRatesParent): Observable<void> {
        return from(this.appwrite.collection<PlasticsManufacturingRatesParent>(Collection.plasticsManufacturingRates).doc(manufacturingRates.id).set(manufacturingRates));
    }

    public deletePlasticsManufacturingRatesParent(manufacturingRatesId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsManufacturingRates, manufacturingRatesId);
    }

    public loadPlasticsManufacturingRatesVersionsByParentId(manufacturingRatesId: string): Observable<PlasticsManufacturingRatesVersion[]> {
        return this.appwrite.list<PlasticsManufacturingRatesVersion>(Collection.plasticsManufacturingRatesVersion, [
            Query.equal('manufacturingRatesId', manufacturingRatesId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadPlasticsManufacturingRatesVersionById(manufacturingRatesVersionId: string): Observable<PlasticsManufacturingRatesVersion> {
        return this.appwrite.get<PlasticsManufacturingRatesVersion>(Collection.plasticsManufacturingRatesVersion, manufacturingRatesVersionId);
    }

    public setPlasticsManufacturingRatesVersion(manufacturingRatesVersion: PlasticsManufacturingRatesVersion): Observable<void> {
        return from(this.appwrite.collection<PlasticsManufacturingRatesVersion>(Collection.plasticsManufacturingRatesVersion).doc(manufacturingRatesVersion.id).set(manufacturingRatesVersion));
    }

    public deletePlasticsManufacturingRatesVersion(manufacturingRatesVersionId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsManufacturingRatesVersion, manufacturingRatesVersionId);
    }

    public getReleasedPlasticsManufacturingRatesVersionCount(manufacturingRatesId: string): Observable<number> {
        return this.appwrite.list<PlasticsManufacturingRatesVersion>(Collection.plasticsManufacturingRatesVersion, [
            Query.equal('manufacturingRatesId', manufacturingRatesId),
            Query.equal('status', [PlasticsManufacturingRatesVersionStatus.Released, PlasticsManufacturingRatesVersionStatus.Obsolete]),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.total),
        );
    }


    // ============================
    // Other / Multi-Entity Operations
    // ============================
    // Complex operations that span multiple entity types (production recording, BOM consumption, dependent data loading, etc.)

    // Legacy path still uses read-modify-write for qty bumps.
    public newProductInventoryTransaction(
        productId: string,
        changeQty: number,
        comment: string,
        hardwareItemIdToQuantityChangeDict: Record<string, number>,
        productIdToQuantityChangeDict: Record<string, number>,
        materialIdToQuantityChangeDict: Record<string, number>,
        curentUser: User,
        processId: string | null = null,
    ): Observable<InventoryTransaction> {

        const transaction: InventoryTransaction = {
            id: this.appwrite.createId(),
            entityId: productId,
            change: changeQty,
            comment,
            createdOn: new Date(),
            createdById: curentUser?.id || "",
            type: InventoryTransactionType.PlasticsProduct,
        }

        const productUpdate$: Observable<void> = this.appwrite.get<Product>(Collection.product, productId).pipe(
            switchMap(p => from(this.appwrite.collection<Product>(Collection.product).doc(p.id).set({ ...p, qtyOnHand: (p.qtyOnHand ?? 0) + changeQty }))),
        );

        const transactionCreate$: Observable<void> = this.sharedApi.setInventoryTransaction(transaction);

        const hardwareItemUpdates$: Observable<void>[] = Object.entries(hardwareItemIdToQuantityChangeDict).map(([hardwareItemId, change]) => {
            return this.appwrite.get<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemId).pipe(
                switchMap(item => {
                    if (item.isUnitCounted) {
                        return from(this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(item.id).set({ ...item, qtyOnHand: (item.qtyOnHand ?? 0) + change }));
                    }
                    return of(undefined);
                })
            );
        });

        const productUpdates$: Observable<void>[] = Object.entries(productIdToQuantityChangeDict).map(([pid, change]) => {
            return this.appwrite.get<Product>(Collection.product, pid).pipe(
                switchMap(p => from(this.appwrite.collection<Product>(Collection.product).doc(p.id).set({ ...p, qtyOnHand: (p.qtyOnHand ?? 0) + change }))),
            );
        });

        const materialUpdates$: Observable<void>[] = Object.entries(materialIdToQuantityChangeDict).map(([materialId, change]) => {
            return this.appwrite.get<PlasticsMaterial>(Collection.plasticsMaterial, materialId).pipe(
                switchMap(m => from(this.appwrite.collection<PlasticsMaterial>(Collection.plasticsMaterial).doc(m.id).set({ ...m, qtyOnHand: (m.qtyOnHand ?? 0) + change }))),
            );
        });

        const hardwareItemTransactions$: Observable<void>[] = Object.entries(hardwareItemIdToQuantityChangeDict).map(([hardwareItemId, change]) => {
            return this.appwrite.get<PlasticsHardwareItem>(Collection.plasticsHardwareItem, hardwareItemId).pipe(
                switchMap(item => {
                    if (item.isUnitCounted) {
                        const hardwareTransaction: InventoryTransaction = {
                            id: this.appwrite.createId(),
                            entityId: hardwareItemId,
                            change,
                            comment,
                            createdOn: new Date(),
                            createdById: curentUser?.id || "",
                            type: InventoryTransactionType.PlasticsHardware,
                        };
                        return this.sharedApi.setInventoryTransaction(hardwareTransaction);
                    }
                    return of(undefined);
                })
            );
        });
        const productTransactions$: Observable<InventoryTransaction>[] = Object.entries(productIdToQuantityChangeDict).map(([pid, change]) => {
            const productTransaction: InventoryTransaction = {
                id: this.appwrite.createId(),
                entityId: pid,
                change,
                comment,
                createdOn: new Date(),
                createdById: curentUser?.id || "",
                type: InventoryTransactionType.PlasticsProduct,
            };
            return this.sharedApi.setInventoryTransaction(productTransaction).pipe(
                map(() => productTransaction)
            );
        });
        const materialTransactions$: Observable<InventoryTransaction>[] = Object.entries(materialIdToQuantityChangeDict).map(([materialId, change]) => {
            const materialTransaction: InventoryTransaction = {
                id: this.appwrite.createId(),
                entityId: materialId,
                change,
                comment,
                createdOn: new Date(),
                createdById: curentUser?.id || "",
                type: InventoryTransactionType.PlasticsMaterial,
            };
            return this.sharedApi.setInventoryTransaction(materialTransaction).pipe(
                map(() => materialTransaction)
            );
        });

        return forkJoin([
            productUpdate$,
            transactionCreate$,
            ...hardwareItemUpdates$,
            ...productUpdates$,
            ...hardwareItemTransactions$,
            ...productTransactions$,
            ...materialUpdates$,
            ...materialTransactions$
        ]).pipe(
            map(() => transaction)
        )

    }

    public loadMoldingTaskDependantDataByMoldingTaskId(moldingTaskId: string): Observable<MoldingTaskDependantData> {
        return this.loadMoldingTasksByIds([moldingTaskId]).pipe(
            first(),
            catchError(error => {
                console.error('Error loading scheduled products:', error);
                return throwError(() => new Error('Failed to load scheduled products.'));
            }),
            mergeMap(([moldingTask]) => {
                // placeholder
                if (!moldingTask.productId) {
                    return of({
                        product: null as any,
                        moldingTask: moldingTask,
                        productVersion: null as any,
                        lineItems: [],
                        lineItemHardwareItems: [],
                        vendorHardwareData: [],
                        lineItemProducts: [],
                        lineItemProductVersions: [],
                        lineItemMaterials: []
                    } as MoldingTaskDependantData)
                }

                return this.loadProductById(moldingTask.productId).pipe(
                    catchError(error => {
                        console.error('Error loading product:', error);
                        return throwError(() => new Error('Failed to load product.'));
                    }),
                    mergeMap((product) =>
                        this.loadReleasedProductVersionByProductId(product.id).pipe(
                            catchError(error => {
                                console.error('Error loading released product version:', error);
                                return throwError(() => new Error('Failed to load released product version.'));
                            }),
                            mergeMap((productVersion) =>
                                this.loadBillOfMaterialsLineItemsByProductVersionId(productVersion.id).pipe(
                                    catchError(error => {
                                        console.error('Error loading bill of materials line items:', error);
                                        return throwError(() => new Error('Failed to load bill of materials line items.'));
                                    }),
                                    mergeMap((lineItems) => {
                                        const hardwareItemIds = lineItems
                                            .filter(li => li.type === BillOfMaterialsLineItemType.Hardware)
                                            .map(li => li.entityId);
                                        const productIds = lineItems
                                            .filter(li => li.type === BillOfMaterialsLineItemType.Product)
                                            .map(li => li.entityId);
                                        const materialIds = lineItems
                                            .filter(li => li.type === BillOfMaterialsLineItemType.Material)
                                            .map(li => li.entityId);

                                        const hardwareItems$ = this.loadHardwareItemsByIds(hardwareItemIds).pipe(
                                            catchError(error => {
                                                console.error('Error loading hardware items:', error);
                                                return of([]); // Return empty array on failure
                                            })
                                        );

                                        const vendorHardwareData$ = this.loadVendorHardwareDataByHardwareItemIds(hardwareItemIds)

                                        const productVersions$ = this.loadReleasedProductVersionsByProductIds(productIds).pipe(
                                            catchError(error => {
                                                console.error('Error loading product versions:', error);
                                                return of([]); // Return empty array on failure
                                            })
                                        );

                                        const products$ = this.loadProductsByIds(productIds).pipe(
                                            catchError(error => {
                                                console.error('Error loading products:', error);
                                                return of([]); // Return empty array on failure
                                            })
                                        );

                                        const materials$ = this.loadMaterialsByIds(materialIds).pipe(
                                            catchError(error => {
                                                console.error('Error loading materials:', error);
                                                return of([]); // Return empty array on failure
                                            })
                                        );

                                        return forkJoin([
                                            hardwareItems$,
                                            products$,
                                            productVersions$,
                                            materials$,
                                            vendorHardwareData$
                                        ]).pipe(
                                            catchError(error => {
                                                console.error('Error during forkJoin processing:', error);
                                                return throwError(() => new Error('Failed to load some dependencies.'));
                                            }),
                                            map(([
                                                lineItemHardwareItems,
                                                lineItemProducts,
                                                lineItemProductVersions,
                                                lineItemMaterials,
                                                vendorHardwareData
                                            ]) => {
                                                const data: MoldingTaskDependantData = {
                                                    product,
                                                    moldingTask: moldingTask,
                                                    productVersion,
                                                    lineItems,
                                                    lineItemHardwareItems,
                                                    lineItemProducts,
                                                    lineItemProductVersions,
                                                    lineItemMaterials,
                                                    vendorHardwareData: vendorHardwareData.flat(),
                                                }
                                                return data;
                                            })
                                        );
                                    })
                                )
                            )
                        )
                    )
                )
            })
        );

    }

    public loadMoldingTaskDependantDataByMoldingTaskIds(moldingTaskIds: string[]): Observable<MoldingTaskDependantData[]> {
        const uniqueMoldingTaskIds = [...new Set(moldingTaskIds.filter(id => !!id))];

        if (uniqueMoldingTaskIds.length === 0) {
            return of([]);
        }

        return this.loadMoldingTasksByIds(uniqueMoldingTaskIds).pipe(
            switchMap((moldingTasks) => {
                const productIds = [...new Set(moldingTasks
                    .map(task => task.productId)
                    .filter(productId => !!productId))];

                if (productIds.length === 0) {
                    return of(moldingTasks.map((moldingTask) => ({
                        product: null as any,
                        moldingTask,
                        productVersion: null as any,
                        lineItems: [],
                        lineItemHardwareItems: [],
                        vendorHardwareData: [],
                        lineItemProducts: [],
                        lineItemProductVersions: [],
                        lineItemMaterials: [],
                    })));
                }

                return this.loadProductsAndReleasedProductVersionsByIds(productIds).pipe(
                    switchMap(({ products, productVersions }) => {
                        const productById = products.reduce((dict, product) => {
                            dict[product.id] = product;
                            return dict;
                        }, {} as Record<string, Product>);

                        return this.loadReleasedProductVersionDependantDataForVersions(productVersions).pipe(
                            map((dataByProductId) => moldingTasks.map((moldingTask): MoldingTaskDependantData => {
                                const data = dataByProductId[moldingTask.productId];

                                if (!moldingTask.productId || !data) {
                                    return {
                                        product: null as any,
                                        moldingTask,
                                        productVersion: null as any,
                                        lineItems: [],
                                        lineItemHardwareItems: [],
                                        vendorHardwareData: [],
                                        lineItemProducts: [],
                                        lineItemProductVersions: [],
                                        lineItemMaterials: [],
                                    };
                                }

                                return {
                                    product: productById[moldingTask.productId],
                                    moldingTask,
                                    productVersion: data.productVersion,
                                    lineItems: data.lineItems,
                                    lineItemHardwareItems: data.lineItemHardwareItems,
                                    vendorHardwareData: data.vendorHardwareData,
                                    lineItemProducts: data.lineItemProducts,
                                    lineItemProductVersions: data.lineItemProductVersions,
                                    lineItemMaterials: data.lineItemMaterials,
                                };
                            })),
                        );
                    }),
                );
            }),
        );
    }

    public loadMoldingTaskOrderedProductData(moldingTaskId: string): Observable<{
        orderedProduct: PlasticsOrderedProduct | null;
        orderedProductVersion: ProductVersion | null;
        customer: Customer | null;
    }> {
        return this.loadMoldingTaskById(moldingTaskId).pipe(
            switchMap((moldingTask) => {

                if (!moldingTask.orderedProductId) {
                    return of({
                        orderedProduct: null,
                        orderedProductVersion: null,
                        customer: null,
                    })
                }

                return this.loadOrderedProductById(moldingTask.orderedProductId).pipe(
                    switchMap((orderedProduct) => {

                        return forkJoin([
                            this.loadReleasedProductVersionByProductId(orderedProduct.productId),
                            this.commonApiService.loadCustomerById(orderedProduct.customerId)
                        ]).pipe(
                            map(([orderedProductVersion, customer]) => ({
                                orderedProductVersion,
                                customer,
                                orderedProduct,
                            }))
                        )
                    })
                )
            })
        )
    }

    private emptyReleasedProductVersionDependantData(): ReleasedProductVersionDependantData {
        return {
            productVersion: null as unknown as ProductVersion,
            lineItems: [],
            lineItemHardwareItems: [],
            lineItemProducts: [],
            lineItemProductVersions: [],
            lineItemMaterials: [],
            vendorHardwareData: [],
            productDrawingResource: null,
        };
    }

    private loadReleasedProductVersionDependantDataForVersions(productVersions: ProductVersion[]): Observable<Record<string, ReleasedProductVersionDependantData>> {
        const uniqueProductVersions = [...new Map(productVersions
            .filter(productVersion => !!productVersion?.id)
            .map(productVersion => [productVersion.id, productVersion])).values()];

        if (uniqueProductVersions.length === 0) {
            return of({});
        }

        const productVersionIds = uniqueProductVersions.map(productVersion => productVersion.id);

        return this.loadBillOfMaterialsLineItemsByProductVersionIds(productVersionIds).pipe(
            catchError(error => {
                console.error('Error loading bill of materials line items:', error);
                return throwError(() => new Error('Failed to load bill of materials line items.'));
            }),
            switchMap((lineItems) => {
                const hardwareItemIds = [...new Set(lineItems
                    .filter(li => li.type === BillOfMaterialsLineItemType.Hardware)
                    .map(li => li.entityId)
                    .filter(id => !!id))];
                const productIds = [...new Set(lineItems
                    .filter(li => li.type === BillOfMaterialsLineItemType.Product)
                    .map(li => li.entityId)
                    .filter(id => !!id))];
                const materialIds = [...new Set(lineItems
                    .filter(li => li.type === BillOfMaterialsLineItemType.Material)
                    .map(li => li.entityId)
                    .filter(id => !!id))];
                const resourceIds = [...new Set(uniqueProductVersions
                    .map(productVersion => productVersion.drawingResourceId)
                    .filter(resourceId => !!resourceId))];

                const hardwareItems$ = hardwareItemIds.length ? this.loadHardwareItemsByIds(hardwareItemIds).pipe(
                    catchError(error => {
                        console.error('Error loading hardware items:', error);
                        return of([]);
                    })
                ) : of([]);

                const vendorHardwareData$ = hardwareItemIds.length
                    ? this.loadVendorHardwareDataByHardwareItemIds(hardwareItemIds)
                    : of([]);

                const productVersions$ = productIds.length ? this.loadReleasedProductVersionsByProductIds(productIds).pipe(
                    catchError(error => {
                        console.error('Error loading product versions:', error);
                        return of([]);
                    })
                ) : of([]);

                const products$ = productIds.length ? this.loadProductsByIds(productIds).pipe(
                    catchError(error => {
                        console.error('Error loading products:', error);
                        return of([]);
                    })
                ) : of([]);

                const materials$ = materialIds.length ? this.loadMaterialsByIds(materialIds).pipe(
                    catchError(error => {
                        console.error('Error loading materials:', error);
                        return of([]);
                    })
                ) : of([]);

                const productDrawingResources$: Observable<Resource[]> = resourceIds.length ? this.commonApiService.loadResourcesByIds(resourceIds).pipe(
                    catchError(error => {
                        console.error('Error loading product drawing resources:', error);
                        return of([] as Resource[]);
                    })
                ) : of([] as Resource[]);

                return forkJoin([
                    hardwareItems$,
                    products$,
                    productVersions$,
                    materials$,
                    vendorHardwareData$,
                    productDrawingResources$,
                ]).pipe(
                    catchError(error => {
                        console.error('Error during forkJoin processing:', error);
                        return throwError(() => new Error('Failed to load some dependencies.'));
                    }),
                    map(([
                        lineItemHardwareItems,
                        lineItemProducts,
                        lineItemProductVersions,
                        lineItemMaterials,
                        vendorHardwareData,
                        productDrawingResources,
                    ]) => {
                        const resourceById = productDrawingResources.reduce((dict: Record<string, Resource>, resource: Resource) => {
                            dict[resource.id] = resource;
                            return dict;
                        }, {} as Record<string, Resource>);

                        return uniqueProductVersions.reduce((dict, productVersion) => {
                            const productVersionLineItems = lineItems.filter(lineItem => lineItem.productVersionId === productVersion.id);
                            const productVersionHardwareIds = new Set(productVersionLineItems
                                .filter(lineItem => lineItem.type === BillOfMaterialsLineItemType.Hardware)
                                .map(lineItem => lineItem.entityId));
                            const productVersionProductIds = new Set(productVersionLineItems
                                .filter(lineItem => lineItem.type === BillOfMaterialsLineItemType.Product)
                                .map(lineItem => lineItem.entityId));
                            const productVersionMaterialIds = new Set(productVersionLineItems
                                .filter(lineItem => lineItem.type === BillOfMaterialsLineItemType.Material)
                                .map(lineItem => lineItem.entityId));

                            dict[productVersion.productId] = {
                                productVersion,
                                lineItems: productVersionLineItems,
                                lineItemHardwareItems: lineItemHardwareItems.filter(item => productVersionHardwareIds.has(item.id)),
                                lineItemProducts: lineItemProducts.filter(product => productVersionProductIds.has(product.id)),
                                lineItemProductVersions: lineItemProductVersions.filter(version => productVersionProductIds.has(version.productId)),
                                lineItemMaterials: lineItemMaterials.filter(material => productVersionMaterialIds.has(material.id)),
                                vendorHardwareData: vendorHardwareData.filter(data => productVersionHardwareIds.has(data.parentHardwareItemId)),
                                productDrawingResource: productVersion.drawingResourceId
                                    ? resourceById[productVersion.drawingResourceId] || null
                                    : null,
                            };

                            return dict;
                        }, {} as Record<string, ReleasedProductVersionDependantData>);
                    }),
                );
            }),
        );
    }

    public loadReleasedProductVersionDependantDataByProductIds(productIds: string[]): Observable<Record<string, ReleasedProductVersionDependantData>> {
        const uniqueProductIds = [...new Set(productIds.filter(productId => !!productId))];

        if (uniqueProductIds.length === 0) {
            return of({});
        }

        return this.loadReleasedProductVersionsByProductIds(uniqueProductIds).pipe(
            switchMap(productVersions => this.loadReleasedProductVersionDependantDataForVersions(productVersions)),
        );
    }

    public loadReleasedProductVersionDependantDataByProductId(productId: string): Observable<ReleasedProductVersionDependantData> {
        if (!productId) {
            return of(this.emptyReleasedProductVersionDependantData())
        }
        return this.loadReleasedProductVersionDependantDataByProductIds([productId]).pipe(
            map(dataByProductId => dataByProductId[productId] ?? this.emptyReleasedProductVersionDependantData()),
        )

    }

    public recordRotoMachineArmCycle(
        productInstances: ProductInstance[],
        machineId: string,
        armId: string,
        currentUser: User,
    ): Observable<PlasticsMoldingTask[]> {
        const recordedProductInstances = productInstances.filter(instance =>
            instance.outcome === RotoMachinePartOutcome.GOOD ||
            instance.outcome === RotoMachinePartOutcome.BAD
        );
        const productVersionIds = [...new Set(recordedProductInstances
            .map(instance => instance.productVersionId)
            .filter(id => !!id))];
        const moldingTaskIds = [...new Set(recordedProductInstances
            .map(instance => instance.scheduledProductId)
            .filter(id => !!id))];

        const billOfMaterialsLineItems$ = productVersionIds.length
            ? this.loadBillOfMaterialsLineItemsByProductVersionIds(productVersionIds)
            : of([] as BillOfMaterialsLineItem[]);
        const moldingTasks$ = moldingTaskIds.length
            ? this.loadMoldingTasksByIds(moldingTaskIds)
            : of([] as PlasticsMoldingTask[]);

        return forkJoin({
            lineItems: billOfMaterialsLineItems$,
            moldingTasks: moldingTasks$,
            cycleSchedule: this.loadRotoSchedule(),
        }).pipe(
            switchMap(({ lineItems, moldingTasks, cycleSchedule }) => {
                const currentTaskIds = new Set(cycleSchedule.productLists
                    .filter(list => list.machineId === machineId && list.armId === armId)
                    .map(list => list.moldingTaskIds[0]));
                if (moldingTaskIds.some(id => !currentTaskIds.has(id)
                    || !moldingTasks.some(task => task.id === id && task.status === PlasticsMoldingTaskStatus.IN_PROGRESS))) {
                    return throwError(() => new Error('The machine setup changed. Refresh before recording another cycle.'));
                }
                const hardwareItemIds = [...new Set(lineItems
                    .filter(lineItem => lineItem.type === BillOfMaterialsLineItemType.Hardware)
                    .map(lineItem => lineItem.entityId)
                    .filter(id => !!id))];
                const hardwareItems$ = hardwareItemIds.length
                    ? this.loadHardwareItemsByIds(hardwareItemIds)
                    : of([] as PlasticsHardwareItem[]);

                return forkJoin({
                    hardwareItems: hardwareItems$,
                    molds: this.loadMoldsByIds([...new Set(moldingTasks.map(task => task.moldId).filter(Boolean))]),
                    checkupSettings: this.moldCheckups.settings(),
                }).pipe(
                    switchMap(({ hardwareItems, molds, checkupSettings }) => {
                        if (checkupSettings.id && checkupSettings.enabled && moldingTasks.some(task => {
                            if (!task.moldId) return false;
                            const mold = molds.find(item => item.id === task.moldId);
                            return !mold || mold.status !== PlasticsMoldStatus.Active || !!mold.openCheckupId
                                || mold.placement?.taskId !== task.id || mold.placement.machineId !== machineId || mold.placement.armId !== armId;
                        })) return throwError(() => new Error('The scheduled mold is not confirmed on this machine arm. Ask a supervisor to confirm the machine setup before recording another cycle.'));
                        const now = new Date();
                        const hardwareItemDict = hardwareItems.reduce<Record<string, PlasticsHardwareItem>>((dict, item) => {
                            dict[item.id] = item;
                            return dict;
                        }, {});
                        const lineItemsByProductVersionId = lineItems.reduce<Record<string, BillOfMaterialsLineItem[]>>((dict, lineItem) => {
                            if (!dict[lineItem.productVersionId]) {
                                dict[lineItem.productVersionId] = [];
                            }
                            dict[lineItem.productVersionId].push(lineItem);
                            return dict;
                        }, {});
                        const moldingTaskById = moldingTasks.reduce<Record<string, PlasticsMoldingTask>>((dict, task) => {
                            dict[task.id] = task;
                            return dict;
                        }, {});

                        const inventoryTransactions: InventoryTransaction[] = [];
                        const incrementOperationsByKey: Record<string, QuantityIncrementOperation> = {};
                        const goodMoldingTaskIds = [...new Set(recordedProductInstances
                            .filter(instance => instance.outcome === RotoMachinePartOutcome.GOOD)
                            .map(instance => instance.scheduledProductId)
                            .filter(id => !!id))];
                        const moldArmCycles: MoldArmCycle[] = recordedProductInstances
                            .map(instance => {
                                const moldingTask = moldingTaskById[instance.scheduledProductId];
                                if (!moldingTask?.moldId) {
                                    return null;
                                }
                                return {
                                    id: this.appwrite.createId(),
                                    moldId: moldingTask.moldId,
                                    moldingTaskId: moldingTask.id,
                                    completedOn: now,
                                    productVersionId: instance.productVersionId,
                                };
                            })
                            .filter((cycle): cycle is MoldArmCycle => !!cycle);

                        moldArmCycles.forEach(cycle => {
                            this.addQuantityIncrement(
                                incrementOperationsByKey,
                                Collection.plasticsMold,
                                cycle.moldId,
                                'totalCycleCount',
                                1,
                            );
                        });

                        const addInventoryTransaction = (
                            entityId: string,
                            change: number,
                            comment: string,
                            type: InventoryTransactionType,
                        ) => {
                            inventoryTransactions.push({
                                id: this.appwrite.createId(),
                                entityId,
                                change,
                                comment,
                                createdOn: now,
                                createdById: currentUser?.id || "",
                                type,
                            });
                        };

                        recordedProductInstances.forEach((instance) => {
                            if (instance.outcome === RotoMachinePartOutcome.GOOD) {
                                this.addQuantityIncrement(
                                    incrementOperationsByKey,
                                    Collection.plasticsMoldingTask,
                                    instance.scheduledProductId,
                                    'completedQuantity',
                                    1,
                                );
                                this.addQuantityIncrement(
                                    incrementOperationsByKey,
                                    Collection.product,
                                    instance.productId,
                                    'qtyOnHand',
                                    1,
                                );
                            }

                            // Existing business rule: BAD records production activity and BOM usage,
                            // but only GOOD advances the scheduled molding quantity.
                            addInventoryTransaction(
                                instance.productId,
                                1,
                                `created with product instance ${instance.id}`,
                                InventoryTransactionType.PlasticsProduct,
                            );

                            const instanceLineItems = lineItemsByProductVersionId[instance.productVersionId] || [];
                            instanceLineItems.forEach((lineItem) => {
                                const change = -lineItem.quantity;
                                if (lineItem.type === BillOfMaterialsLineItemType.Product) {
                                    this.addQuantityIncrement(
                                        incrementOperationsByKey,
                                        Collection.product,
                                        lineItem.entityId,
                                        'qtyOnHand',
                                        change,
                                    );
                                    addInventoryTransaction(
                                        lineItem.entityId,
                                        change,
                                        `created with product instance ${instance.id}`,
                                        InventoryTransactionType.PlasticsProduct,
                                    );
                                    return;
                                }

                                if (lineItem.type === BillOfMaterialsLineItemType.Hardware) {
                                    const hardwareItem = hardwareItemDict[lineItem.entityId];
                                    if (!hardwareItem) {
                                        throw new Error(`BOM hardware item not found: ${lineItem.entityId}`);
                                    }
                                    if (hardwareItem.isUnitCounted && !hardwareItem.inventoryControlled) {
                                        this.addQuantityIncrement(
                                            incrementOperationsByKey,
                                            Collection.plasticsHardwareItem,
                                            lineItem.entityId,
                                            'qtyOnHand',
                                            change,
                                        );
                                        addInventoryTransaction(
                                            lineItem.entityId,
                                            change,
                                            `created with product instance ${instance.id}`,
                                            InventoryTransactionType.PlasticsHardware,
                                        );
                                    }
                                    return;
                                }

                                if (lineItem.type === BillOfMaterialsLineItemType.Material) {
                                    this.addQuantityIncrement(
                                        incrementOperationsByKey,
                                        Collection.plasticsMaterial,
                                        lineItem.entityId,
                                        'qtyOnHand',
                                        change,
                                    );
                                    addInventoryTransaction(
                                        lineItem.entityId,
                                        change,
                                        `created with product instance ${instance.id}`,
                                        InventoryTransactionType.PlasticsMaterial,
                                    );
                                }
                            });
                        });

                        const tally: RotoMachineArmTally = {
                            id: this.appwrite.createId(),
                            machineId,
                            armId,
                            timestamp: now,
                        };

                        const batchSetEntityGroups: BatchSetEntityGroups = {
                            [Collection.productInstance]: recordedProductInstances,
                            [Collection.inventoryTransaction]: inventoryTransactions,
                            [Collection.rotoMachineArmTally]: [tally],
                            [Collection.moldArmCycle]: moldArmCycles,
                        };
                        const incrementOperations = Object.values(incrementOperationsByKey)
                            .filter(operation => operation.value !== 0)
                            .map(operation => operation.collection === Collection.plasticsMold && checkupSettings.id && checkupSettings.enabled
                                ? { ...operation, thresholdLatch: { flagColumn: 'checkupOnNextRemoval', baselineColumn: 'lastCheckupCycleCount',
                                    intervalColumn: 'checkupInterval', defaultInterval: checkupSettings.defaultInterval,
                                    expected: { status: PlasticsMoldStatus.Active, openCheckupId: null,
                                        placementJson: JSON.stringify(molds.find(mold => mold.id === operation.id)?.placement ?? null) } } }
                                : operation);

                        return this.appwrite.batchSetAndIncrementAcrossCollectionsTransactional(
                            batchSetEntityGroups,
                            incrementOperations,
                        ).pipe(
                            switchMap(() => goodMoldingTaskIds.length
                                ? this.loadMoldingTasksByIds(goodMoldingTaskIds)
                                : of([] as PlasticsMoldingTask[])
                            ),
                        );
                    }),
                );
            }),
        );
    }

    public recordBulkProductionInventory(
        quantity: number,
        productId: string,
        productVersionId: string,
        currentUser: User,
    ): Observable<any> {
        const transaction: InventoryTransaction = {
            id: this.appwrite.createId(),
            entityId: productId,
            change: quantity,
            comment: `created with product version ${productVersionId}`,
            createdOn: new Date(),
            createdById: currentUser?.id || "",
            type: InventoryTransactionType.PlasticsProduct,
        }

        const transaction$ = from(this.appwrite.collection(Collection.inventoryTransaction).doc(transaction.id).set(transaction));
        const product$ = this.incrementField(Collection.product, productId, 'qtyOnHand', quantity);

        const lineItemTransactions$ = this.consumeBillOfMaterialsInventory(
            productVersionId,
            quantity,
            `created with product version ${productVersionId}`,
            currentUser
        );

        return forkJoin([
            product$,
            transaction$,
            lineItemTransactions$,
        ]);
    }

    public recordFHCornerLabelBoxInventory(
        deductions: readonly { hardwareItemId: string; quantity: number }[],
        finishingTaskId: string,
        productVersionId: string,
        currentUser: User,
    ): Observable<void> {
        const quantityByHardwareItemId = deductions.reduce((dict, deduction) => {
            if (!deduction.hardwareItemId
                || !Number.isInteger(deduction.quantity)
                || deduction.quantity <= 0) {
                return dict;
            }

            dict[deduction.hardwareItemId] = (dict[deduction.hardwareItemId] ?? 0) + deduction.quantity;
            return dict;
        }, {} as Record<string, number>);
        const normalizedDeductions = Object.entries(quantityByHardwareItemId);
        if (!normalizedDeductions.length) return of(void 0);

        const transactionGroupId = this.appwrite.createId();
        const now = new Date();
        const transactions: InventoryTransaction[] = normalizedDeductions.map(([hardwareItemId, quantity]) => ({
            id: this.appwrite.createId(),
            entityId: hardwareItemId,
            change: -quantity,
            comment: `packaging consumed for finishing task ${finishingTaskId} with product version ${productVersionId}`,
            createdOn: now,
            createdById: currentUser?.id || '',
            type: InventoryTransactionType.PlasticsHardware,
            processId: finishingTaskId,
            transactionGroupId,
        }));
        const createGroups: BatchSetEntityGroups = {
            [Collection.inventoryTransaction]: transactions,
        };
        const incrementOperations = normalizedDeductions.map(([hardwareItemId, quantity]) => ({
            collection: Collection.plasticsHardwareItem,
            id: hardwareItemId,
            field: 'qtyOnHand',
            value: -quantity,
        }));

        return this.appwrite.batchCreateUpdateAndIncrementAcrossCollectionsTransactional(
            createGroups,
            {},
            incrementOperations,
        );
    }


    // Simplified main functions
    public recordSuccessfulProduction(
        instances: ProductInstance[],
        currentUser: User,
    ): Observable<any> {
        const instances$ = instances.map((instance) => {
            return from(this.appwrite.collection(Collection.productInstance).doc(instance.id).set(instance))
        });

        const moldingTaskUpdates$ = instances.map((instance) => {
            return this.incrementField(Collection.plasticsMoldingTask, instance.scheduledProductId, 'completedQuantity', 1);
        });

        const productUpdates$ = instances.map((instance) => {
            return this.incrementField(Collection.product, instance.productId, 'qtyOnHand', 1);
        });

        const productInventoryTransactions$ = instances.map((instance) => {
            const transaction: InventoryTransaction = {
                id: this.appwrite.createId(),
                entityId: instance.productId,
                change: 1,
                comment: `created with product instance ${instance.id}`,
                createdOn: new Date(),
                createdById: currentUser?.id || "",
                type: InventoryTransactionType.PlasticsProduct,
            }
            return from(this.appwrite.collection(Collection.inventoryTransaction).doc(transaction.id).set(transaction))
        });

        const lineItems$ = instances.map((instance) => {
            return this.consumeBillOfMaterialsInventory(
                instance.productVersionId,
                1,
                `created with product instance ${instance.id}`,
                currentUser
            );
        });

        return forkJoin([
            ...instances$,
            ...moldingTaskUpdates$,
            ...productUpdates$,
            ...lineItems$,
            ...productInventoryTransactions$,
        ]);
    }


    public recordDefectiveProduction(
        instances: ProductInstance[],
        currentUser: User
    ): Observable<any> {
        const instances$ = instances.map((instance) => {
            return from(this.appwrite.collection(Collection.productInstance).doc(instance.id).set(instance))
        });

        const productInventoryTransactions$ = instances.map((instance) => {
            const transaction: InventoryTransaction = {
                id: this.appwrite.createId(),
                entityId: instance.productId,
                change: 1,
                comment: `created with product instance ${instance.id}`,
                createdOn: new Date(),
                createdById: currentUser?.id || "",
                type: InventoryTransactionType.PlasticsProduct,
            }
            return from(this.appwrite.collection(Collection.inventoryTransaction).doc(transaction.id).set(transaction))
        });

        const lineItems$ = instances.map((instance) => {
            return this.consumeBillOfMaterialsInventory(
                instance.productVersionId,
                1,
                `created with product instance ${instance.id}`,
                currentUser
            );
        });

        return forkJoin([
            ...instances$,
            ...lineItems$,
            ...productInventoryTransactions$,
        ]);
    }

    // Helper to process BOM line items
    private consumeBillOfMaterialsInventory(
        productVersionId: string,
        quantity: number,
        comment: string,
        currentUser: User
    ): Observable<any> {
        return this.loadBillOfMaterialsLineItemsByProductVersionId(productVersionId).pipe(
            switchMap((lineItems) => {
                const productLineItems = lineItems.filter(li => li.type === BillOfMaterialsLineItemType.Product);
                const hardwareLineItems = lineItems.filter(li => li.type === BillOfMaterialsLineItemType.Hardware);
                const materialLineItems = lineItems.filter(li => li.type === BillOfMaterialsLineItemType.Material);

                const updatedProducts$ = productLineItems.map((li) => {
                    return this.incrementField(Collection.product, li.entityId, 'qtyOnHand', -li.quantity * quantity);
                });

                const productInventoryTransactions$ = productLineItems.map((li) => {
                    const transaction: InventoryTransaction = {
                        id: this.appwrite.createId(),
                        entityId: li.entityId,
                        change: -li.quantity * quantity,
                        comment: comment,
                        createdOn: new Date(),
                        createdById: currentUser?.id || "",
                        type: InventoryTransactionType.PlasticsProduct,
                    }
                    return from(this.appwrite.collection(Collection.inventoryTransaction).doc(transaction.id).set(transaction))
                });

                const updatedHardwareItems$ = hardwareLineItems.map((li) => {
                    return from(this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(li.entityId)
                        .get().pipe(
                            switchMap((resp) => {
                                const item: PlasticsHardwareItem = fromAppwriteDoc(resp);
                                if (item.isUnitCounted && !item.inventoryControlled) {
                                    return this.incrementField(Collection.plasticsHardwareItem, li.entityId, 'qtyOnHand', -li.quantity * quantity);
                                } else {
                                    return of(null);
                                }
                            })
                        )
                    )
                });

                const hardwareInventoryTransactions$ = hardwareLineItems.map((li) => {
                    return from(this.appwrite.collection<PlasticsHardwareItem>(Collection.plasticsHardwareItem).doc(li.entityId)
                        .get().pipe(
                            switchMap(resp => {
                                const item: PlasticsHardwareItem = fromAppwriteDoc(resp);
                                if (item.isUnitCounted && !item.inventoryControlled) {
                                    const transaction: InventoryTransaction = {
                                        id: this.appwrite.createId(),
                                        entityId: li.entityId,
                                        change: -li.quantity * quantity,
                                        comment: comment,
                                        createdOn: new Date(),
                                        createdById: currentUser?.id || "",
                                        type: InventoryTransactionType.PlasticsHardware,
                                    }
                                    return from(this.appwrite.collection(Collection.inventoryTransaction).doc(transaction.id).set(transaction))
                                } else {
                                    return of(null)
                                }
                            })
                        )
                    )
                });

                const updatedMaterials$ = materialLineItems.map((li) => {
                    return this.incrementField(Collection.plasticsMaterial, li.entityId, 'qtyOnHand', -li.quantity * quantity);
                });

                const materialInventoryTransactions$ = materialLineItems.map((li) => {
                    const transaction: InventoryTransaction = {
                        id: this.appwrite.createId(),
                        entityId: li.entityId,
                        change: -li.quantity * quantity,
                        comment: comment,
                        createdOn: new Date(),
                        createdById: currentUser?.id || "",
                        type: InventoryTransactionType.PlasticsMaterial,
                    }
                    return from(this.appwrite.collection(Collection.inventoryTransaction).doc(transaction.id).set(transaction))
                });

                return forkJoin([
                    ...updatedProducts$,
                    ...productInventoryTransactions$,
                    ...updatedHardwareItems$,
                    ...hardwareInventoryTransactions$,
                    ...updatedMaterials$,
                    ...materialInventoryTransactions$,
                ]).pipe(
                    defaultIfEmpty([])
                );
            })
        );
    }

    public loadProductAndProductVersionsByResourceId(resourceId: string): Observable<{
        products: Product[];
        productVersions: ProductVersion[];
    }> {
        return this.appwrite.list<ProductVersion>(Collection.productVersion, [
            Query.equal('drawingResourceId', resourceId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            switchMap((productVersions) => {
                return this.loadProductsByIds(productVersions.map(v => v.productId)).pipe(
                    map((products) => {
                        return {
                            products,
                            productVersions,
                        }
                    })
                )

            })
        )

    }

    public loadTasksByOrderedProductId(orderedProductId: string): Observable<{
        mixingTasks: PlasticsMaterialMixingTask[];
        moldingTasks: PlasticsMoldingTask[];
        finishingTasks: PlasticsFinishingTask[];
    }> {
        const mixingTasks$ = this.loadMaterialMixingTasksByOrderedProductId(orderedProductId);
        const moldingTasks$ = this.loadMoldingTasksByOrderedProductId(orderedProductId);
        const finishingTask$ = this.loadFinishingTasksByOrderedProductId(orderedProductId);
        return forkJoin([
            mixingTasks$,
            moldingTasks$,
            finishingTask$,
        ]).pipe(
            map(([
                mixingTasks,
                moldingTasks,
                finishingTasks
            ]) => ({
                mixingTasks,
                moldingTasks,
                finishingTasks
            }))
        )
    }

    public loadTasksByOrderedProductIds(orderedProductIds: string[]): Observable<{
        mixingTasks: PlasticsMaterialMixingTask[];
        moldingTasks: PlasticsMoldingTask[];
        finishingTasks: PlasticsFinishingTask[];
    }> {
        return forkJoin({
            mixingTasks: this.appwrite.listByFieldValues<PlasticsMaterialMixingTask>(Collection.plasticsMaterialMixingTask, 'orderedProductId', orderedProductIds),
            moldingTasks: this.loadMoldingTasksByOrderedProductIds(orderedProductIds),
            finishingTasks: this.loadFinishingTasksByOrderedProductIds(orderedProductIds),
        }).pipe(
            map(({
                mixingTasks,
                moldingTasks,
                finishingTasks,
            }) => ({
                mixingTasks,
                moldingTasks,
                finishingTasks,
            }))
        )
    }


    // ============================
    // Plastics Kit
    // ============================

    public setPlasticsKit(kit: PlasticsKit): Observable<void> {
        return from(this.appwrite.collection<PlasticsKit>(Collection.plasticsKit).doc(kit.id).set(kit));
    }

    public loadPlasticsKitsByMoldingTaskId(moldingTaskId: string): Observable<PlasticsKit[]> {
        return this.appwrite.list<PlasticsKit>(Collection.plasticsKit, [
            Query.equal('moldingTaskId', moldingTaskId),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([]),
        );
    }

    public loadPlasticsKitsByMoldingTaskIds(ids: string[]): Observable<PlasticsKit[]> {
        return this.appwrite.listByFieldValues<PlasticsKit>(Collection.plasticsKit, 'moldingTaskId', ids, [], 100);
    }

    public loadPlasticsKitsByIds(ids: string[]): Observable<PlasticsKit[]> {
        return this.appwrite.listByIds<PlasticsKit>(Collection.plasticsKit, ids);
    }

    public getUncheckedOutPlasticsKitsValueChanges(): Observable<PlasticsKit[]> {
        return this.appwrite.collection<PlasticsKit>(
            Collection.plasticsKit,
            ref => ref
                .where('checkedOutOn', '==', null)
                .limit(5000)
        ).valueChanges();
    }

    public getKittingLocationBinsValueChanges(): Observable<PlasticsKittingLocationBin[]> {
        return this.appwrite.collection<PlasticsKittingLocationBin>(Collection.plasticsKittingLocationBin).valueChanges().pipe(
            map(resp => resp)
        )
    }

    public loadKittingLocationBinsByKitIds(kitIds: string[]): Observable<PlasticsKittingLocationBin[]> {
        return this.appwrite.listByFieldValues<PlasticsKittingLocationBin>(Collection.plasticsKittingLocationBin, 'kitId', kitIds, [], 100);
    }

    public deletePlasticsKit(kitId: string): Observable<void> {
        return this.appwrite.delete(Collection.plasticsKit, kitId);
    }

    public loadKittingLocations(): Observable<PlasticsKittingLocation[]> {
        return this.appwrite.collection<PlasticsKittingLocation>(Collection.plasticsKittingLocation).get().pipe(
            map((resp) => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc)),
            defaultIfEmpty([])
        )
    }

    public loadKittingAreas(): Observable<PlasticsKittingArea[]> {
        return this.appwrite.collection<PlasticsKittingArea>(Collection.plasticsKittingArea).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public getKittingLocationsByAreaIdValueChanges(areaId: string): Observable<PlasticsKittingLocation[]> {
        return this.appwrite.collection<PlasticsKittingLocation>(Collection.plasticsKittingLocation, ref => ref
            .where('kittingAreaId', '==', areaId)
        ).valueChanges().pipe(
            map(locations => locations.sort((a, b) => a.name.localeCompare(b.name)))
        );
    }

    public loadKittingLocationsByAreaId(areaId: string): Observable<PlasticsKittingLocation[]> {
        return this.appwrite.collection<PlasticsKittingLocation>(Collection.plasticsKittingLocation, ref => ref
            .where('kittingAreaId', '==', areaId)
        ).get().pipe(
            map(locations => (locations.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc) as PlasticsKittingLocation[]).sort((a, b) => a.name.localeCompare(b.name)))
        );
    }

    getKittingAreaByIdValueChanges(areaId: string): Observable<PlasticsKittingArea | null> {
        return this.appwrite
            .collection<PlasticsKittingArea>(Collection.plasticsKittingArea)
            .doc(areaId)
            .valueChanges()
            .pipe(
                map(area => area || null)
            );
    }

    getKittingBinsByAreaIdValuechanges(areaId: string): Observable<PlasticsKittingLocationBin[]> {
        return this.appwrite.collection<PlasticsKittingLocationBin>(Collection.plasticsKittingLocationBin, ref => ref
            .where('kittingAreaId', '==', areaId)
        ).valueChanges().pipe(
            map(resp => resp)
        )
    }

    getMoldingTasksByStatusValueChanges(status: PlasticsMoldingTaskStatus) {
        return this.appwrite.collection<PlasticsMoldingTask>(Collection.plasticsMoldingTask, ref => ref
            .where('status', '==', status)
        ).valueChanges();
    }

    public loadMoldingTasksByStatus(status: PlasticsMoldingTaskStatus): Observable<PlasticsMoldingTask[]> {
        return this.appwrite.list<PlasticsMoldingTask>(Collection.plasticsMoldingTask, [
            Query.equal('status', status),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
            defaultIfEmpty([]),
        );
    }


    // ============================
    // Mold Repair Requests
    // ============================

    public loadMoldRepairRequests(): Observable<MoldRepairRequest[]> {
        return this.appwrite.collection<MoldRepairRequest>(Collection.moldRepairRequest).get().pipe(
            map(resp => resp.docs.map((doc: any) => doc.data()).filter((doc: any) => !!doc))
        )
    }

    public loadMoldRepairRequestById(taskId: string): Observable<MoldRepairRequest> {
        return this.appwrite.collection<MoldRepairRequest>(Collection.moldRepairRequest).doc(taskId).get().pipe(
            map(resp => fromAppwriteDoc(resp))
        )
    }

    public setMoldRepairRequest(task: MoldRepairRequest): Observable<void> {
        return from(this.appwrite.collection<MoldRepairRequest>(Collection.moldRepairRequest).doc(task.id).set(task))
    }
}
